import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate,
  useLocation,
  useSearchParams } from 'react-router-dom';
import AppLayout from './AppLayout.jsx';
import MiSuscripcion from './MiSuscripcion.jsx';
import PublicProfilePreview from './PublicProfilePreview';
import ShareProfileButton from './ShareProfileButton.jsx';
import { buildPublicProfileShareUrl } from '../utils/publicProfileShareUrl.js';
import { mergeContactoPublicoForForm } from '../utils/contactoPublicoForm.js';
import { isLocalizacionCompleta, parseNominatimAddress } from '../utils/nominatimAddress.js';
import SkillsEditor from './SkillsEditor';
import ImageCropper from './ImageCropper';
import LocationMap from './LocationMap.jsx';
import ComingSoonSection from './ComingSoonSection.jsx';
import MusicosTab from './MusicosTab.jsx';
import ProfileTipsCarousel from './ProfileTipsCarousel.jsx';
import { useMvpMode } from '../config/mvp.jsx';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessTuWeb,
  firstColaboradorGestionPath,
  isBandaAccountUserType,
  } from '../utils/bandaGestionAccess';
import { useProductTour } from '../hooks/useProductTour';
import { bandaPerfilSteps } from '../tours/bandaPerfilTour';
import { bandaTuWebSteps } from '../tours/bandaTuWebTour';
import { trackProfileUpdate,
  trackProfileCompletion,
  trackAvatarUpload,
  trackPhotoUpload } from '../services/analytics';
import { bandasAPI,
  catalogoAPI,
  resenasAPI,
  authAPI,
  avalesAPI,
  disponibilidadAPI,
  musicosAPI,
  instrumentosAPI,
  actuacionesAPI,
  ensayosAPI } from '../services/api';
import { DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors } from '@dnd-kit/core';
import { arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  Icon,
  SimpleGrid,
  Card,
  Badge,
  Flex,
  IconButton,
  Image,
  Wrap,
  Grid,
  GridItem,
  NumberInput,
  NativeSelect,
  Alert,
  Progress,
  Tabs,
  Accordion,
  useDisclosure,
  Checkbox,
  Spinner,
  Link,
  Slider,
  RadioGroup,
  Stack,
  AspectRatio,
  List,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, InputRightAddon, SliderTrack, SliderThumb, SliderFilledTrack, SliderRoot, Radio, tabsControlledValue, tabsOnValueChange, Switch} from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import { MultiCheckboxField, IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import {
  FaUser,
  FaCamera,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaCalendarAlt,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSave,
  FaMusic,
  FaFolder,
  FaChurch,
  FaIdCard,
  FaShieldAlt,
  FaCrown,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowRight,
  FaInfoCircle,
  FaFileAlt,
  FaTimes,
  FaCheck,
  FaCircle,
  FaMousePointer,
  FaSearchPlus,
  FaEraser,
  FaPlay,
  FaEye,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaComments,
  FaPaperPlane,
  FaEyeSlash,
  FaGlobe,
  FaDrum,
  FaMapMarkedAlt,
  FaCrop,
  FaLightbulb,
  FaHandshake,
  FaAward,
  FaStar,
  FaRegStar,
  FaTiktok,
  FaExternalLinkAlt,
  FaTrophy,
  FaLink,
  FaUsers,
  FaSpotify,
  FaBullhorn,
  FaPalette,
  FaUndo,
  FaBolt,
  FaList,
  FaGripVertical,
  FaImages,
  FaVideo,
  FaUpload,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import {
  FONT_OPTIONS,
  MICROSITE_SECTION_CATALOG,
  getMicrositeBodySectionOrder,
  isMicrositeSectionHidden,
  getMicrositePalette,
  MICROSITE_THEME_DEFAULT_PALETTE,
  normalizeAnimacionesMicrositio,
  normalizeCabeceraHero,
  readMostrarPlantillaMusicosNosotros,
  readMostrarEspecialidadesNosotros,
  readPlantillaMusicosInstrumentosModo,
  readPlantillaMusicosInstrumentoPorMusico,
  normalizeContactoMostrar,
  sortMicrositeCalendarioItems,
  isCalendarioItemPasado,
  isCalendarioItemEnVentanaTuWeb,
} from './microsite/micrositeUtils';
import { hayMusicosActivosConVariosInstrumentos, labelAsignacionInstrumentoRow } from '../utils/musicoInstrumentos';

/** Icono por id de sección (editor Tu web → Contenido). */
const TU_WEB_CONTENIDO_SECTION_ICONS = {
  nosotros: FaUsers,
  historia: FaFileAlt,
  noticias: FaBullhorn,
  directiva: FaUsers,
  galeria: FaImages,
  videos: FaVideo,
  repertorio: FaMusic,
  logros: FaTrophy,
  resenas: FaComments,
  calendario: FaCalendarAlt,
  patrocinadores: FaHandshake,
  enlaces: FaLink,
  contacto: FaGlobe,
};

/** Entradas del menú lateral «Tu Web» (mismo estilo que el drawer de Gestión en AppLayout). */
const TU_WEB_SIDEBAR_ITEMS = [
  { id: 'estilos', label: 'Estilos', desc: 'Tema visual y tipografías', icon: FaPalette, color: 'purple' },
  { id: 'secciones', label: 'Secciones', desc: 'Visibilidad en tu web pública', icon: FaList, color: 'teal' },
  { id: 'contenido', label: 'Contenido', desc: 'Textos e información por sección', icon: FaEdit, color: 'blue' },
  { id: 'publicar', label: 'Publicar', desc: 'Sube tu web al subdominio y guarda versiones', icon: FaBullhorn, color: 'orange' },
  { id: 'dominio', label: 'Dominio', desc: 'URL y dominio personalizado', icon: FaGlobe, color: 'cyan' },
];

/** Interruptores de visibilidad en el hero (Tu web → Contenido → Cabecera). */
const TU_WEB_HERO_CAMPOS = [
  { key: 'mostrarVerificado', label: 'Icono de perfil verificado' },
  { key: 'mostrarUbicacion', label: 'Ubicación' },
  { key: 'mostrarNMusicos', label: 'Número de músicos' },
  { key: 'mostrarAnoFundacion', label: 'Año de fundación' },
  { key: 'mostrarTipoBanda', label: 'Tipo de banda' },
  { key: 'mostrarRrss', label: 'Redes sociales' },
];

/** Visibilidad por campo en la sección Contacto del micrositio (se guarda en micrositeConfig). */
const TU_WEB_CONTACTO_MOSTRAR_CAMPOS = [
  { key: 'email', label: 'Email de contacto' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'website', label: 'Sitio web' },
  { key: 'ubicacion', label: 'Ciudad y provincia (texto en la web)' },
  { key: 'redes', label: 'Redes: Instagram, Facebook y YouTube' },
  { key: 'textoContratar', label: 'Mensaje de contratación' },
];

const BANDA_MANDATORY_FOR_VALIDATE_CHECKS = [
  { label: 'Nombre de la banda', test: (d) => !!d?.nombre?.trim() },
  {
    label: 'Tipo de banda',
    test: (d) =>
      !!(d?.tipoAgrupacionInstrumental?.nombre && String(d.tipoAgrupacionInstrumental.nombre).trim()) || !!d?.tipoGrupo?.trim(),
  },
  { label: 'Localización', test: isLocalizacionCompleta },
  { label: 'Teléfono de contacto', test: (d) => !!d?.contactoPublico?.telefono?.trim() },
  { label: 'Email de contacto', test: (d) => !!d?.contactoPublico?.email?.trim() },
];

/**
 * Todos los apartados de la pestaña Perfil público que alimentan la barra % y el checklist.
 * mandatoryForValidate: bloquea guardar si falta (mismo criterio que BANDA_MANDATORY_FOR_VALIDATE_CHECKS).
 */
const BANDA_PROFILE_COMPLETENESS_CHECKS = [
  ...BANDA_MANDATORY_FOR_VALIDATE_CHECKS.map((c) => ({ ...c, mandatoryForValidate: true })),
  { label: 'Descripción de la banda', test: (d) => !!d?.bio?.trim(), mandatoryForValidate: false },
  { label: 'Logo o imagen de perfil', test: (d) => !!d?.avatar, mandatoryForValidate: false },
  {
    label: 'Imagen de fondo o fotos en galería',
    test: (d) => !!(d?.fondoHeaderImagen || (Array.isArray(d?.fotos) && d.fotos.length > 0)),
    mandatoryForValidate: false,
  },
  {
    label: 'Sitio web o al menos una red social',
    test: (d) =>
      !!d?.contactoPublico?.website?.trim() ||
      !!d?.redesSociales?.instagram?.trim() ||
      !!d?.redesSociales?.facebook?.trim() ||
      !!d?.redesSociales?.youtube?.trim() ||
      !!d?.redesSociales?.twitter?.trim() ||
      !!d?.redesSociales?.tiktok?.trim(),
    mandatoryForValidate: false,
  },
  {
    label: 'Al menos un vídeo en el perfil',
    test: (d) => Array.isArray(d?.videos) && d.videos.length > 0,
    mandatoryForValidate: false,
  },
  {
    label: 'Al menos una especialidad musical',
    test: (d) => Array.isArray(d?.especialidades) && d.especialidades.length > 0,
    mandatoryForValidate: false,
  },
  {
    label: 'Número de músicos',
    test: (d) => d?.nMusicos != null && String(d.nMusicos).trim() !== '',
    mandatoryForValidate: false,
  },
  {
    label: 'Año de fundación',
    test: (d) => d?.anoFundacion != null && String(d.anoFundacion).trim() !== '',
    mandatoryForValidate: false,
  },
  {
    label: 'Kilómetros dispuestos a desplazarse',
    test: (d) => d?.kmDispuestos != null && String(d.kmDispuestos).trim() !== '',
    mandatoryForValidate: false,
    privateField: true,
    omitFromOptionalSlides: true,
  },
];

const MAX_DIRECTIVA_FOTO_BYTES = 5 * 1024 * 1024;

function validateDirectivaFotoFile(file) {
  if (!file.type.startsWith('image/')) return 'El archivo debe ser una imagen (JPG, PNG, WebP…).';
  if (file.size > MAX_DIRECTIVA_FOTO_BYTES) return 'La imagen no puede superar 5 MB.';
  return null;
}

function newDirectivaMember() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `dir-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return { id, nombre: '', cargo: '', foto: '' };
}

function newPatrocinador() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `pat-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return { id, nombre: '', logo: '', url: '' };
}

function newNoticia() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `not-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return { id, activo: false, texto: '', imagen: '', createdAt: new Date().toISOString() };
}

/** Id del panel «Cabecera» en Tu web → Contenido (no es id de catálogo de secciones). */
const TU_WEB_CONTENIDO_CABECERA_ID = 'cabecera';

/** Claves de `micrositeConfig` que este bloque persiste con el guardado manual de Contenido. */
function pickMsConfigForContenidoDigest(mc) {
  if (!mc || typeof mc !== 'object') return {};
  return {
    historia: mc.historia,
    noticias: mc.noticias,
    calendarioMostrarActuaciones: mc.calendarioMostrarActuaciones,
    calendarioMostrarEnsayos: mc.calendarioMostrarEnsayos,
    calendarioModo: mc.calendarioModo,
    calendarioMeses: mc.calendarioMeses,
    calendarioActuacionesOcultasIds: mc.calendarioActuacionesOcultasIds,
    calendarioEnsayosOcultosIds: mc.calendarioEnsayosOcultosIds,
    calendarioIncluirActuacionesPasadas: mc.calendarioIncluirActuacionesPasadas,
    calendarioIncluirEnsayosPasados: mc.calendarioIncluirEnsayosPasados,
    directiva: mc.directiva,
    patrocinadores: mc.patrocinadores,
    logros: mc.logros,
    enlacesPersonalizados: mc.enlacesPersonalizados,
    textoContratar: mc.textoContratar,
    // Excluidos: cabeceraHero, toggles de sección, contactoMostrar (se guardan al instante con otros controles).
  };
}

function computeMicrositeContenidoDigest(msConfig, profileData, userType) {
  const slice = pickMsConfigForContenidoDigest(msConfig);
  if (!isBandaAccountUserType(userType)) {
    return JSON.stringify({ slice });
  }
  const pd = profileData || {};
  return JSON.stringify({
    slice,
    contactoPublico: pd.contactoPublico,
    redesSociales: pd.redesSociales,
    localizacion: pd.localizacion,
    lat: pd.lat,
    lng: pd.lng,
  });
}

const sameContenidoJson = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Ids de catálogo de sección con cambios pendientes respecto al último guardado manual (excluye `cabecera`). */
function computeTuWebContenidoUnsavedSectionIds(savedDigestStr, currentDigestStr, userType) {
  const out = new Set();
  if (savedDigestStr === '__init__' || !savedDigestStr || !currentDigestStr) return out;
  let saved;
  let cur;
  try {
    saved = JSON.parse(savedDigestStr);
    cur = JSON.parse(currentDigestStr);
  } catch {
    return out;
  }
  if (!saved || !cur || typeof saved !== 'object' || typeof cur !== 'object') return out;
  const s0 = saved.slice && typeof saved.slice === 'object' ? saved.slice : {};
  const s1 = cur.slice && typeof cur.slice === 'object' ? cur.slice : {};
  if (!sameContenidoJson(s1.historia, s0.historia)) out.add('historia');
  if (!sameContenidoJson(s1.noticias, s0.noticias)) out.add('noticias');
  if (
    !sameContenidoJson(s1.calendarioMostrarActuaciones, s0.calendarioMostrarActuaciones) ||
    !sameContenidoJson(s1.calendarioMostrarEnsayos, s0.calendarioMostrarEnsayos) ||
    !sameContenidoJson(s1.calendarioModo, s0.calendarioModo) ||
    !sameContenidoJson(s1.calendarioMeses, s0.calendarioMeses) ||
    !sameContenidoJson(s1.calendarioActuacionesOcultasIds, s0.calendarioActuacionesOcultasIds) ||
    !sameContenidoJson(s1.calendarioEnsayosOcultosIds, s0.calendarioEnsayosOcultosIds) ||
    !sameContenidoJson(s1.calendarioIncluirActuacionesPasadas, s0.calendarioIncluirActuacionesPasadas) ||
    !sameContenidoJson(s1.calendarioIncluirEnsayosPasados, s0.calendarioIncluirEnsayosPasados)
  ) {
    out.add('calendario');
  }
  if (!sameContenidoJson(s1.directiva, s0.directiva)) out.add('directiva');
  if (!sameContenidoJson(s1.patrocinadores, s0.patrocinadores)) out.add('patrocinadores');
  if (!sameContenidoJson(s1.logros, s0.logros)) out.add('logros');
  if (!sameContenidoJson(s1.enlacesPersonalizados, s0.enlacesPersonalizados)) out.add('enlaces');
  if (!sameContenidoJson(s1.textoContratar, s0.textoContratar)) out.add('contacto');
  if (isBandaAccountUserType(userType)) {
    if (
      !sameContenidoJson(cur.contactoPublico, saved.contactoPublico) ||
      !sameContenidoJson(cur.redesSociales, saved.redesSociales) ||
      !sameContenidoJson(cur.localizacion, saved.localizacion) ||
      (cur.lat ?? null) !== (saved.lat ?? null) ||
      (cur.lng ?? null) !== (saved.lng ?? null)
    ) {
      out.add('contacto');
    }
  }
  return out;
}

/** Fila reordenable (Tu web → Secciones): el asa activa el sensor de arrastre. */
function SortableTuWebMicrositeSeccionRow({ sec, visible, onVisibleChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sec.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };
  return (
    <Flex
      ref={setNodeRef}
      style={style}
      align={{ base: 'flex-start', sm: 'center' }}
      direction={{ base: 'column', sm: 'row' }}
      justify="space-between"
      gap={3}
      p={4}
      rounded="xl"
      borderWidth="1px"
      borderColor="gray.200"
      bg="gray.50"
      w="full"
      minW={0}
      shadow={isDragging ? 'md' : 'none'}
    >
      <IconButton
        size="sm"
        variant="outline"
        colorPalette="gray"
        aria-label="Arrastrar para reordenar la sección"
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        flexShrink={0}
        {...attributes}
        {...listeners}><Icon as={FaGripVertical} /></IconButton>
      <VStack align="start" gap={1} flex={1} minW={0} w="full">
        <Text fontWeight="700" fontSize="sm">{sec.label}</Text>
        <Text fontSize="xs" color="gray.600">{sec.desc}</Text>
      </VStack>
      <Field.Root
        display="flex"
        alignItems="center"
        flexShrink={0}
        w={{ base: 'full', sm: 'auto' }}
        minW={{ sm: '140px' }}
        justifyContent={{ base: 'space-between', sm: 'flex-end' }}
      >
        <Field.Label htmlFor={`ms-sec-${sec.id}`} mb={0} fontSize="sm" fontWeight="600" mr={3}>Visible</Field.Label>
        <Switch
          id={`ms-sec-${sec.id}`}
          colorPalette="purple"
          checked={visible}
          onChange={(e) => onVisibleChange(sec.id, e.target.checked)}
        />
      </Field.Root>
    </Flex>
  );
}

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const { user, profile, updateProfile, uploadAvatar, logout, loadProfile } = useAuth();
  const isColaboradorBanda = user?.userType === 'BANDA_COLABORADOR';
  const { isMvpMode } = useMvpMode();
  const fileInputRef = useRef(null);
  const tuWebLogoInputRef = useRef(null);
  const imagenCabeceraWebInputRef = useRef(null);
  const galleryInputRef = useRef(null); // Input para la galería de fotos (pestaña Perfil; se desmonta con isLazy)
  const galleryTuWebInputRef = useRef(null); // Misma galería en Tu web → Contenido (input siempre montado con esa pestaña)
  const directivaFotoInputRef = useRef(null);
  const directivaFotoPickRef = useRef(null);
  const patrocinadorLogoInputRef = useRef(null);
  const patrocinadorLogoPickRef = useRef(null);
  const noticiaImagenInputRef = useRef(null);
  const noticiaImagenPickRef = useRef(null);
  /** Último `profileData` para guardados de micrositio declarados antes del estado (orden de hooks). */
  const profileDataRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isProfileLoadedRef = useRef(false);
  
  // Estados para el editor de imágenes (cropper)
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState(null); // 'avatar' | 'fondo' | 'imagenCabeceraWeb'
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  
  // Estado para el modal de imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Estado para el micrositio / slug
  const micrositeDomain = window.location.hostname.includes('dev.cadencya.com') ? 'dev.cadencya.com' : 'cadencya.com';
  const [slugInput, setSlugInput] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [currentSlug, setCurrentSlug] = useState(null);
  const [useMicrositeUrlAsWebsite, setUseMicrositeUrlAsWebsite] = useState(false);
  const slugCheckTimeout = useRef(null);
  const micrositePublicUrl = currentSlug ? `https://${currentSlug}.${micrositeDomain}` : '';
  /** Debounce guardado de paleta (input color dispara muchos onChange al arrastrar). */
  const paletteSaveDebounceRef = useRef(null);
  const pendingPaletteSaveRef = useRef(null);
  const animSaveDebounceRef = useRef(null);
  const pendingAnimMsConfigRef = useRef(null);

  const cancelPaletteSaveDebounce = useCallback(() => {
    if (paletteSaveDebounceRef.current) {
      clearTimeout(paletteSaveDebounceRef.current);
      paletteSaveDebounceRef.current = null;
    }
    pendingPaletteSaveRef.current = null;
  }, []);

  useEffect(() => () => {
    if (paletteSaveDebounceRef.current) {
      clearTimeout(paletteSaveDebounceRef.current);
      paletteSaveDebounceRef.current = null;
    }
    if (animSaveDebounceRef.current) {
      clearTimeout(animSaveDebounceRef.current);
      animSaveDebounceRef.current = null;
    }
  }, []);

  // Estado para micrositeConfig (secciones exclusivas de la web)
  const [msConfig, setMsConfig] = useState({
    colorPrimario: '#7c3aed',
    tema: 'moderno',
    tipografia: 'default',
    historia: '',
    noticias: [],
    directiva: [],
    patrocinadores: [],
    logros: [],
    enlacesPersonalizados: [],
    textoContratar: '',
    seccionesOcultas: [],
    coloresPorTema: {},
    animacionesMicrositio: normalizeAnimacionesMicrositio(),
    cabeceraHero: normalizeCabeceraHero(),
    mostrarPlantillaMusicosNosotros: false,
    plantillaMusicosNosotrosInstrumentosModo: 'todos',
    plantillaMusicosNosotrosInstrumentoPorMusico: {},
    mostrarEspecialidadesNosotros: true,
    contactoMostrar: normalizeContactoMostrar({}),
    calendarioMostrarActuaciones: true,
    calendarioMostrarEnsayos: true,
    calendarioModo: 'lista',
    calendarioMeses: [],
    calendarioActuacionesOcultasIds: [],
    calendarioEnsayosOcultosIds: [],
    calendarioIncluirActuacionesPasadas: false,
    calendarioIncluirEnsayosPasados: false,
  });
  const [msConfigSaving, setMsConfigSaving] = useState(false);
  const [micrositePublicaciones, setMicrositePublicaciones] = useState([]);
  const [micrositePublicacionesLoading, setMicrositePublicacionesLoading] = useState(false);
  const [tuWebCalendarioActuacionesAll, setTuWebCalendarioActuacionesAll] = useState([]);
  const [tuWebCalendarioEnsayosAll, setTuWebCalendarioEnsayosAll] = useState([]);
  const [tuWebCalendarioLoading, setTuWebCalendarioLoading] = useState(false);
  /** Submenú lateral dentro de la pestaña Tu Web (mismo patrón visual que Gestión en AppLayout). */
  const [tuWebMenu, setTuWebMenu] = useState('estilos');
  /** Lista de músicos (Gestión) para opciones de plantilla en Tu web → Contenido. */
  const [tuWebMusicosPlantilla, setTuWebMusicosPlantilla] = useState([]);

  const persistPaletteColor = useCallback(
    (temaId, field, value) => {
      setMsConfig((prev) => {
        const prevPal = prev.coloresPorTema?.[temaId] || {};
        const next = {
          ...prev,
          coloresPorTema: { ...(prev.coloresPorTema || {}), [temaId]: { ...prevPal, [field]: value } },
          ...(field === 'primario' && temaId === 'moderno' ? { colorPrimario: value } : {}),
        };
        pendingPaletteSaveRef.current = next;
        return next;
      });

      if (paletteSaveDebounceRef.current) clearTimeout(paletteSaveDebounceRef.current);
      paletteSaveDebounceRef.current = setTimeout(async () => {
        paletteSaveDebounceRef.current = null;
        const payload = pendingPaletteSaveRef.current;
        pendingPaletteSaveRef.current = null;
        if (!payload) return;
        try {
          await bandasAPI.updateMicrositeConfig(payload);
          toaster.create({ title: 'Colores guardados', type: 'success', duration: 1500 });
        } catch (err) {
          toaster.create({
            title: 'Error',
            description: err.response?.data?.message || 'No se pudo guardar la paleta',
            type: 'error',
            duration: 4000,
          });
        }
      }, 1200);
    },
    [],
  );

  const persistPaletteReset = useCallback(
    async (temaId) => {
      cancelPaletteSaveDebounce();

      const cp = { ...(msConfig.coloresPorTema || {}) };
      delete cp[temaId];
      const nextConfig = {
        ...msConfig,
        coloresPorTema: Object.keys(cp).length ? cp : {},
        ...(temaId === 'moderno' ? { colorPrimario: MICROSITE_THEME_DEFAULT_PALETTE.moderno.primario } : {}),
      };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        await bandasAPI.updateMicrositeConfig(nextConfig);
        toaster.create({
          title: 'Paleta restaurada',
          description: 'Colores de este estilo restablecidos a los predeterminados.',
          type: 'success',
          duration: 2500,
        });
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo restaurar la paleta',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce],
  );

  const persistMicrositeSectionVisible = useCallback(
    async (sectionId, visible) => {
      cancelPaletteSaveDebounce();
      const hiddenArr = Array.isArray(msConfig.seccionesOcultas) ? [...msConfig.seccionesOcultas] : [];
      const hiddenSet = new Set(hiddenArr);
      if (visible) hiddenSet.delete(sectionId);
      else hiddenSet.add(sectionId);
      const nextOcultas = Array.from(hiddenSet);
      const nextConfig = { ...msConfig, seccionesOcultas: nextOcultas };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        await bandasAPI.updateMicrositeConfig(nextConfig);
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la visibilidad de la sección',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce],
  );

  const persistMicrositeSectionOrder = useCallback(
    async (nextIds) => {
      cancelPaletteSaveDebounce();
      const prevIds = getMicrositeBodySectionOrder(msConfig);
      const unchanged =
        nextIds.length === prevIds.length && nextIds.every((id, i) => id === prevIds[i]);
      if (unchanged) return;
      const nextConfig = { ...msConfig, seccionesOrden: nextIds };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        await bandasAPI.updateMicrositeConfig({ seccionesOrden: nextIds });
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar el orden de las secciones',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce],
  );

  const persistCabeceraHeroCampo = useCallback(
    async (campo, visible) => {
      cancelPaletteSaveDebounce();
      const ch = normalizeCabeceraHero(msConfig.cabeceraHero);
      const nextCabecera = { ...ch, [campo]: visible };
      const nextConfig = { ...msConfig, cabeceraHero: nextCabecera };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        await bandasAPI.updateMicrositeConfig({ cabeceraHero: nextCabecera });
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la cabecera de la web',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce],
  );

  const persistMostrarPlantillaMusicosNosotros = useCallback(
    async (checked) => {
      cancelPaletteSaveDebounce();
      const nextConfig = { ...msConfig, mostrarPlantillaMusicosNosotros: checked };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        const res = await bandasAPI.updateMicrositeConfig({ mostrarPlantillaMusicosNosotros: checked });
        const savedMc = res.data?.micrositeConfig;
        if (savedMc && typeof savedMc === 'object' && !Array.isArray(savedMc) && 'mostrarPlantillaMusicosNosotros' in savedMc) {
          setMsConfig((prev) => ({
            ...prev,
            mostrarPlantillaMusicosNosotros: readMostrarPlantillaMusicosNosotros(savedMc),
          }));
        }
        if (isBandaAccountUserType(user?.userType)) {
          try {
            await loadProfile('BANDA');
          } catch {
            /* el toggle ya quedó persistido; el perfil en contexto se alineará en la próxima carga */
          }
        }
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la opción de músicos',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce, loadProfile, user?.userType],
  );

  const persistPlantillaMusicosInstrumentosModo = useCallback(
    async (modo) => {
      cancelPaletteSaveDebounce();
      const normalized = modo === 'uno' ? 'uno' : 'todos';
      const nextConfig = { ...msConfig, plantillaMusicosNosotrosInstrumentosModo: normalized };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        const res = await bandasAPI.updateMicrositeConfig({
          plantillaMusicosNosotrosInstrumentosModo: normalized,
        });
        const savedMc = res.data?.micrositeConfig;
        if (savedMc && typeof savedMc === 'object' && !Array.isArray(savedMc)) {
          setMsConfig((prev) => ({
            ...prev,
            plantillaMusicosNosotrosInstrumentosModo: readPlantillaMusicosInstrumentosModo(savedMc),
            plantillaMusicosNosotrosInstrumentoPorMusico: readPlantillaMusicosInstrumentoPorMusico(savedMc),
          }));
        }
        if (isBandaAccountUserType(user?.userType)) {
          try {
            await loadProfile('BANDA');
          } catch {
            /* el toggle ya quedó persistido */
          }
        }
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la vista de instrumentos',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce, loadProfile, user?.userType],
  );

  const persistPlantillaMusicosInstrumentoPorMusico = useCallback(
    async (nextMap) => {
      cancelPaletteSaveDebounce();
      const nextConfig = { ...msConfig, plantillaMusicosNosotrosInstrumentoPorMusico: nextMap };
      const prevConfig = msConfig;
      setMsConfig(nextConfig);
      try {
        const res = await bandasAPI.updateMicrositeConfig({
          plantillaMusicosNosotrosInstrumentoPorMusico: nextMap,
        });
        const savedMc = res.data?.micrositeConfig;
        if (savedMc && typeof savedMc === 'object' && !Array.isArray(savedMc)) {
          setMsConfig((prev) => ({
            ...prev,
            plantillaMusicosNosotrosInstrumentoPorMusico: readPlantillaMusicosInstrumentoPorMusico(savedMc),
          }));
        }
        if (isBandaAccountUserType(user?.userType)) {
          try {
            await loadProfile('BANDA');
          } catch {
            /* no bloquear */
          }
        }
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la selección',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce, loadProfile, user?.userType],
  );

  const persistMostrarEspecialidadesNosotros = useCallback(
    async (checked) => {
      cancelPaletteSaveDebounce();
      const prevConfig = msConfig;
      setMsConfig((p) => ({ ...p, mostrarEspecialidadesNosotros: checked }));
      try {
        const res = await bandasAPI.updateMicrositeConfig({ mostrarEspecialidadesNosotros: checked });
        const savedMc = res.data?.micrositeConfig;
        if (savedMc && typeof savedMc === 'object' && !Array.isArray(savedMc) && 'mostrarEspecialidadesNosotros' in savedMc) {
          setMsConfig((prev) => ({
            ...prev,
            mostrarEspecialidadesNosotros: readMostrarEspecialidadesNosotros(savedMc),
          }));
        }
      } catch (err) {
        setMsConfig(prevConfig);
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la opción de especialidades',
          type: 'error',
          duration: 4000,
        });
      }
    },
    [msConfig, cancelPaletteSaveDebounce],
  );

  const scheduleMicrositeAnimacionesSave = useCallback(() => {
    if (animSaveDebounceRef.current) clearTimeout(animSaveDebounceRef.current);
    animSaveDebounceRef.current = setTimeout(async () => {
      animSaveDebounceRef.current = null;
      const cfg = pendingAnimMsConfigRef.current;
      if (!cfg) return;
      try {
        await bandasAPI.updateMicrositeConfig(cfg);
        toaster.create({ title: 'Animaciones guardadas', type: 'success', duration: 1500 });
      } catch (err) {
        toaster.create({
          title: 'Error',
          description: err.response?.data?.message || 'No se pudo guardar la configuración de animaciones',
          type: 'error',
          duration: 4000,
        });
      }
    }, 650);
  }, []);

  const patchMicrositeAnimaciones = useCallback(
    (patch) => {
      cancelPaletteSaveDebounce();
      setMsConfig((prev) => {
        const nextAnim = normalizeAnimacionesMicrositio({
          ...normalizeAnimacionesMicrositio(prev.animacionesMicrositio),
          ...patch,
        });
        const next = { ...prev, animacionesMicrositio: nextAnim };
        pendingAnimMsConfigRef.current = next;
        return next;
      });
      scheduleMicrositeAnimacionesSave();
    },
    [cancelPaletteSaveDebounce, scheduleMicrositeAnimacionesSave],
  );

  const openTuWebMicrositePreview = useCallback(() => {
    window.open(`${window.location.origin}/banda/microsite-preview`, '_blank', 'noopener,noreferrer');
  }, []);

  const refreshMicrositePublicaciones = useCallback(async () => {
    try {
      setMicrositePublicacionesLoading(true);
      const res = await bandasAPI.listMicrositePublicaciones();
      setMicrositePublicaciones(res.data?.items || []);
    } catch (err) {
      setMicrositePublicaciones([]);
      toaster.create({
        title: 'Error',
        description: err.response?.data?.message || 'No se pudo cargar el histórico de publicaciones',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setMicrositePublicacionesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tuWebMenu !== 'publicar') return;
    refreshMicrositePublicaciones();
  }, [tuWebMenu, refreshMicrositePublicaciones]);

  useEffect(() => {
    if (!isBandaAccountUserType(user?.userType) || tuWebMenu !== 'contenido') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await musicosAPI.getAll();
        if (!cancelled) setTuWebMusicosPlantilla(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setTuWebMusicosPlantilla([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.userType, tuWebMenu]);

  const tuWebMusicosActivosMultiInstrumento = useMemo(() => {
    const list = (tuWebMusicosPlantilla || []).filter((m) => m.activo !== false);
    return list.filter((m) => (m.instrumentosAsignados || []).length > 1);
  }, [tuWebMusicosPlantilla]);

  const tuWebPreviewTooltipLabel = currentSlug
    ? 'Abre en una nueva pestaña la web pública con la configuración y datos ya guardados en el servidor.'
    : 'Primero elige y guarda la URL de tu web (subdominio) para poder previsualizarla.';

  const micrositeSeccionesSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleMicrositeSeccionesDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = getMicrositeBodySectionOrder(msConfig);
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      persistMicrositeSectionOrder(arrayMove(ids, oldIndex, newIndex));
    },
    [msConfig, persistMicrositeSectionOrder],
  );

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  const isTuWebTab = searchParams.get('tab') === 'tuweb';
  useProductTour('banda-perfil', bandaPerfilSteps, {
    autoStart: false,
    listenGlobal: !isColaboradorBanda && !isTuWebTab,
  });
  useProductTour('banda-tuweb', bandaTuWebSteps, {
    autoStart: false,
    listenGlobal: isTuWebTab,
  });

  // Marchas y autores (búsqueda fuzzy via API)
  const [marchasDisponibles, setMarchasDisponibles] = useState([]);
  const [autoresDisponibles, setAutoresDisponibles] = useState([]);
  const searchTimeoutRef = useRef(null);
  const autorSearchTimeoutRef = useRef(null);

  // Tipos de obras disponibles
  const tiposDeObra = [
    'Marcha procesional',
    'Marcha fúnebre',
    'Pasodoble',
    'Pasacalles',
    'Himno',
    'Romería',
    'Diana',
    'Marcha de concierto',
    'Marcha popular',
    'Saeta',
    'Marcha nupcial',
    'Marcha militar',
    'Otro',
  ];
  
  // Estado para previsualización del perfil público
  const [showPublicPreview, setShowPublicPreview] = useState(false);
  const [eventosPublicosPreview, setEventosPublicosPreview] = useState([]);

  const handleOpenPreview = async () => {
    setShowPublicPreview(true);
    try {
      const res = await disponibilidadAPI.getAll();
      const ahora = new Date();
      ahora.setHours(0, 0, 0, 0);
      const futuros = (res.data || [])
        .filter(d => d.isPublic && new Date(d.fecha) >= ahora && (d.estado === 'OCUPADO' || d.estado === 'EVENTO'))
        .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      setEventosPublicosPreview(futuros);
    } catch (err) {
      console.error('Error cargando eventos públicos para preview:', err);
    }
  };
  
  // Estado para solicitud de reseña
  const [reviewRequestEmail, setReviewRequestEmail] = useState('');
  const [isRequestingReview, setIsRequestingReview] = useState(false);
  
  // Estado para avales
  const [avalesRecibidos, setAvalesRecibidos] = useState([]);
  const [avalesPendientes, setAvalesPendientes] = useState([]);
  const [solicitudesAval, setSolicitudesAval] = useState([]);
  const [avalRequestEmail, setAvalRequestEmail] = useState('');
  const [isRequestingAval, setIsRequestingAval] = useState(false);
  const [isProcessingAval, setIsProcessingAval] = useState(null); // ID del aval que se está procesando
  
  // Estado para eliminar reseña
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const { open: isDeleteReviewModalOpen, onOpen: onDeleteReviewModalOpen, onClose: onDeleteReviewModalClose } = useDisclosure();
  
  // Estado para confirmar solicitud de reseña
  const { open: isConfirmReviewRequestOpen, onOpen: onConfirmReviewRequestOpen, onClose: onConfirmReviewRequestClose } = useDisclosure();
  
  // Estados para el repertorio
  const [repertorioInput, setRepertorioInput] = useState('');
  const [marchasFiltradas, setMarchasFiltradas] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Estados para editar ensayos
  const [ensayoEditando, setEnsayoEditando] = useState(null);
  const { open: isEnsayoModalOpen, onOpen: onEnsayoModalOpen, onClose: onEnsayoModalClose } = useDisclosure();
  
  // Modal para añadir marcha manual
  const { open: isAddMarchaModalOpen, onOpen: onAddMarchaModalOpen, onClose: onAddMarchaModalClose } = useDisclosure();
  
  // Modal para confirmar eliminación de todas las fotos
  const { open: isDeleteAllPhotosModalOpen, onOpen: onDeleteAllPhotosModalOpen, onClose: onDeleteAllPhotosModalClose } = useDisclosure();
  const [isDeletingPhotos, setIsDeletingPhotos] = useState(false);
  
  const [nuevaMarcha, setNuevaMarcha] = useState({
    nombre: '',
    autor: '',
    autorPersonalizado: '',
    tipo: ''
  });
  const [autoresFiltrados, setAutoresFiltrados] = useState([]);
  const [showAutorSuggestions, setShowAutorSuggestions] = useState(false);
  
  // Estados para escaneo de PDF
  const [isScanningPdf, setIsScanningPdf] = useState(false);
  const [extractedMarchas, setExtractedMarchas] = useState([]);
  const [selectedExtractedMarchas, setSelectedExtractedMarchas] = useState([]);
  const { open: isPdfScanModalOpen, onOpen: onPdfScanModalOpen, onClose: onPdfScanModalClose } = useDisclosure();
  const { open: isImportArchivoModalOpen, onOpen: onImportArchivoModalOpen, onClose: onImportArchivoModalClose } = useDisclosure();
  const pdfInputRef = useRef(null);
  /** PDF escaneo repertorio desde Tu web (pestaña Perfil desmontada con isLazy). */
  const pdfTuWebRepertorioInputRef = useRef(null);

  const [archivoObrasList, setArchivoObrasList] = useState([]);
  const [archivoCarpetasList, setArchivoCarpetasList] = useState([]);
  const [archivoMusicaLoaded, setArchivoMusicaLoaded] = useState(false);
  const [archivoMusicaLoading, setArchivoMusicaLoading] = useState(false);
  const [repertorioArchivoFiltro, setRepertorioArchivoFiltro] = useState('');
  const [repertorioManualNombre, setRepertorioManualNombre] = useState('');
  const [repertorioManualAutor, setRepertorioManualAutor] = useState('');
  const [repertorioManualTipo, setRepertorioManualTipo] = useState('');
  const [importArchivoTab, setImportArchivoTab] = useState(0);
  const [importArchivoFiltro, setImportArchivoFiltro] = useState('');
  const [importCarpetasSeleccion, setImportCarpetasSeleccion] = useState([]);
  const [importObrasSeleccion, setImportObrasSeleccion] = useState([]);

  // Referencias para navegación a campos
  const nombreRef = useRef(null);
  const responsableNombreRef = useRef(null);
  const responsableApellidosRef = useRef(null);
  const responsableEmailRef = useRef(null);
  const calleRef = useRef(null);
  const ciudadRef = useRef(null);
  const provinciaRef = useRef(null);
  
  // Estado específico para bandas
  const [userType] = useState('banda');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const obrasArchivoFiltradas = useMemo(() => {
    const q = repertorioArchivoFiltro.trim().toLowerCase();
    const list = archivoObrasList || [];
    if (!q) return list;
    return list.filter((o) => {
      const hay = (s) => (s || '').toLowerCase().includes(q);
      return hay(o.titulo) || hay(o.autor) || hay(o?.genero?.nombre);
    });
  }, [archivoObrasList, repertorioArchivoFiltro]);

  const obrasImportArchivoFiltradas = useMemo(() => {
    const q = importArchivoFiltro.trim().toLowerCase();
    const list = archivoObrasList || [];
    if (!q) return list;
    return list.filter((o) => {
      const hay = (s) => (s || '').toLowerCase().includes(q);
      return hay(o.titulo) || hay(o.autor) || hay(o?.genero?.nombre);
    });
  }, [archivoObrasList, importArchivoFiltro]);

  const loadArchivoMusical = useCallback(async () => {
    if (archivoMusicaLoading) return archivoMusicaLoaded;
    if (archivoMusicaLoaded) return true;
    setArchivoMusicaLoading(true);
    try {
      const [resObras, resCarpetas] = await Promise.all([
        bandasAPI.listObrasPartituras(),
        bandasAPI.listPartiturasCarpetas(),
      ]);
      setArchivoObrasList(Array.isArray(resObras.data) ? resObras.data : []);
      setArchivoCarpetasList(Array.isArray(resCarpetas.data) ? resCarpetas.data : []);
      setArchivoMusicaLoaded(true);
      return true;
    } catch (e) {
      console.error(e);
      toaster.create({
        title: 'No se pudo cargar el archivo de partituras',
        description: e.response?.data?.message || 'Inténtalo más tarde',
        type: 'error',
        duration: 5000,
      });
      return false;
    } finally {
      setArchivoMusicaLoading(false);
    }
  }, [archivoMusicaLoaded, archivoMusicaLoading]);

  /** Carga obras y carpetas del archivo de partituras al abrir Tu web (para importar al repertorio público). */
  useEffect(() => {
    if (activeTab !== 3 || archivoMusicaLoaded || archivoMusicaLoading) return;
    loadArchivoMusical();
  }, [activeTab, archivoMusicaLoaded, archivoMusicaLoading, loadArchivoMusical]);

  const perfilTabParam = searchParams.get('tab');

  /** Colaborador: solo Tu web en /banda/perfil; sin Tu web → primera sección de Gestión. */
  useEffect(() => {
    if (!isColaboradorBanda || !user) return;
    if (location.pathname !== '/banda/perfil') return;
    const hasTuWeb = canAccessTuWeb(user);
    const firstG = firstColaboradorGestionPath(user);
    const tab = perfilTabParam;

    if (tab === 'tuweb' && hasTuWeb) {
      setActiveTab(3);
      return;
    }
    if (!hasTuWeb && firstG) {
      navigate(firstG, { replace: true });
      return;
    }
    if (hasTuWeb && !firstG) {
      if (tab !== 'tuweb') {
        navigate('/banda/perfil?tab=tuweb', { replace: true });
      }
      setActiveTab(3);
      return;
    }
    if (hasTuWeb && firstG && tab !== 'tuweb') {
      navigate(firstG, { replace: true });
    }
  }, [isColaboradorBanda, user, location.pathname, perfilTabParam, navigate]);

  const hideColaboradorProfileTabs =
    isColaboradorBanda && canAccessTuWeb(user) && perfilTabParam === 'tuweb';

  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const locationJustSelectedRef = useRef(false);
  
  // Estados para añadir videos de YouTube
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');

  const [profileData, setProfileData] = useState({
    // Perfil público
    nombre: '',
    bio: '',
    nMusicos: '',
    avatar: null,
    fondoHeaderImagen: null, // Imagen de fondo personalizable para la card del header
    imagenCabeceraWeb: null, // Hero del micrositio (distinta del fondo de perfil público)
    fotos: [],
    videos: [], // Array de {titulo, url} para videos de YouTube
    contactoPublico: {
      telefono: '',
      email: '',
      website: '',
    },
    redesSociales: {
      instagram: '',
      facebook: '',
      youtube: '',
      twitter: '',
      tiktok: '',
    },
    especialidades: [],
    tipoGrupo: '',
    tipoAgrupacionInstrumentalId: '',
    tipoAgrupacionInstrumental: null,
    anoFundacion: '',
    repertorio: [], // Array de obras/marchas del repertorio
    // Horarios de ensayo
    ensayos: {
      publico: false, // Si se muestran públicamente o no
      horarios: [] // Array de { id, diaSemana, horaInicio, horaFin, ubicacion, notas }
    },
    localizacion: {
      calle: '',
      numero: '',
      ciudad: '',
      provincia: '',
      codigoPostal: '',
      pais: 'España',
    },
    lat: null,
    lng: null,
    
    // Información para verificación
    cif: '',
    personaResponsable: {
      nombre: '',
      apellidos: '',
      dni: '',
      telefono: '',
      email: '',
    },
    // Específico para bandas
    kmDispuestos: '',
    // Gestión de disponibilidad
    fechasOcupadas: [], // Array de fechas ocupadas {date: '2024-03-15', evento: 'Concierto', tipo: 'ocupado'}
    fechasDisponibles: [], // Array de fechas disponibles {date: '2024-03-15', precio: 500, notas: 'Disponible'}
    disponibilidadGeneral: 'disponible', // 'disponible', 'parcial', 'ocupado'
    // Específico para cofradías
    tipoEventos: [],
    // Reseñas (cargadas desde la API)
    resenas: [],
    solicitudesResenaPendientes: [],
  });
  profileDataRef.current = profileData;

  const tuWebMicrositeBodyOrderIds = useMemo(() => getMicrositeBodySectionOrder(msConfig), [msConfig]);

  const tuWebContenidoSectionsVisibles = useMemo(
    () =>
      tuWebMicrositeBodyOrderIds
        .map((id) => MICROSITE_SECTION_CATALOG.find((s) => s.id === id))
        .filter((s) => s && !isMicrositeSectionHidden(s.id, msConfig)),
    [tuWebMicrositeBodyOrderIds, msConfig],
  );

  const [tuWebContenidoPanelId, setTuWebContenidoPanelId] = useState(TU_WEB_CONTENIDO_CABECERA_ID);
  const [micrositeContenidoSavedDigest, setMicrositeContenidoSavedDigest] = useState('__init__');

  const tuWebContenidoNavItems = useMemo(() => {
    const items = [];
    if (isBandaAccountUserType(user?.userType)) {
      items.push({
        id: TU_WEB_CONTENIDO_CABECERA_ID,
        label: 'Cabecera (hero)',
        desc: 'Logo, portada y elementos del inicio',
        color: 'purple',
      });
    }
    tuWebContenidoSectionsVisibles.forEach((s) => items.push(s));
    return items;
  }, [user?.userType, tuWebContenidoSectionsVisibles]);

  useEffect(() => {
    const ids = tuWebContenidoNavItems.map((i) => i.id);
    if (ids.length === 0) return;
    setTuWebContenidoPanelId((cur) => (ids.includes(cur) ? cur : ids[0]));
  }, [tuWebContenidoNavItems]);

  useEffect(() => {
    const shouldLoad =
      tuWebMenu === 'contenido' && tuWebContenidoPanelId === 'calendario' && isBandaAccountUserType(user?.userType);
    if (!shouldLoad) {
      setTuWebCalendarioLoading(false);
      return;
    }

    let cancelled = false;
    setTuWebCalendarioLoading(true);
    (async () => {
      try {
        const [actsRes, ensRes] = await Promise.all([actuacionesAPI.getAll(), ensayosAPI.getAll()]);
        if (cancelled) return;

        const actsRaw = actsRes?.data;
        const ensRaw = ensRes?.data;
        const actsList = Array.isArray(actsRaw) ? actsRaw : [];
        const ensList = Array.isArray(ensRaw) ? ensRaw : [];

        const acts = sortMicrositeCalendarioItems(
          actsList
            .filter((a) => a && a.id)
            .slice(0, 200),
        );

        const ensayos = sortMicrositeCalendarioItems(
          ensList
            .filter((e) => e && e.id)
            .filter((e) => (e.estado ? e.estado !== 'CANCELADO' : true))
            .filter((e) => !!e.fecha)
            .slice(0, 300),
        );

        setTuWebCalendarioActuacionesAll(acts);
        setTuWebCalendarioEnsayosAll(ensayos);
      } catch {
        // si falla, no bloqueamos el editor: se seguirá mostrando el toggle general
      } finally {
        if (!cancelled) setTuWebCalendarioLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tuWebMenu, tuWebContenidoPanelId, user?.userType]);

  const tuWebCalendarioActuaciones = useMemo(() => {
    const all = tuWebCalendarioActuacionesAll || [];
    const includePast = msConfig.calendarioIncluirActuacionesPasadas === true;
    return all.filter((a) => isCalendarioItemEnVentanaTuWeb(a, { includePast }));
  }, [tuWebCalendarioActuacionesAll, msConfig.calendarioIncluirActuacionesPasadas]);

  const tuWebCalendarioEnsayos = useMemo(() => {
    const all = tuWebCalendarioEnsayosAll || [];
    const includePast = msConfig.calendarioIncluirEnsayosPasados === true;
    return all.filter((e) => isCalendarioItemEnVentanaTuWeb(e, { includePast }));
  }, [tuWebCalendarioEnsayosAll, msConfig.calendarioIncluirEnsayosPasados]);

  const micrositeContenidoDigestActual = useMemo(
    () => computeMicrositeContenidoDigest(msConfig, profileData, user?.userType),
    [msConfig, profileData, user?.userType],
  );

  const micrositeContenidoDirty =
    micrositeContenidoSavedDigest !== '__init__' && micrositeContenidoDigestActual !== micrositeContenidoSavedDigest;

  const tuWebContenidoUnsavedSectionIds = useMemo(
    () =>
      computeTuWebContenidoUnsavedSectionIds(
        micrositeContenidoSavedDigest,
        micrositeContenidoDigestActual,
        user?.userType,
      ),
    [micrositeContenidoSavedDigest, micrositeContenidoDigestActual, user?.userType],
  );

  const tuWebContenidoUnsavedSectionLabels = useMemo(() => {
    return Array.from(tuWebContenidoUnsavedSectionIds)
      .map((id) => MICROSITE_SECTION_CATALOG.find((c) => c.id === id)?.label || id)
      .filter(Boolean);
  }, [tuWebContenidoUnsavedSectionIds]);

  /** Guardado explícito de todo `micrositeConfig` (Contenido, Estilos manuales, etc.). No confundir con la barra naranja del perfil. Lee `profileData` vía ref para no romper el orden de hooks. */
  const saveMicrositeContenidoManual = useCallback(async () => {
    const pd = profileDataRef.current;
    try {
      setMsConfigSaving(true);
      if (paletteSaveDebounceRef.current) {
        clearTimeout(paletteSaveDebounceRef.current);
        paletteSaveDebounceRef.current = null;
      }
      const payload = pendingPaletteSaveRef.current || msConfig;
      pendingPaletteSaveRef.current = null;

      if (isBandaAccountUserType(user?.userType)) {
        const resProfile = await updateProfile({
          contactoPublico: pd.contactoPublico,
          redesSociales: pd.redesSociales,
          localizacion: pd.localizacion,
          lat: pd.lat,
          lng: pd.lng,
        });
        if (!resProfile.success) {
          throw new Error(resProfile.error || 'No se pudieron guardar los datos de contacto del perfil');
        }
        setInitialProfileData((prev) =>
          prev
            ? {
                ...prev,
                contactoPublico: { ...pd.contactoPublico },
                redesSociales: { ...pd.redesSociales },
                localizacion: { ...pd.localizacion },
                lat: pd.lat,
                lng: pd.lng,
              }
            : prev,
        );
      }

      await bandasAPI.updateMicrositeConfig(payload);
      setMicrositeContenidoSavedDigest(
        computeMicrositeContenidoDigest(payload, profileDataRef.current, user?.userType),
      );
      toaster.create({
        title: 'Guardado',
        description:
          isBandaAccountUserType(user?.userType)
            ? 'Contacto del perfil y contenido de la web actualizados'
            : 'El contenido de tu web se ha actualizado',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description: err.response?.data?.message || err.message || 'No se pudo guardar',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setMsConfigSaving(false);
    }
  }, [msConfig, updateProfile, user?.userType]);

  const [errors, setErrors] = useState({});
  const [verificationStatus, setVerificationStatus] = useState('not_requested'); // 'not_requested', 'pending', 'verified', 'rejected'
  const [verificationCode, setVerificationCode] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  
  // Modales
  const { open: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const { open: isVerificationModalOpen, onOpen: onVerificationModalOpen, onClose: onVerificationModalClose } = useDisclosure();
  const { open: isErrorModalOpen, onOpen: onErrorModalOpen, onClose: onErrorModalClose } = useDisclosure();
  const { open: isDeactivateModalOpen, onOpen: onDeactivateModalOpen, onClose: onDeactivateModalClose } = useDisclosure();
  const { open: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { open: isUsernameModalOpen, onOpen: onUsernameModalOpen, onClose: onUsernameModalClose } = useDisclosure();
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'success' // 'success', 'error', 'warning', 'info'
  });

  // Estado para detectar cambios sin guardar
  const [initialProfileData, setInitialProfileData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const bg = useColorModeValue('gray.50', 'gray.900');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  useEffect(() => {
    if (!micrositePublicUrl) {
      if (useMicrositeUrlAsWebsite) setUseMicrositeUrlAsWebsite(false);
      return;
    }
    const normalize = (v) => String(v || '').trim().replace(/\/+$/, '').toLowerCase();
    const shouldUseMicrositeUrl = normalize(profileData?.contactoPublico?.website) === normalize(micrositePublicUrl);
    if (shouldUseMicrositeUrl !== useMicrositeUrlAsWebsite) {
      setUseMicrositeUrlAsWebsite(shouldUseMicrositeUrl);
    }
  }, [micrositePublicUrl, profileData?.contactoPublico?.website, useMicrositeUrlAsWebsite]);

  useEffect(() => {
    if (isLoadingProfile || !isProfileLoadedRef.current) return;
    setMicrositeContenidoSavedDigest((prev) => (prev === '__init__' ? micrositeContenidoDigestActual : prev));
  }, [isLoadingProfile, micrositeContenidoDigestActual]);

  /** Solo id de Banda (no user.id: la API pública es GET /bandas/:bandaId). */
  const publicProfileShareUrl = useMemo(
    () => buildPublicProfileShareUrl('banda', profile?.id),
    [profile?.id],
  );

  const [directivaFotoUploadingIndex, setDirectivaFotoUploadingIndex] = useState(null);
  const [patrocinadorLogoUploadingIndex, setPatrocinadorLogoUploadingIndex] = useState(null);
  const [noticiaImagenUploadingIndex, setNoticiaImagenUploadingIndex] = useState(null);
  const [noticiaEditorOpen, setNoticiaEditorOpen] = useState(false);
  const [noticiaEditorIndex, setNoticiaEditorIndex] = useState(null);
  const [noticiaEditorDraft, setNoticiaEditorDraft] = useState({ id: '', titulo: '', encabezado: '', cuerpo: '', activo: false, imagen: '', createdAt: '' });
  const noticiaBodyRef = useRef(null);
  const noticiaThumbInputRef = useRef(null);
  const noticiaThumbPickRef = useRef(null);
  const noticiaMediaInputRef = useRef(null);
  const noticiaMediaPickRef = useRef(null);
  const [noticiaThumbUploading, setNoticiaThumbUploading] = useState(false);
  const [noticiaMediaUploading, setNoticiaMediaUploading] = useState(false);
  const [noticiaLinkModalOpen, setNoticiaLinkModalOpen] = useState(false);
  const [noticiaLinkDraft, setNoticiaLinkDraft] = useState('https://');
  const [noticiaVideoModalOpen, setNoticiaVideoModalOpen] = useState(false);
  const [noticiaVideoDraft, setNoticiaVideoDraft] = useState('https://');

  const handleDirectivaFotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = directivaFotoPickRef.current;
    directivaFotoPickRef.current = null;
    if (!file || !pick) return;
    const err = validateDirectivaFotoFile(file);
    if (err) {
      toaster.create({ title: err, type: 'warning', duration: 4000 });
      return;
    }
    try {
      setDirectivaFotoUploadingIndex(pick.index);
      const res = await bandasAPI.uploadMicrositeDirectivaFoto(pick.id, file, pick.previousUrl || undefined);
      const url = res.data?.url;
      if (!url) throw new Error('Sin URL');
      setMsConfig((p) => {
        const d = [...p.directiva];
        if (!d[pick.index] || d[pick.index].id !== pick.id) return p;
        d[pick.index] = { ...d[pick.index], foto: url };
        return { ...p, directiva: d };
      });
      toaster.create({
        title: 'Foto subida',
        description: 'Pulsa «Guardar cambios» en Tu web → Contenido (barra naranja) si quieres asegurar el resto de cambios de la web.',
        type: 'success',
        duration: 3500,
      });
    } catch (err) {
      toaster.create({
        title: 'No se pudo subir la foto',
        description: err.response?.data?.message || err.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setDirectivaFotoUploadingIndex(null);
    }
  };

  const queueDirectivaFotoPick = (index, memberId, previousUrl) => {
    if (!memberId) {
      toaster.create({ title: 'Falta el identificador del miembro', type: 'warning', duration: 4000 });
      return;
    }
    directivaFotoPickRef.current = { index, id: memberId, previousUrl: previousUrl || '' };
    requestAnimationFrame(() => directivaFotoInputRef.current?.click());
  };

  const removeDirectivaFotoAt = (index) => {
    setMsConfig((p) => {
      const row = p.directiva[index];
      if (row?.foto) {
        bandasAPI.deleteMicrositeDirectivaFoto(row.foto).catch(() => {});
      }
      const d = [...p.directiva];
      d[index] = { ...d[index], foto: '' };
      return { ...p, directiva: d };
    });
  };

  const handlePatrocinadorLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = patrocinadorLogoPickRef.current;
    patrocinadorLogoPickRef.current = null;
    if (!file || !pick) return;
    const err = validateDirectivaFotoFile(file);
    if (err) {
      toaster.create({ title: err, type: 'warning', duration: 4000 });
      return;
    }
    try {
      setPatrocinadorLogoUploadingIndex(pick.index);
      const res = await bandasAPI.uploadMicrositePatrocinadorLogo(pick.id, file, pick.previousUrl || undefined);
      const url = res.data?.url;
      if (!url) throw new Error('Sin URL');
      setMsConfig((p) => {
        const ps = [...p.patrocinadores];
        if (!ps[pick.index] || ps[pick.index].id !== pick.id) return p;
        ps[pick.index] = { ...ps[pick.index], logo: url };
        return { ...p, patrocinadores: ps };
      });
      toaster.create({
        title: 'Logo subido',
        description: 'Pulsa «Guardar cambios» en Tu web → Contenido (barra naranja) si quieres asegurar el resto de cambios de la web.',
        type: 'success',
        duration: 3500,
      });
    } catch (err) {
      toaster.create({
        title: 'No se pudo subir el logo',
        description: err.response?.data?.message || err.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setPatrocinadorLogoUploadingIndex(null);
    }
  };

  const queuePatrocinadorLogoPick = (index, sponsorId, previousUrl) => {
    if (!sponsorId) {
      toaster.create({ title: 'Falta el identificador del patrocinador', type: 'warning', duration: 4000 });
      return;
    }
    patrocinadorLogoPickRef.current = { index, id: sponsorId, previousUrl: previousUrl || '' };
    requestAnimationFrame(() => patrocinadorLogoInputRef.current?.click());
  };

  const removePatrocinadorLogoAt = (index) => {
    setMsConfig((p) => {
      const row = p.patrocinadores[index];
      if (row?.logo) {
        bandasAPI.deleteMicrositePatrocinadorLogo(row.logo).catch(() => {});
      }
      const ps = [...p.patrocinadores];
      ps[index] = { ...ps[index], logo: '' };
      return { ...p, patrocinadores: ps };
    });
  };

  const handleNoticiaImagenFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = noticiaImagenPickRef.current;
    noticiaImagenPickRef.current = null;
    if (!file || !pick) return;
    const err = validateDirectivaFotoFile(file);
    if (err) {
      toaster.create({ title: err, type: 'warning', duration: 4000 });
      return;
    }
    try {
      setNoticiaImagenUploadingIndex(pick.index);
      const res = await bandasAPI.uploadMicrositeNoticiaImagen(pick.id, file, pick.previousUrl || undefined);
      const url = res.data?.url;
      if (!url) throw new Error('Sin URL');
      setMsConfig((p) => {
        const ns = [...(p.noticias || [])];
        if (!ns[pick.index] || ns[pick.index].id !== pick.id) return p;
        ns[pick.index] = { ...ns[pick.index], imagen: url };
        return { ...p, noticias: ns };
      });
      toaster.create({
        title: 'Imagen subida',
        description: 'Pulsa «Guardar cambios» en Tu web → Contenido (barra naranja) si quieres asegurar el resto de cambios de la web.',
        type: 'success',
        duration: 3500,
      });
    } catch (err) {
      toaster.create({
        title: 'No se pudo subir la imagen',
        description: err.response?.data?.message || err.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setNoticiaImagenUploadingIndex(null);
    }
  };

  const queueNoticiaImagenPick = (index, noticiaId, previousUrl) => {
    if (!noticiaId) {
      toaster.create({ title: 'Falta el identificador de la noticia', type: 'warning', duration: 4000 });
      return;
    }
    noticiaImagenPickRef.current = { index, id: noticiaId, previousUrl: previousUrl || '' };
    requestAnimationFrame(() => noticiaImagenInputRef.current?.click());
  };

  const removeNoticiaImagenAt = (index) => {
    setMsConfig((p) => {
      const row = (p.noticias || [])[index];
      if (row?.imagen) {
        bandasAPI.deleteMicrositeNoticiaImagen(row.imagen).catch(() => {});
      }
      const ns = [...(p.noticias || [])];
      ns[index] = { ...ns[index], imagen: '' };
      return { ...p, noticias: ns };
    });
  };

  const openNoticiaEditor = (indexOrNull) => {
    if (indexOrNull == null) {
      const draft = newNoticia();
      setNoticiaEditorDraft({
        id: draft.id,
        titulo: '',
        encabezado: '',
        cuerpo: '',
        activo: false,
        imagen: '',
        createdAt: draft.createdAt,
      });
      setNoticiaEditorIndex(null);
      setNoticiaEditorOpen(true);
      return;
    }
    const row = (msConfig.noticias || [])[indexOrNull];
    if (!row) return;
    const raw = String(row.texto || '');
    const lines = raw.split('\n');
    const tLine = (lines[0] || '').replace(/^\s*#\s+/, '').trim();
    const hLine = (lines[1] || '').replace(/^\s*##\s+/, '').trim();
    const body = lines.slice(2).join('\n').trim();
    setNoticiaEditorDraft({
      id: row.id,
      titulo: tLine,
      encabezado: hLine,
      cuerpo: body,
      activo: row.activo === true,
      imagen: row.imagen || '',
      createdAt: row.createdAt || new Date().toISOString(),
    });
    setNoticiaEditorIndex(indexOrNull);
    setNoticiaEditorOpen(true);
  };

  const closeNoticiaEditor = () => {
    setNoticiaEditorOpen(false);
    setNoticiaEditorIndex(null);
  };

  const saveNoticiaEditor = () => {
    const titulo = String(noticiaEditorDraft.titulo || '').trim();
    const encabezado = String(noticiaEditorDraft.encabezado || '').trim();
    const cuerpo = String(noticiaEditorDraft.cuerpo || '').trim();
    const composed = `# ${titulo}\n## ${encabezado}\n${cuerpo}`.trim();

    setMsConfig((p) => {
      const ns = [...(p.noticias || [])];
      const row = {
        id: noticiaEditorDraft.id,
        activo: noticiaEditorDraft.activo === true,
        texto: composed,
        imagen: noticiaEditorDraft.imagen || '',
        createdAt: noticiaEditorDraft.createdAt || new Date().toISOString(),
      };
      if (noticiaEditorIndex == null) ns.unshift(row);
      else ns[noticiaEditorIndex] = row;
      return { ...p, noticias: ns };
    });

    closeNoticiaEditor();
    toaster.create({
      title: 'Noticia lista',
      description: 'Pulsa «Guardar cambios» en la barra naranja para publicarla en tu web.',
      type: 'success',
      duration: 3500,
    });
  };

  const insertAtBodyCursor = (insertText, { selectInside = false } = {}) => {
    const el = noticiaBodyRef.current;
    if (!el) return;
    const value = String(el.value || '');
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}${insertText}${after}`;
    setNoticiaEditorDraft((d) => ({ ...d, cuerpo: next }));
    requestAnimationFrame(() => {
      el.focus();
      const posStart = start + (selectInside ? insertText.indexOf('](') + 2 : insertText.length);
      const posEnd = start + insertText.length - (selectInside ? 1 : 0);
      el.setSelectionRange(posStart, posEnd);
    });
  };

  const wrapBodySelection = (open, close) => {
    const el = noticiaBodyRef.current;
    if (!el) return;
    const value = String(el.value || '');
    const start0 = el.selectionStart ?? value.length;
    const end0 = el.selectionEnd ?? value.length;
    const start = Math.min(start0, end0);
    const end = Math.max(start0, end0);
    const sel = value.slice(start, end);
    const next = `${value.slice(0, start)}${open}${sel}${close}${value.slice(end)}`;
    setNoticiaEditorDraft((d) => ({ ...d, cuerpo: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + open.length, end + open.length);
    });
  };

  const toggleBodyListPrefix = (type) => {
    const el = noticiaBodyRef.current;
    if (!el) return;
    const value = String(el.value || '');
    const start0 = el.selectionStart ?? 0;
    const end0 = el.selectionEnd ?? 0;
    const start = Math.min(start0, end0);
    const end = Math.max(start0, end0);
    const before = value.slice(0, start);
    const mid = value.slice(start, end);
    const after = value.slice(end);
    const lines = mid.split('\n');
    const nextLines = lines.map((l, idx) => {
      const t = l.replace(/^(\s*)(- |\d+\. )/, '$1');
      if (type === 'ul') return `- ${t}`;
      return `${idx + 1}. ${t}`;
    });
    const next = `${before}${nextLines.join('\n')}${after}`;
    setNoticiaEditorDraft((d) => ({ ...d, cuerpo: next }));
    requestAnimationFrame(() => el.focus());
  };

  const parseYouTubeId = (url) => {
    const u = String(url || '').trim();
    const m1 = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (m1?.[1]) return m1[1];
    const m2 = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i);
    if (m2?.[1]) return m2[1];
    return null;
  };

  const renderNoticiaInlinePreview = (text) => {
    const src = String(text || '');
    const parts = [];
    const re = /(\*\*[^*]+\*\*|_[^_]+_|\+\+[^+]+\+\+|\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m;
    while ((m = re.exec(src)) !== null) {
      if (m.index > last) parts.push({ t: 'text', v: src.slice(last, m.index) });
      const token = m[0];
      if (token.startsWith('**')) parts.push({ t: 'b', v: token.slice(2, -2) });
      else if (token.startsWith('_')) parts.push({ t: 'i', v: token.slice(1, -1) });
      else if (token.startsWith('++')) parts.push({ t: 'u', v: token.slice(2, -2) });
      else if (token.startsWith('[')) {
        const mm = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (mm?.[1] && mm?.[2]) parts.push({ t: 'a', v: mm[1], href: mm[2] });
        else parts.push({ t: 'text', v: token });
      }
      last = m.index + token.length;
    }
    if (last < src.length) parts.push({ t: 'text', v: src.slice(last) });
    return parts.map((p, idx) => {
      if (p.t === 'b') return <Text as="span" fontWeight="800" key={idx}>{p.v}</Text>;
      if (p.t === 'i') return <Text as="span" fontStyle="italic" key={idx}>{p.v}</Text>;
      if (p.t === 'u') return <Text as="span" textDecoration="underline" key={idx}>{p.v}</Text>;
      if (p.t === 'a') {
        return (
          <Link
            key={idx}
            href={p.href}
            color="purple.600"
            fontWeight="700"
            target='_blank'
            rel='noopener noreferrer'>
            {p.v}
          </Link>
        );
      }
      return <React.Fragment key={idx}>{p.v}</React.Fragment>;
    });
  };

  const renderNoticiaPreview = (fullText) => {
    const lines = String(fullText || '').split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
      const line = String(lines[i] || '');
      if (!line.trim()) {
        out.push(<Box key={`sp-${i}`} h={2} />);
        i += 1;
        continue;
      }
      const ym = line.trim().match(/^\{\{youtube:([a-zA-Z0-9_-]{11})\}\}$/);
      if (ym?.[1]) {
        const id = ym[1];
        out.push(
          <Box key={`yt-${i}`} rounded="xl" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <AspectRatio ratio={16 / 9}>
              <iframe
                title="YouTube"
                src={`https://www.youtube-nocookie.com/embed/${id}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </AspectRatio>
          </Box>,
        );
        i += 1;
        continue;
      }
      const im = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (im?.[2]) {
        out.push(
          <Box key={`img-${i}`} rounded="xl" overflow="hidden" borderWidth="1px" borderColor="gray.100" bg="gray.50">
            <Image src={im[2]} alt={im[1] || 'Imagen'} w="full" maxH={{ base: '280px', md: '420px' }} objectFit="contain" />
          </Box>,
        );
        i += 1;
        continue;
      }
      if (line.trim().startsWith('- ')) {
        const items = [];
        while (i < lines.length && String(lines[i] || '').trim().startsWith('- ')) {
          items.push(String(lines[i]).trim().slice(2));
          i += 1;
        }
        out.push(
          <List.Root as="ul" key={`ul-${i}`} gap={1} ps={5}>
            {items.map((t, idx) => (
              <List.Item key={idx} color="gray.700" lineHeight="1.9">
                {renderNoticiaInlinePreview(t)}
              </List.Item>
            ))}
          </List.Root>,
        );
        continue;
      }
      if (/^\s*\d+\.\s+/.test(line.trim())) {
        const items = [];
        while (i < lines.length && /^\s*\d+\.\s+/.test(String(lines[i] || '').trim())) {
          items.push(String(lines[i]).trim().replace(/^\d+\.\s+/, ''));
          i += 1;
        }
        out.push(
          <List.Root as="ol" key={`ol-${i}`} gap={1} ps={5}>
            {items.map((t, idx) => (
              <List.Item key={idx} color="gray.700" lineHeight="1.9">
                {renderNoticiaInlinePreview(t)}
              </List.Item>
            ))}
          </List.Root>,
        );
        continue;
      }
      if (line.startsWith('# ')) {
        out.push(
          <Text key={`h1-${i}`} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="900" lineHeight="1.4">
            {renderNoticiaInlinePreview(line.slice(2))}
          </Text>,
        );
        i += 1;
        continue;
      }
      if (line.startsWith('## ')) {
        out.push(
          <Text key={`h2-${i}`} fontSize={{ base: 'md', md: 'lg' }} fontWeight="800" lineHeight="1.5">
            {renderNoticiaInlinePreview(line.slice(3))}
          </Text>,
        );
        i += 1;
        continue;
      }
      out.push(
        <Text key={`p-${i}`} fontSize="md" lineHeight="1.9" color="gray.700" whiteSpace="pre-wrap">
          {renderNoticiaInlinePreview(line)}
        </Text>,
      );
      i += 1;
    }
    return <VStack align="stretch" gap={2}>{out}</VStack>;
  };

  const handlePickNoticiaThumb = () => {
    noticiaThumbPickRef.current = { noticiaId: noticiaEditorDraft.id, previousUrl: noticiaEditorDraft.imagen || '' };
    requestAnimationFrame(() => noticiaThumbInputRef.current?.click());
  };

  const handleNoticiaThumbChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = noticiaThumbPickRef.current;
    noticiaThumbPickRef.current = null;
    if (!file || !pick) return;
    const err = validateDirectivaFotoFile(file);
    if (err) {
      toaster.create({ title: err, type: 'warning', duration: 4000 });
      return;
    }
    try {
      setNoticiaThumbUploading(true);
      const res = await bandasAPI.uploadMicrositeNoticiaImagen(pick.noticiaId, file, pick.previousUrl || undefined);
      const url = res.data?.url;
      if (!url) throw new Error('Sin URL');
      setNoticiaEditorDraft((d) => ({ ...d, imagen: url }));
      toaster.create({ title: 'Miniatura subida', type: 'success', duration: 2500 });
    } catch (err2) {
      toaster.create({
        title: 'No se pudo subir la miniatura',
        description: err2.response?.data?.message || err2.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setNoticiaThumbUploading(false);
    }
  };

  const handleInsertImageUpload = () => {
    noticiaMediaPickRef.current = { noticiaId: noticiaEditorDraft.id };
    requestAnimationFrame(() => noticiaMediaInputRef.current?.click());
  };

  const handleNoticiaMediaChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = noticiaMediaPickRef.current;
    noticiaMediaPickRef.current = null;
    if (!file || !pick) return;
    const err = validateDirectivaFotoFile(file);
    if (err) {
      toaster.create({ title: err, type: 'warning', duration: 4000 });
      return;
    }
    try {
      setNoticiaMediaUploading(true);
      const mediaId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `med-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const res = await bandasAPI.uploadMicrositeNoticiaMedia(pick.noticiaId, mediaId, file);
      const url = res.data?.url;
      if (!url) throw new Error('Sin URL');
      insertAtBodyCursor(`\n![imagen](${url})\n`);
      toaster.create({ title: 'Imagen insertada', type: 'success', duration: 2500 });
    } catch (err2) {
      toaster.create({
        title: 'No se pudo insertar la imagen',
        description: err2.response?.data?.message || err2.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setNoticiaMediaUploading(false);
    }
  };

  const applyNoticiaBlockStyle = (noticiaId, index, style) => {
    const el = document.getElementById(`noticia-texto-${noticiaId}`);
    if (!el) return;
    const value = String(el.value || '');
    const selStart = el.selectionStart ?? value.length;
    const selEnd = el.selectionEnd ?? value.length;
    const start = Math.min(selStart, selEnd);
    const end = Math.max(selStart, selEnd);

    // Apply per-line prefix on selected range
    const before = value.slice(0, start);
    const mid = value.slice(start, end);
    const after = value.slice(end);

    const normalizeLine = (line) => line.replace(/^(\s*)(#{1,2}\s+)/, '$1');
    const pref =
      style === 'title' ? '# ' :
      style === 'heading' ? '## ' :
      '';
    const nextMid = mid
      .split('\n')
      .map((line) => {
        const base = normalizeLine(line);
        return pref ? `${pref}${base}` : base;
      })
      .join('\n');

    const next = `${before}${nextMid}${after}`;
    setMsConfig((p) => {
      const ns = [...(p.noticias || [])];
      if (!ns[index] || ns[index].id !== noticiaId) return p;
      ns[index] = { ...ns[index], texto: next };
      return { ...p, noticias: ns };
    });

    requestAnimationFrame(() => {
      const el2 = document.getElementById(`noticia-texto-${noticiaId}`);
      if (!el2) return;
      el2.focus();
      el2.setSelectionRange(start, start + nextMid.length);
    });
  };

  const wrapNoticiaInline = (noticiaId, index, wrap) => {
    const el = document.getElementById(`noticia-texto-${noticiaId}`);
    if (!el) return;
    const value = String(el.value || '');
    const selStart = el.selectionStart ?? value.length;
    const selEnd = el.selectionEnd ?? value.length;
    const start = Math.min(selStart, selEnd);
    const end = Math.max(selStart, selEnd);
    const selected = value.slice(start, end);
    const before = value.slice(0, start);
    const after = value.slice(end);
    const open = wrap.open;
    const close = wrap.close;
    const next = `${before}${open}${selected}${close}${after}`;

    setMsConfig((p) => {
      const ns = [...(p.noticias || [])];
      if (!ns[index] || ns[index].id !== noticiaId) return p;
      ns[index] = { ...ns[index], texto: next };
      return { ...p, noticias: ns };
    });

    requestAnimationFrame(() => {
      const el2 = document.getElementById(`noticia-texto-${noticiaId}`);
      if (!el2) return;
      el2.focus();
      // keep selection inside wrappers
      el2.setSelectionRange(start + open.length, end + open.length);
    });
  };

  // Efecto para cargar datos del perfil desde la API
  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (profile) {
          // Si ya se cargó el perfil antes, solo actualizar campos de imagen
          // para no sobreescribir datos locales no guardados del usuario
          if (isProfileLoadedRef.current) {
            setProfileData(prev => ({
              ...prev,
              avatar: profile.avatar || prev.avatar,
              fondoHeaderImagen: profile.fondoHeaderImagen || prev.fondoHeaderImagen,
              imagenCabeceraWeb: profile.imagenCabeceraWeb ?? prev.imagenCabeceraWeb,
              fotos: profile.galeria || profile.fotos || prev.fotos,
            }));
            setInitialProfileData(prev => ({
              ...prev,
              avatar: profile.avatar || null,
              fondoHeaderImagen: profile.fondoHeaderImagen || null,
              imagenCabeceraWeb: profile.imagenCabeceraWeb ?? null,
              fotos: profile.galeria || profile.fotos || [],
            }));
            const mcRefresh = profile.micrositeConfig;
            if (mcRefresh && typeof mcRefresh === 'object' && !Array.isArray(mcRefresh)) {
              setMsConfig((prev) => {
                const next = { ...prev };
                if ('mostrarPlantillaMusicosNosotros' in mcRefresh) {
                  next.mostrarPlantillaMusicosNosotros = readMostrarPlantillaMusicosNosotros(mcRefresh);
                }
                if ('plantillaMusicosNosotrosInstrumentosModo' in mcRefresh) {
                  next.plantillaMusicosNosotrosInstrumentosModo = readPlantillaMusicosInstrumentosModo(mcRefresh);
                }
                if ('plantillaMusicosNosotrosInstrumentoPorMusico' in mcRefresh) {
                  next.plantillaMusicosNosotrosInstrumentoPorMusico =
                    readPlantillaMusicosInstrumentoPorMusico(mcRefresh);
                }
                if ('mostrarEspecialidadesNosotros' in mcRefresh) {
                  next.mostrarEspecialidadesNosotros = readMostrarEspecialidadesNosotros(mcRefresh);
                }
                if ('seccionesOrden' in mcRefresh) {
                  next.seccionesOrden = getMicrositeBodySectionOrder(mcRefresh);
                }
                return next;
              });
            }
            return;
          }

          // Carga inicial completa: mapear todos los datos del perfil de la API al estado local
          setProfileData(prev => ({
            ...prev,
            nombre: profile.nombre || '',
            bio: profile.bio || '',
            nMusicos: profile.nMusicos || '',
            avatar: profile.avatar || null,
            fondoHeaderImagen: profile.fondoHeaderImagen || null,
            imagenCabeceraWeb: profile.imagenCabeceraWeb || null,
            fotos: profile.galeria || profile.fotos || [],
            videos: profile.videos || [],
            contactoPublico: mergeContactoPublicoForForm(
              profile.contactoPublico,
              profile.personaResponsable,
            ),
            redesSociales: profile.redesSociales || { instagram: '', facebook: '', youtube: '', twitter: '', tiktok: '' },
            especialidades: profile.especialidades || [],
            tipoGrupo: profile.tipoGrupo || '',
            tipoAgrupacionInstrumentalId: profile.tipoAgrupacionInstrumentalId || profile.tipoAgrupacionInstrumental?.id || '',
            tipoAgrupacionInstrumental: profile.tipoAgrupacionInstrumental || null,
            anoFundacion: profile.anoFundacion || '',
            repertorio: (profile.repertorio || []).map(r => ({
              id: r.id,
              marchaId: r.marchaId || r.marcha?.id,
              nombre: r.marcha?.nombre || '',
              autor: r.marcha?.autor || '',
              tipo: r.marcha?.tipo || '',
              fromDB: true,
            })),
            ensayos: {
              publico: profile.ensayos?.length > 0 && profile.ensayos[0]?.publico || false,
              horarios: (profile.ensayos || []).map(e => ({
                id: e.id,
                diaSemana: e.diaSemana,
                horaInicio: e.horaInicio,
                horaFin: e.horaFin,
                ubicacion: e.ubicacion || '',
                notas: e.notas || '',
                publico: e.publico || false,
              })),
            },
            localizacion: profile.localizacion || {
              calle: '', numero: '', ciudad: '', provincia: '', codigoPostal: '', pais: 'España',
            },
            lat: profile.lat || null,
            lng: profile.lng || null,
            cif: profile.cif || '',
            personaResponsable: profile.personaResponsable || {
              nombre: '', apellidos: '', dni: '', telefono: '', email: '',
            },
            kmDispuestos: profile.kmDispuestos || '',
            resenas: (profile.resenasRecibidas || []).map(r => ({
              id: r.id,
              cofradiaName: r.autorCofradia?.nombre || 'Anónimo',
              cofradiaAvatar: r.autorCofradia?.avatar || null,
              eventoNombre: r.evento?.nombre || null,
              eventoFecha: r.evento?.fecha || null,
              ratingGeneral: r.ratingGeneral,
              averageRating: [
                r.ratingPuntualidad,
                r.ratingComunicacion,
                r.ratingCalidadArtistica,
                r.ratingGeneral
              ].filter(Boolean).reduce((acc, val, _, arr) => acc + val / arr.length, 0),
              rating: r.ratingGeneral || r.rating,
              comment: r.comment,
              receivedDate: r.createdAt,
              isPublished: r.isPublished,
            })),
            }));
          
          // Guardar el estado inicial para detectar cambios
          const initialData = {
            nombre: profile.nombre || '',
            bio: profile.bio || '',
            nMusicos: profile.nMusicos || '',
            avatar: profile.avatar || null,
            fondoHeaderImagen: profile.fondoHeaderImagen || null,
            imagenCabeceraWeb: profile.imagenCabeceraWeb || null,
            fotos: profile.galeria || profile.fotos || [],
            videos: profile.videos || [],
            contactoPublico: mergeContactoPublicoForForm(
              profile.contactoPublico,
              profile.personaResponsable,
            ),
            redesSociales: profile.redesSociales || { instagram: '', facebook: '', youtube: '', twitter: '', tiktok: '' },
            especialidades: profile.especialidades || [],
            tipoGrupo: profile.tipoGrupo || '',
            tipoAgrupacionInstrumentalId: profile.tipoAgrupacionInstrumentalId || profile.tipoAgrupacionInstrumental?.id || '',
            tipoAgrupacionInstrumental: profile.tipoAgrupacionInstrumental || null,
            anoFundacion: profile.anoFundacion || '',
            localizacion: profile.localizacion || { calle: '', numero: '', ciudad: '', provincia: '', codigoPostal: '', pais: 'España' },
            lat: profile.lat || null,
            lng: profile.lng || null,
            cif: profile.cif || '',
            personaResponsable: profile.personaResponsable || { nombre: '', apellidos: '', dni: '', telefono: '', email: '' },
            kmDispuestos: profile.kmDispuestos || '',
            ensayos: {
              publico: profile.ensayos?.length > 0 && profile.ensayos[0]?.publico || false,
              horarios: (profile.ensayos || []).map(e => ({
                id: e.id,
                diaSemana: e.diaSemana,
                horaInicio: e.horaInicio,
                horaFin: e.horaFin,
                ubicacion: e.ubicacion || '',
                notas: e.notas || '',
                publico: e.publico || false,
              })),
            },
            repertorio: (profile.repertorio || []).map(r => ({
              id: r.id,
              marchaId: r.marchaId || r.marcha?.id,
              nombre: r.marcha?.nombre || '',
              autor: r.marcha?.autor || '',
              tipo: r.marcha?.tipo || '',
              fromDB: true,
            })),
          };
          setInitialProfileData(initialData);
          isProfileLoadedRef.current = true;

          // Initialize slug and micrositeConfig
          if (profile.slug) {
            setCurrentSlug(profile.slug);
            setSlugInput(profile.slug);
          }
          if (profile.micrositeConfig) {
            const mc = profile.micrositeConfig;
            setMsConfig((prev) => {
              const base = {
                ...prev,
                ...mc,
                seccionesOcultas: Array.isArray(mc.seccionesOcultas) ? mc.seccionesOcultas : [],
                coloresPorTema:
                  mc.coloresPorTema && typeof mc.coloresPorTema === 'object' && !Array.isArray(mc.coloresPorTema)
                    ? mc.coloresPorTema
                    : {},
                animacionesMicrositio: normalizeAnimacionesMicrositio(mc.animacionesMicrositio),
                cabeceraHero: normalizeCabeceraHero(mc.cabeceraHero),
                mostrarPlantillaMusicosNosotros: readMostrarPlantillaMusicosNosotros(mc),
                plantillaMusicosNosotrosInstrumentosModo: readPlantillaMusicosInstrumentosModo(mc),
                plantillaMusicosNosotrosInstrumentoPorMusico: readPlantillaMusicosInstrumentoPorMusico(mc),
                mostrarEspecialidadesNosotros: readMostrarEspecialidadesNosotros(mc),
                contactoMostrar: normalizeContactoMostrar(mc.contactoMostrar),
              };
              if (Array.isArray(mc.directiva)) {
                base.directiva = mc.directiva.map((d) => ({
                  id:
                    typeof d.id === 'string' && d.id.trim()
                      ? d.id.trim()
                      : typeof crypto !== 'undefined' && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `dir-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                  nombre: d.nombre || '',
                  cargo: d.cargo || '',
                  foto: d.foto || '',
                }));
              }
              if (Array.isArray(mc.noticias)) {
                base.noticias = mc.noticias.map((n) => ({
                  id:
                    typeof n.id === 'string' && n.id.trim()
                      ? n.id.trim()
                      : typeof crypto !== 'undefined' && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `not-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                  activo: n.activo === true,
                  texto: n.texto || '',
                  imagen: n.imagen || '',
                  createdAt: typeof n.createdAt === 'string' && n.createdAt ? n.createdAt : new Date().toISOString(),
                }));
              }
              if (Array.isArray(mc.patrocinadores)) {
                base.patrocinadores = mc.patrocinadores.map((pat) => ({
                  id:
                    typeof pat.id === 'string' && pat.id.trim()
                      ? pat.id.trim()
                      : typeof crypto !== 'undefined' && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `pat-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                  nombre: pat.nombre || '',
                  logo: pat.logo || '',
                  url: pat.url || '',
                }));
              }
              base.seccionesOrden = getMicrositeBodySectionOrder(base);
              return base;
            });
          }

          const status = profile.verificationStatus?.toLowerCase() || 'not_requested';
          setVerificationStatus(status);

          // Si está pendiente, cargar el código de verificación
          if (status === 'pending') {
            try {
              const verRes = await bandasAPI.getVerificationStatus();
              if (verRes.data?.lastRequest?.verificationCode) {
                setVerificationCode(verRes.data.lastRequest.verificationCode);
              }
            } catch (verError) {
              console.error('Error cargando código de verificación:', verError);
            }
          }
        }
      } catch (error) {
        console.error('Error cargando perfil:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [profile]);

  // Catálogo se busca on-demand via API (fuzzy search), no se carga todo upfront

  // Sugerencias de especialidades para bandas
  const especialidadesSugeridas = [
    'Marchas procesionales',
    'Música sacra',
    'Himnos religiosos',
    'Marchas fúnebres',
    'Pasodobles',
    'Música clásica',
    'Música popular',
    'Música cofrade',
    'Marchas moras y cristianas',
    'Villancicos',
    'Música tradicional',
    'Marchas militares',
    'Música de cámara',
    'Jazz',
    'Música moderna',
    'Sevillanas',
    'Flamenco',
    'Música folk',
    'Música litúrgica',
    'Te Deum',
    'Salves',
    'Marchas de entrada',
    'Marchas de Salida',
    'Música de Concierto'
  ];
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const reviewCardBg = useColorModeValue('gray.50', 'gray.700');
  const reviewCommentBg = useColorModeValue('white', 'gray.600');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  // Color tokens para evitar llamadas inline a useColorModeValue en JSX/maps
  const checklistBg = useColorModeValue('blue.50', 'blue.900');
  const checklistBorderColor = useColorModeValue('blue.200', 'blue.700');
  const checklistTitleColor = useColorModeValue('blue.700', 'blue.200');
  const checklistSubtitleColor = useColorModeValue('blue.600', 'blue.300');
  const checkDoneColor = useColorModeValue('gray.600', 'gray.300');
  const checkTodoColor = useColorModeValue('gray.700', 'gray.200');
  const borderColorAlt = useColorModeValue('gray.200', 'gray.600');
  const subtleBorderColor = useColorModeValue('gray.100', 'gray.700');
  const subtleBorderColorB = useColorModeValue('gray.100', 'gray.600');
  const purpleHoverBg = useColorModeValue('purple.50', 'gray.600');
  const purpleHoverBgAlt = useColorModeValue('purple.50', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const scrollbarThumbBg = useColorModeValue('gray.400', 'gray.500');
  const scrollbarThumbHoverBg = useColorModeValue('gray.500', 'gray.400');
  const selectedBg = useColorModeValue('green.50', 'green.900');

  // Detectar cambios sin guardar
  useEffect(() => {
    if (!initialProfileData) return;
    
    const fieldsToCompare = [
      'nombre', 'bio', 'nMusicos', 'avatar', 'fondoHeaderImagen', 'imagenCabeceraWeb',
      'anoFundacion', 'cif', 'kmDispuestos',
    ];
    
    let hasChanges = false;
    
    // Comparar campos simples
    for (const field of fieldsToCompare) {
      if (profileData[field] !== initialProfileData[field]) {
        hasChanges = true;
        break;
      }
    }
    
    // Comparar arrays y objetos (fotos, videos, ensayos, repertorio, etc.)
    if (!hasChanges) {
      hasChanges = JSON.stringify(profileData.fotos) !== JSON.stringify(initialProfileData.fotos) ||
                   JSON.stringify(profileData.videos) !== JSON.stringify(initialProfileData.videos) ||
                   JSON.stringify(profileData.contactoPublico) !== JSON.stringify(initialProfileData.contactoPublico) ||
                   JSON.stringify(profileData.redesSociales) !== JSON.stringify(initialProfileData.redesSociales) ||
                   JSON.stringify(profileData.especialidades) !== JSON.stringify(initialProfileData.especialidades) ||
                   JSON.stringify(profileData.localizacion) !== JSON.stringify(initialProfileData.localizacion) ||
                   JSON.stringify(profileData.personaResponsable) !== JSON.stringify(initialProfileData.personaResponsable) ||
                   JSON.stringify(profileData.ensayos) !== JSON.stringify(initialProfileData.ensayos) ||
                   JSON.stringify(profileData.repertorio) !== JSON.stringify(initialProfileData.repertorio);
    }

    setHasUnsavedChanges(hasChanges);
  }, [profileData, initialProfileData]);

  // Prevenir cierre de pestaña/navegador con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Necesario para algunos navegadores
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    FONT_OPTIONS.forEach(f => {
      if (!f.url) return;
      const id = `gfont-preview-${f.id}`;
      if (document.getElementById(id)) return;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = f.url;
      document.head.appendChild(link);
    });
  }, []);

  // Función para manejar navegación con cambios sin guardar
  const handleNavigationAttempt = (path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setIsUnsavedChangesModalOpen(true);
    } else {
      navigate(path);
    }
  };

  // Confirmar navegación descartando cambios
  const confirmNavigation = () => {
    setIsUnsavedChangesModalOpen(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Cancelar navegación
  const cancelNavigation = () => {
    setIsUnsavedChangesModalOpen(false);
    setPendingNavigation(null);
  };

  // ========== HANDLERS DE AVALES ==========
  
  // Cargar avales al montar el componente
  useEffect(() => {
    const loadAvales = async () => {
      try {
        if (isColaboradorBanda) {
          const recibidosRes = await avalesAPI.getRecibidos();
          setAvalesRecibidos(recibidosRes.data || []);
          setAvalesPendientes([]);
          setSolicitudesAval([]);
          return;
        }
        const [recibidosRes, pendientesRes, solicitudesRes] = await Promise.all([
          avalesAPI.getRecibidos(),
          avalesAPI.getPendientes(),
          avalesAPI.getSolicitudesEnviadas(),
        ]);
        setAvalesRecibidos(recibidosRes.data || []);
        setAvalesPendientes(pendientesRes.data || []);
        setSolicitudesAval(solicitudesRes.data || []);
      } catch (error) {
        console.error('Error cargando avales:', error);
      }
    };
    
    if (profile) {
      loadAvales();
    }
  }, [profile, isColaboradorBanda]);

  // Solicitar aval por email
  const handleRequestAval = async () => {
    if (isColaboradorBanda) {
      toaster.create({ title: 'No disponible en modo colaborador', type: 'info', duration: 3000 });
      return;
    }
    if (!avalRequestEmail) return;
    
    setIsRequestingAval(true);
    try {
      await avalesAPI.solicitar({ email: avalRequestEmail });
      toaster.create({
        title: 'Solicitud enviada',
        description: `Se ha enviado un email a ${avalRequestEmail} solicitando el aval`,
        type: 'success',
        duration: 5000,
      });
      setAvalRequestEmail('');
      // Recargar todas las listas de avales
      const [recibidosRes, pendientesRes, solicitudesRes] = await Promise.all([
        avalesAPI.getRecibidos(),
        avalesAPI.getPendientes(),
        avalesAPI.getSolicitudesEnviadas(),
      ]);
      setAvalesRecibidos(recibidosRes.data || []);
      setAvalesPendientes(pendientesRes.data || []);
      setSolicitudesAval(solicitudesRes.data || []);
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo enviar la solicitud',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsRequestingAval(false);
    }
  };

  // Aceptar aval pendiente
  const handleAcceptAval = async (avalId) => {
    if (isColaboradorBanda) {
      toaster.create({ title: 'No disponible en modo colaborador', type: 'info', duration: 3000 });
      return;
    }
    setIsProcessingAval(avalId);
    try {
      await avalesAPI.aceptar(avalId);
      toaster.create({
        title: 'Aval aceptado',
        description: 'El aval ha sido añadido a tu perfil',
        type: 'success',
        duration: 3000,
      });
      // Actualizar estado local inmediatamente para mejor UX
      setAvalesPendientes(prev => prev.filter(a => a.id !== avalId));
      // Recargar avales en background
      try {
        const [recibidosRes, pendientesRes] = await Promise.all([
          avalesAPI.getRecibidos(),
          avalesAPI.getPendientes(),
        ]);
        setAvalesRecibidos(recibidosRes.data || []);
        setAvalesPendientes(pendientesRes.data || []);
      } catch (reloadError) {
        console.error('Error recargando avales:', reloadError);
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo aceptar el aval',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsProcessingAval(null);
    }
  };

  // Rechazar aval pendiente
  const handleRejectAval = async (avalId) => {
    if (isColaboradorBanda) {
      toaster.create({ title: 'No disponible en modo colaborador', type: 'info', duration: 3000 });
      return;
    }
    setIsProcessingAval(avalId);
    try {
      await avalesAPI.rechazar(avalId);
      toaster.create({
        title: 'Aval rechazado',
        description: 'El aval ha sido rechazado',
        type: 'info',
        duration: 3000,
      });
      // Actualizar estado local inmediatamente para mejor UX
      setAvalesPendientes(prev => prev.filter(a => a.id !== avalId));
      // Recargar pendientes en background
      try {
        const pendientesRes = await avalesAPI.getPendientes();
        setAvalesPendientes(pendientesRes.data || []);
      } catch (reloadError) {
        console.error('Error recargando pendientes:', reloadError);
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo rechazar el aval',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsProcessingAval(null);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNestedInputChange = (parent, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  // Funciones para manejar el repertorio
  const handleRepertorioInputChange = (value) => {
    setRepertorioInput(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await catalogoAPI.searchMarchas(value.trim());
          const results = res.data || [];
          setMarchasFiltradas(results);
          setShowSuggestions(results.length > 0);
        } catch {
          setMarchasFiltradas([]);
          setShowSuggestions(false);
        }
      }, 300);
    } else {
      setMarchasFiltradas([]);
      setShowSuggestions(false);
    }
  };

  const handleAddMarchaFromDB = (marcha) => {
    const yaExiste = profileData.repertorio.some(
      item => item.marchaId === marcha.id || item.nombre.toLowerCase() === marcha.nombre.toLowerCase()
    );

    if (!yaExiste) {
      setProfileData(prev => ({
        ...prev,
        repertorio: [...prev.repertorio, {
          id: Date.now(),
          marchaId: marcha.id,
          nombre: marcha.nombre,
          autor: marcha.autor,
          tipo: marcha.tipo,
          fromDB: true
        }]
      }));
    }

    setRepertorioInput('');
    setMarchasFiltradas([]);
    setShowSuggestions(false);
  };

  const handleAddMarchaManual = () => {
    if (repertorioInput.trim().length > 0) {
      // Abrir modal con el nombre prellenado
      setNuevaMarcha({
        nombre: repertorioInput.trim(),
        autor: '',
        autorPersonalizado: '',
        tipo: ''
      });
      setShowSuggestions(false);
      onAddMarchaModalOpen();
    }
  };

  const handleAutorInputChange = (value) => {
    setNuevaMarcha(prev => ({ ...prev, autor: value, autorPersonalizado: value }));

    if (autorSearchTimeoutRef.current) clearTimeout(autorSearchTimeoutRef.current);

    if (value.trim().length >= 2) {
      autorSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await catalogoAPI.searchAutores(value.trim());
          const results = res.data || [];
          setAutoresFiltrados([...results, 'Desconocido']);
          setShowAutorSuggestions(true);
        } catch {
          setAutoresFiltrados(['Desconocido']);
          setShowAutorSuggestions(false);
        }
      }, 300);
    } else {
      setAutoresFiltrados([]);
      setShowAutorSuggestions(false);
    }
  };

  const handleSelectAutor = (autor) => {
    setNuevaMarcha(prev => ({ ...prev, autor, autorPersonalizado: autor }));
    setShowAutorSuggestions(false);
  };

  const handleConfirmAddMarcha = () => {
    const yaExiste = profileData.repertorio.some(
      item => item.nombre.toLowerCase() === nuevaMarcha.nombre.toLowerCase()
    );

    if (!yaExiste && nuevaMarcha.nombre.trim() && nuevaMarcha.tipo) {
      setProfileData(prev => ({
        ...prev,
        repertorio: [...prev.repertorio, {
          id: Date.now(),
          nombre: nuevaMarcha.nombre.trim(),
          autor: nuevaMarcha.autorPersonalizado.trim() || 'Desconocido',
          tipo: nuevaMarcha.tipo,
          fromDB: false
        }]
      }));

      setRepertorioInput('');
      setMarchasFiltradas([]);
      setNuevaMarcha({ nombre: '', autor: '', autorPersonalizado: '', tipo: '' });
      onAddMarchaModalClose();
    }
  };

  const handleCancelAddMarcha = () => {
    setNuevaMarcha({ nombre: '', autor: '', autorPersonalizado: '', tipo: '' });
    setAutoresFiltrados([]);
    setShowAutorSuggestions(false);
    onAddMarchaModalClose();
  };

  const handleRemoveMarcha = (id) => {
    setProfileData(prev => ({
      ...prev,
      repertorio: prev.repertorio.filter(item => item.id !== id)
    }));
  };

  /** Añade obras del archivo de partituras al repertorio público (dedupe por nombre). */
  const mergeObrasIntoRepertorioFromArchivo = (obrasPartitura) => {
    const mapped = (obrasPartitura || []).map((o) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      nombre: (`${o?.titulo ?? ''}`.trim() || 'Sin título'),
      autor: (`${o?.autor ?? ''}`.trim() || 'Desconocido'),
      tipo: o?.genero?.nombre || 'Otro',
      fromDB: false,
    }));
    let added = 0;
    setProfileData((prev) => {
      const existing = new Set(prev.repertorio.map((r) => (r.nombre || '').toLowerCase()));
      const toAdd = mapped.filter((m) => !existing.has((m.nombre || '').toLowerCase()));
      added = toAdd.length;
      if (toAdd.length === 0) return prev;
      return { ...prev, repertorio: [...prev.repertorio, ...toAdd] };
    });
    if (added === 0) {
      toaster.create({
        title: 'Sin novedades',
        description: 'Todas esas obras ya estaban en el repertorio (mismo nombre).',
        type: 'info',
        duration: 4000,
      });
      return;
    }
    toaster.create({
      title: 'Obras añadidas',
      description: `Se añadieron ${added} obra(s). Recuerda guardar el perfil si hay otros cambios pendientes.`,
      type: 'success',
      duration: 4000,
    });
  };

  const obrasFromCarpetaPartituras = (carpeta) =>
    [...(carpeta?.obras || [])]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((x) => x.obra)
      .filter(Boolean);

  const handleOpenImportArchivoModal = async () => {
    const ok = await loadArchivoMusical();
    if (!ok) return;
    setImportArchivoTab(0);
    setImportArchivoFiltro('');
    setImportCarpetasSeleccion([]);
    setImportObrasSeleccion([]);
    onImportArchivoModalOpen();
  };

  const handleImportArchivoTodo = () => {
    mergeObrasIntoRepertorioFromArchivo(archivoObrasList);
    onImportArchivoModalClose();
  };

  const handleImportArchivoCarpetas = () => {
    const obras = [];
    const seen = new Set();
    for (const cId of importCarpetasSeleccion) {
      const carpeta = archivoCarpetasList.find((c) => c.id === cId);
      for (const obra of obrasFromCarpetaPartituras(carpeta)) {
        if (obra?.id && !seen.has(obra.id)) {
          seen.add(obra.id);
          obras.push(obra);
        }
      }
    }
    mergeObrasIntoRepertorioFromArchivo(obras);
    onImportArchivoModalClose();
  };

  const handleImportArchivoObras = () => {
    const sel = new Set(importObrasSeleccion);
    const obras = (archivoObrasList || []).filter((o) => sel.has(o.id));
    mergeObrasIntoRepertorioFromArchivo(obras);
    onImportArchivoModalClose();
  };

  const toggleImportCarpetaSeleccion = (carpetaId) => {
    setImportCarpetasSeleccion((prev) =>
      prev.includes(carpetaId) ? prev.filter((id) => id !== carpetaId) : [...prev, carpetaId],
    );
  };

  const toggleImportObraSeleccion = (obraId) => {
    setImportObrasSeleccion((prev) =>
      prev.includes(obraId) ? prev.filter((id) => id !== obraId) : [...prev, obraId],
    );
  };

  const handleSelectAllImportObras = () => {
    const elegibles = obrasImportArchivoFiltradas.filter(
      (o) => !profileData.repertorio.some(
        (r) => r.nombre.toLowerCase() === (o.titulo || '').trim().toLowerCase(),
      ),
    );
    const ids = elegibles.map((o) => o.id);
    const allSelected = ids.length > 0 && ids.every((id) => importObrasSeleccion.includes(id));
    if (allSelected) {
      setImportObrasSeleccion((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setImportObrasSeleccion((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const handleAddRepertorioManualTuWeb = () => {
    const n = repertorioManualNombre.trim();
    const t = repertorioManualTipo.trim();
    if (!n || !t) {
      toaster.create({ title: 'Datos incompletos', description: 'Indica el nombre de la obra y el tipo.', type: 'warning', duration: 4000 });
      return;
    }
    if (profileData.repertorio.some((item) => item.nombre.toLowerCase() === n.toLowerCase())) {
      toaster.create({ title: 'Duplicado', description: 'Ya existe una obra con ese nombre.', type: 'warning', duration: 4000 });
      return;
    }
    const autor = repertorioManualAutor.trim() || 'Desconocido';
    setProfileData((prev) => ({
      ...prev,
      repertorio: [...prev.repertorio, { id: Date.now(), nombre: n, autor, tipo: t, fromDB: false }],
    }));
    setRepertorioManualNombre('');
    setRepertorioManualAutor('');
    setRepertorioManualTipo('');
    toaster.create({ title: 'Obra añadida', type: 'success', duration: 2500 });
  };

  // Funciones para escaneo de PDF
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toaster.create({
        title: 'Error',
        description: 'Por favor selecciona un archivo PDF',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    setIsScanningPdf(true);
    try {
      const response = await bandasAPI.scanRepertorioPdf(file);
      const marchas = response.data?.marchas || [];
      
      if (marchas.length === 0) {
        toaster.create({
          title: 'Sin resultados',
          description: 'No se encontraron marchas en el PDF',
          type: 'warning',
          duration: 3000,
        });
        return;
      }

      setExtractedMarchas(marchas);
      setSelectedExtractedMarchas(marchas.map((_, index) => index)); // Select all by default
      onPdfScanModalOpen();
      
      toaster.create({
        title: 'PDF procesado',
        description: `Se encontraron ${marchas.length} marchas`,
        type: 'success',
        duration: 3000,
      });

      if (response.data?.aviso) {
        toaster.create({
          title: 'Límite diario de escaneos',
          description: response.data.aviso,
          type: 'warning',
          duration: 12000,
          closable: true,
        });
      }
    } catch (error) {
      console.error('Error scanning PDF:', error);
      toaster.create({
        title: 'Error procesando PDF',
        description: error.response?.data?.message || 'No se pudo procesar el archivo',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsScanningPdf(false);
      // Reset file input
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
      if (pdfTuWebRepertorioInputRef.current) {
        pdfTuWebRepertorioInputRef.current.value = '';
      }
    }
  };

  const handleToggleExtractedMarcha = (index) => {
    setSelectedExtractedMarchas(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectAllExtracted = () => {
    if (selectedExtractedMarchas.length === extractedMarchas.length) {
      setSelectedExtractedMarchas([]);
    } else {
      setSelectedExtractedMarchas(extractedMarchas.map((_, index) => index));
    }
  };

  const handleAddExtractedMarchas = () => {
    const marchasToAdd = selectedExtractedMarchas
      .map(index => extractedMarchas[index])
      .filter(marcha => {
        // Check if already exists in repertorio
        const exists = profileData.repertorio.some(
          item => item.nombre.toLowerCase() === marcha.nombre.toLowerCase()
        );
        return !exists;
      });

    if (marchasToAdd.length === 0) {
      toaster.create({
        title: 'Sin marchas nuevas',
        description: 'Todas las marchas seleccionadas ya están en tu repertorio',
        type: 'info',
        duration: 3000,
      });
      return;
    }

    setProfileData(prev => ({
      ...prev,
      repertorio: [
        ...prev.repertorio,
        ...marchasToAdd.map(marcha => ({
          id: Date.now() + Math.random(),
          nombre: marcha.nombre,
          autor: marcha.autor || 'Desconocido',
          tipo: marcha.tipo || 'Marcha procesional',
          fromDB: false
        }))
      ]
    }));

    toaster.create({
      title: 'Marchas añadidas',
      description: `Se añadieron ${marchasToAdd.length} marchas al repertorio`,
      type: 'success',
      duration: 3000,
    });

    // Close modal and reset state
    onPdfScanModalClose();
    setExtractedMarchas([]);
    setSelectedExtractedMarchas([]);
  };

  // Funciones para gestionar ensayos
  const handleToggleEnsayosPublicos = () => {
    const nuevoEstadoPublico = !profileData.ensayos.publico;
    
    setProfileData(prev => ({
      ...prev,
      ensayos: {
        publico: nuevoEstadoPublico,
        // Actualizar también el campo publico de todos los ensayos individuales
        horarios: prev.ensayos.horarios.map(ensayo => ({
          ...ensayo,
          publico: nuevoEstadoPublico
        }))
      }
    }));
  };

  const handleAddEnsayo = () => {
    const nuevoEnsayo = {
      id: `temp-${Date.now()}`,
      diaSemana: '',
      horaInicio: '',
      horaFin: '',
      ubicacion: '',
      notas: '',
      publico: profileData.ensayos?.publico || false // Tomar el valor del toggle global
    };
    setEnsayoEditando(nuevoEnsayo);
    onEnsayoModalOpen();
  };
  
  const handleEditEnsayo = (ensayo) => {
    setEnsayoEditando({ ...ensayo });
    onEnsayoModalOpen();
  };

  const handleSaveEnsayo = () => {
    if (!ensayoEditando.diaSemana || !ensayoEditando.horaInicio || !ensayoEditando.horaFin) {
      return; // Validación básica
    }
    
    // Asegurarse de que el ensayo tenga el campo publico correcto según el toggle global
    const ensayoConPublico = {
      ...ensayoEditando,
      publico: profileData.ensayos?.publico || false
    };
    
    setProfileData(prev => {
      const existingIndex = prev.ensayos.horarios.findIndex(e => e.id === ensayoEditando.id);
      
      if (existingIndex >= 0) {
        // Actualizar ensayo existente
        return {
          ...prev,
          ensayos: {
            ...prev.ensayos,
            horarios: prev.ensayos.horarios.map(e => 
              e.id === ensayoEditando.id ? ensayoConPublico : e
            )
          }
        };
      } else {
        // Añadir nuevo ensayo
        return {
          ...prev,
          ensayos: {
            ...prev.ensayos,
            horarios: [...prev.ensayos.horarios, ensayoConPublico]
          }
        };
      }
    });
    
    setEnsayoEditando(null);
    onEnsayoModalClose();
  };

  const handleRemoveEnsayo = (id) => {
    setProfileData(prev => ({
      ...prev,
      ensayos: {
        ...prev.ensayos,
        horarios: prev.ensayos.horarios.filter(ensayo => ensayo.id !== id)
      }
    }));
  };

  // Función para descargar el repertorio en PDF
  const handleDownloadPDF = () => {
    // Crear el contenido HTML para el PDF
    const printWindow = window.open('', '_blank');
    const bandaName = profileData.nombre || 'Mi Banda';
    const logoSrc = profileData.avatar || '';
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Repertorio de ${bandaName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            padding: 40px;
            position: relative;
            min-height: 100vh;
          }
          
          /* Marca de agua */
          .watermark {
            position: fixed;
            bottom: 20px;
            right: 20px;
            opacity: 0.15;
            font-size: 48px;
            font-weight: bold;
            color: #805AD5;
            transform: rotate(-45deg);
            pointer-events: none;
            z-index: -1;
          }
          
          /* Header */
          .header {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #805AD5;
          }
          
          .logo {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #805AD5;
          }
          
          .logo-placeholder {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, #805AD5 0%, #B794F4 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 36px;
            color: white;
            font-weight: bold;
            border: 3px solid #805AD5;
          }
          
          .header-text h1 {
            font-size: 28px;
            color: #2D3748;
            margin-bottom: 5px;
          }
          
          .header-text p {
            font-size: 14px;
            color: #718096;
          }
          
          /* Tabla */
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          
          thead {
            background: #805AD5;
            color: white;
          }
          
          th {
            padding: 12px;
            text-align: left;
            font-weight: 600;
            font-size: 14px;
          }
          
          th:first-child {
            width: 50px;
            text-align: center;
          }
          
          tbody tr {
            border-bottom: 1px solid #E2E8F0;
          }
          
          tbody tr:nth-child(even) {
            background: #F7FAFC;
          }
          
          tbody tr:hover {
            background: #EDF2F7;
          }
          
          td {
            padding: 12px;
            font-size: 13px;
            color: #2D3748;
          }
          
          td:first-child {
            text-align: center;
            color: #718096;
            font-weight: 500;
          }
          
          .marcha-nombre {
            font-weight: 600;
            color: #2D3748;
            margin-bottom: 2px;
          }
          
          .marcha-autor {
            font-size: 12px;
            color: #718096;
          }
          
          .badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 10px;
            font-weight: 600;
            margin-left: 8px;
          }
          
          .badge-verified {
            background: #D6BCFA;
            color: #553C9A;
          }
          
          .badge-tipo {
            background: #E2E8F0;
            color: #4A5568;
          }
          
          /* Footer */
          .footer {
            position: fixed;
            bottom: 20px;
            left: 40px;
            right: 40px;
            text-align: center;
            color: #A0AEC0;
            font-size: 11px;
            padding-top: 15px;
            border-top: 1px solid #E2E8F0;
          }
          
          .footer-logo {
            font-weight: bold;
            color: #805AD5;
            font-size: 12px;
          }
          
          @media print {
            .watermark {
              opacity: 0.08;
            }
            
            body {
              padding: 20px;
            }
            
            @page {
              margin: 1cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="watermark">CADENCYA</div>
        
        <div class="header">
          ${logoSrc 
            ? `<img src="${logoSrc}" alt="Logo" class="logo" />` 
            : `<div class="logo-placeholder">${bandaName.charAt(0).toUpperCase()}</div>`
          }
          <div class="header-text">
            <h1>Repertorio de ${bandaName}</h1>
            <p>Generado el ${new Date().toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre de la obra</th>
              <th>Autor</th>
              <th>Tipo</th>
            </tr>
          </thead>
          <tbody>
            ${profileData.repertorio.map((marcha, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>
                  <div class="marcha-nombre">
                    ${marcha.nombre}
                    ${marcha.fromDB ? '<span class="badge badge-verified">Validado</span>' : ''}
                  </div>
                </td>
                <td class="marcha-autor">${marcha.autor || '-'}</td>
                <td>
                  ${marcha.tipo ? `<span class="badge badge-tipo">${marcha.tipo}</span>` : '-'}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <div class="footer-logo">CADENCYA</div>
          <div>Tu plataforma de gestión musical</div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Esperar a que se cargue el contenido antes de imprimir
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  // Abrir el cropper cuando se selecciona un archivo
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tamaño del archivo (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        showModal('Error', 'La imagen es demasiado grande. Máximo 5MB.', 'error');
        return;
      }

      // Leer el archivo y abrir el cropper
      const reader = new FileReader();
      reader.onload = (e) => {
        setCropperImage(e.target.result);
        setCropperType('avatar');
        setPendingFile(file);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo otra vez
    event.target.value = '';
  };

  // Procesar la imagen recortada del avatar
  const handleAvatarCropComplete = async (croppedImageUrl) => {
    setIsUploading(true);
    try {
      // Convertir la URL del blob a un archivo
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      
      const result = await uploadAvatar(file);
      if (result.success) {
        setProfileData(prev => ({ ...prev, avatar: result.url }));
        trackAvatarUpload('BANDA');
      } else {
        // Fallback a la URL del blob
        setProfileData(prev => ({ ...prev, avatar: croppedImageUrl }));
      }
    } catch (error) {
      // Fallback a la URL del blob
      setProfileData(prev => ({ ...prev, avatar: croppedImageUrl }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFondoHeaderUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tamaño del archivo (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        showModal('Error', 'La imagen es demasiado grande. Máximo 5MB.', 'error');
        return;
      }

      // Leer el archivo y abrir el cropper
      const reader = new FileReader();
      reader.onload = (e) => {
        setCropperImage(e.target.result);
        setCropperType('fondo');
        setPendingFile(file);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo otra vez
    event.target.value = '';
  };

  // Procesar la imagen recortada del fondo
  const handleFondoCropComplete = async (croppedImageUrl) => {
    setIsUploading(true);
    try {
      // Convertir la URL del blob a un archivo
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'fondo.jpg', { type: 'image/jpeg' });
      
      const apiResponse = await bandasAPI.uploadFondo(file);
      setProfileData(prev => ({ ...prev, fondoHeaderImagen: apiResponse.data.url }));
    } catch (error) {
      // Fallback a la URL del blob
      setProfileData(prev => ({ ...prev, fondoHeaderImagen: croppedImageUrl }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleImagenCabeceraWebUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showModal('Error', 'La imagen es demasiado grande. Máximo 5MB.', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setCropperImage(e.target.result);
        setCropperType('imagenCabeceraWeb');
        setPendingFile(file);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleImagenCabeceraWebCropComplete = async (croppedImageUrl) => {
    setIsUploading(true);
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'cabecera-web.jpg', { type: 'image/jpeg' });
      const apiResponse = await bandasAPI.uploadImagenCabeceraWeb(file);
      setProfileData((prev) => ({ ...prev, imagenCabeceraWeb: apiResponse.data.url }));
      if (isBandaAccountUserType(user?.userType)) await loadProfile('BANDA');
      toaster.create({ title: 'Imagen de cabecera actualizada', type: 'success', duration: 2000 });
    } catch (error) {
      setProfileData((prev) => ({ ...prev, imagenCabeceraWeb: croppedImageUrl }));
      toaster.create({ title: 'Error', description: 'No se pudo subir la imagen de cabecera', type: 'error', duration: 4000 });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImagenCabeceraWeb = async () => {
    setIsUploading(true);
    try {
      await bandasAPI.deleteImagenCabeceraWeb();
      setProfileData((prev) => ({ ...prev, imagenCabeceraWeb: null }));
      if (isBandaAccountUserType(user?.userType)) await loadProfile('BANDA');
      toaster.create({ title: 'Imagen de cabecera eliminada', type: 'success', duration: 2000 });
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo eliminar la imagen',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Manejar el resultado del cropper
  const handleCropComplete = (croppedImageUrl) => {
    if (cropperType === 'avatar') {
      handleAvatarCropComplete(croppedImageUrl);
    } else if (cropperType === 'fondo') {
      handleFondoCropComplete(croppedImageUrl);
    } else if (cropperType === 'imagenCabeceraWeb') {
      handleImagenCabeceraWebCropComplete(croppedImageUrl);
    }
    setCropperImage(null);
    setCropperType(null);
    setPendingFile(null);
  };

  // Editar la imagen actual del avatar (sin subir una nueva)
  const handleEditCurrentAvatar = () => {
    if (profileData.avatar) {
      setCropperImage(profileData.avatar);
      setCropperType('avatar');
      setIsCropperOpen(true);
    }
  };

  // Editar la imagen actual del fondo (sin subir una nueva)
  const handleEditCurrentFondo = () => {
    if (profileData.fondoHeaderImagen) {
      setCropperImage(profileData.fondoHeaderImagen);
      setCropperType('fondo');
      setIsCropperOpen(true);
    }
  };

  const handleEditCurrentImagenCabeceraWeb = () => {
    if (profileData.imagenCabeceraWeb) {
      setCropperImage(profileData.imagenCabeceraWeb);
      setCropperType('imagenCabeceraWeb');
      setIsCropperOpen(true);
    }
  };

  // Funciones para drag and drop de galería
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const MAX_GALLERY_PHOTOS = 20;

  const validateFiles = (files) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = [];
    const errors = [];

    // Comprobar límite de galería
    const currentCount = profileData.fotos?.length || 0;
    const remaining = MAX_GALLERY_PHOTOS - currentCount;

    if (remaining <= 0) {
      errors.push(`Has alcanzado el límite de ${MAX_GALLERY_PHOTOS} fotos. Elimina alguna para subir más.`);
      return { validFiles, errors };
    }

    const filesToCheck = Array.from(files).slice(0, remaining);

    if (filesToCheck.length < files.length) {
      errors.push(`Solo se pueden subir ${remaining} foto(s) más (límite: ${MAX_GALLERY_PHOTOS}). Se ignorarán las restantes.`);
    }

    filesToCheck.forEach((file, index) => {
      if (!validTypes.includes(file.type)) {
        errors.push(`Archivo ${index + 1}: Solo se permiten imágenes JPG, PNG o WebP`);
      } else if (file.size > maxSize) {
        errors.push(`Archivo ${index + 1}: El tamaño máximo es 5MB`);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, errors };
  };

  const processFiles = async (files) => {
    setIsUploading(true);
    const { validFiles, errors } = validateFiles(files);

    // Si solo hay errores de límite pero sí hay archivos válidos, continuar con warning
    if (validFiles.length === 0) {
      if (errors.length > 0) {
        showModal(
          'Archivos No Válidos',
          errors.join('\n'),
          'error'
        );
      }
      setIsUploading(false);
      return;
    }

    try {
      // Subir fotos al servidor
      const response = await bandasAPI.uploadPhotos(validFiles);
      const uploadedUrls = response.data.urls || [];
      
      // Añadir las URLs al estado
      setProfileData(prev => ({
        ...prev,
        fotos: [...prev.fotos, ...uploadedUrls]
      }));

      const currentTotal = (profileData.fotos?.length || 0) + uploadedUrls.length;
      let message = `Se han subido ${uploadedUrls.length} foto(s) a tu galería.`;
      if (response.data.warning) {
        message += `\n${response.data.warning}`;
      }
      message += `\n(${currentTotal}/${MAX_GALLERY_PHOTOS} fotos)`;

      showModal(
        'Fotos Subidas',
        message,
        'success'
      );
      trackPhotoUpload('BANDA', uploadedUrls.length);
    } catch (error) {
      console.error('Error subiendo fotos:', error);
      const errorMsg = error.response?.data?.message || 'Hubo un problema al subir las imágenes. Inténtalo de nuevo.';
      showModal(
        'Error al Subir',
        errorMsg,
        'error'
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Función para extraer el ID de un video de YouTube (incluyendo Shorts)
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Detecta si la URL es un Short de YouTube
  const isYouTubeShort = (url) =>
    !!(url && /youtube\.com\/shorts\//.test(url));

  // Función para añadir video de YouTube
  const handleAddVideo = () => {
    if (!newVideoTitle.trim()) {
      showModal('Error', 'Por favor, introduce un título para el video.', 'error');
      return;
    }
    
    const videoId = extractYouTubeId(newVideoUrl);
    if (!videoId) {
      showModal('Error', 'Por favor, introduce una URL válida de YouTube.', 'error');
      return;
    }
    
    const newVideo = {
      id: Date.now(),
      titulo: newVideoTitle.trim(),
      url: newVideoUrl.trim()
    };
    
    setProfileData(prev => ({
      ...prev,
      videos: [...(prev.videos || []), newVideo]
    }));
    
    setNewVideoTitle('');
    setNewVideoUrl('');
    
    showModal('Video Añadido', 'El video se ha añadido correctamente.', 'success');
  };

  // Función para eliminar video
  const handleRemoveVideo = (videoId) => {
    setProfileData(prev => ({
      ...prev,
      videos: prev.videos.filter(v => v.id !== videoId)
    }));
  };

  // Función para eliminar todas las fotos
  const handleDeleteAllPhotos = async () => {
    setIsDeletingPhotos(true);
    try {
      const fotosToDelete = [...profileData.fotos];
      for (const photoUrl of fotosToDelete) {
        try {
          await bandasAPI.deletePhoto(photoUrl);
        } catch (error) {
          console.error('Error eliminando foto:', error);
        }
      }
      // Actualizar el estado local
      setProfileData(prev => ({ ...prev, fotos: [] }));
      setInitialProfileData(prev => ({ ...prev, fotos: [] }));
      onDeleteAllPhotosModalClose();
      toaster.create({
        title: 'Fotos eliminadas',
        description: 'Todas las fotos han sido eliminadas correctamente',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error eliminando fotos:', error);
      toaster.create({
        title: 'Error',
        description: 'Hubo un problema al eliminar las fotos',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeletingPhotos(false);
    }
  };

  // Función para desactivar cuenta
  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      toaster.create({
        title: 'Error',
        description: 'Debes introducir tu contraseña para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    setIsDeactivating(true);
    try {
      await authAPI.deactivateAccount(deactivatePassword);
      onDeactivateModalClose();
      toaster.create({
        title: 'Cuenta desactivada',
        description: 'Tu cuenta ha sido desactivada. Serás redirigido al inicio.',
        type: 'info',
        duration: 3000,
      });
      // Cerrar sesión y redirigir
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error desactivando cuenta:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo desactivar la cuenta',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeactivating(false);
      setDeactivatePassword('');
    }
  };

  // Función para exportar datos (GDPR)
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await authAPI.exportUserData();
      const data = response.data;
      
      // Crear archivo JSON para descargar
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis-datos-cadencya-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toaster.create({
        title: 'Datos exportados',
        description: 'Tus datos se han descargado correctamente.',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error exportando datos:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudieron exportar los datos',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Función para eliminar cuenta permanentemente (GDPR)
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toaster.create({
        title: 'Error',
        description: 'Debes introducir tu contraseña para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    if (deleteConfirmText !== 'ELIMINAR') {
      toaster.create({
        title: 'Error',
        description: 'Debes escribir ELIMINAR para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      await authAPI.deleteAccountPermanently(deletePassword);
      onDeleteModalClose();
      
      // Cerrar sesión inmediatamente
      logout();
      
      toaster.create({
        title: 'Cuenta eliminada',
        description: 'Tu cuenta y todos tus datos han sido eliminados permanentemente.',
        type: 'info',
        duration: 5000,
      });
      
      // Redirigir a la landing page inmediatamente
      navigate('/');
    } catch (error) {
      console.error('Error eliminando cuenta:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo eliminar la cuenta',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      setDeletePassword('');
      setDeleteConfirmText('');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, []);

  const handlePhotoUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const removePhoto = async (photoUrl) => {
    try {
      // Enviar la URL completa al backend
      await bandasAPI.deletePhoto(photoUrl);

      setProfileData(prev => ({
        ...prev,
        fotos: prev.fotos.filter(url => url !== photoUrl)
      }));

      showModal('Foto Eliminada', 'La foto se ha eliminado correctamente.', 'success');
    } catch (error) {
      console.error('Error eliminando foto:', error);
      // Aún así eliminamos del estado local
      setProfileData(prev => ({
        ...prev,
        fotos: prev.fotos.filter(url => url !== photoUrl)
      }));
    }
  };

  const calculateProfileCompletion = () => {
    const n = BANDA_PROFILE_COMPLETENESS_CHECKS.length;
    if (!n) return 100;
    const completed = BANDA_PROFILE_COMPLETENESS_CHECKS.filter((c) => c.test(profileData)).length;
    return Math.round((completed / n) * 100);
  };

  /** Obligatorios para guardar / validar (subconjunto de la barra total). */
  const isBandaMandatoryCompleteForValidate = () =>
    BANDA_MANDATORY_FOR_VALIDATE_CHECKS.every((c) => c.test(profileData));

  const getMissingBandaMandatoryForValidateLabels = () =>
    BANDA_MANDATORY_FOR_VALIDATE_CHECKS.filter((c) => !c.test(profileData)).map((c) => c.label);

  const missingMandatoryForValidateLabels = getMissingBandaMandatoryForValidateLabels();

  const profileCompletionPct = calculateProfileCompletion();
  const showCompletenessSection = profileCompletionPct < 100;
  /** Aviso/código de validación junto a la barra de completitud (aún no validada o pendiente con campos por completar). */
  const integrateValidationWithCompleteness =
    showCompletenessSection &&
    (verificationStatus === 'not_requested' || verificationStatus === 'pending');
  /** Tarjeta lateral de validación: solo si ya está validada, o casos sin barra de completitud. */
  const showValidationCardInSidebar =
    verificationStatus === 'verified' ||
    verificationStatus === 'rejected' ||
    ((verificationStatus === 'pending' || verificationStatus === 'not_requested') &&
      !showCompletenessSection);

  const bandaMandatoryChecks = useMemo(
    () => BANDA_PROFILE_COMPLETENESS_CHECKS.filter((c) => c.mandatoryForValidate),
    [],
  );
  const bandaOptionalPendingSlides = useMemo(() => {
    return BANDA_PROFILE_COMPLETENESS_CHECKS.filter(
      (c) => !c.mandatoryForValidate && !c.omitFromOptionalSlides && !c.test(profileData),
    ).map((c) => ({
      id: `opt-${c.label}`,
      title: 'Recomendado para tu perfil',
      icon: FaExclamationTriangle,
      content: (
        <Text fontSize="sm" color="orange.700">
          <Text as="span" fontWeight="600">
            {c.label}
          </Text>
          {c.privateField ? ' (dato privado)' : ''}
          {' — '}
          no es obligatorio para validar, pero mejora tu visibilidad y posicionamiento en Cadencya.
        </Text>
      ),
    }));
  }, [profileData]);

  const profileTipsSlides = useMemo(() => {
    const slides = [
      {
        id: 'ensembles',
        title: 'Recomendación',
        icon: FaLightbulb,
        content:
          'Si tu formación musical cuenta con algún ensemble de cámara o banda escuela, genera un perfil independiente para aumentar su visibilidad.',
      },
      {
        id: 'photos',
        title: 'Consejos para mejores fotos',
        icon: FaCamera,
        content: (
          <Text fontSize="sm" color="blue.600">
            • Sube fotos de tu banda en acción, instrumentos y uniformes
            <br />
            • Imágenes claras y bien iluminadas tienen mejor impacto
            <br />• Máximo 5MB por imagen para carga rápida
          </Text>
        ),
      },
    ];
    if (showCompletenessSection && verificationStatus !== 'pending') {
      slides.splice(1, 0, {
        id: 'completion',
        title: 'Consejo',
        icon: FaLightbulb,
        content:
          'Aún puedes completar información en tu perfil. Cuanto más datos añadas, más visible será tu banda y mejor posicionada en la comunidad.',
      });
    }
    return slides;
  }, [showCompletenessSection, verificationStatus]);

  const showModal = (title, message, type = 'success') => {
    setModalContent({ title, message, type });
    if (type === 'error') {
      onErrorModalOpen();
    } else {
      onSaveModalOpen();
    }
  };

  const handleRequestReviewClick = () => {
    // Validar email
    if (!reviewRequestEmail || !reviewRequestEmail.includes('@')) {
      showModal(
        'Email inválido',
        'Por favor, introduce un email válido de la cofradía.',
        'error'
      );
      return;
    }

    // Verificar si ya existe una solicitud pendiente para este email
    const existeSolicitud = profileData.solicitudesResenaPendientes.some(
      s => s.email === reviewRequestEmail && s.status === 'pending'
    );
    
    if (existeSolicitud) {
      showModal(
        'Solicitud ya enviada',
        `Ya existe una solicitud pendiente para ${reviewRequestEmail}.`,
        'error'
      );
      return;
    }

    // Abrir modal de confirmación
    onConfirmReviewRequestOpen();
  };

  const handleConfirmRequestReview = async () => {
    setIsRequestingReview(true);
    onConfirmReviewRequestClose();
    
    try {
      // TODO: Aquí irá la lógica de SendGrid para enviar el email
      // Por ahora simulamos un delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Añadir la solicitud a las solicitudes pendientes
      const nuevaSolicitud = {
        id: Date.now(),
        email: reviewRequestEmail,
        date: new Date().toISOString().split('T')[0],
        status: 'pending'
      };
      
      setProfileData(prev => ({
        ...prev,
        solicitudesResenaPendientes: [...prev.solicitudesResenaPendientes, nuevaSolicitud]
      }));
      
      showModal(
        'Solicitud enviada',
        `Se ha enviado una solicitud de reseña a ${reviewRequestEmail}. La cofradía recibirá un correo con instrucciones para dejar su reseña.`,
        'success'
      );
      
      setReviewRequestEmail('');
    } catch (error) {
      showModal(
        'Error al enviar',
        'No se pudo enviar la solicitud. Por favor, inténtalo de nuevo.',
        'error'
      );
    } finally {
      setIsRequestingReview(false);
    }
  };

  const handleDeleteReview = () => {
    if (!reviewToDelete) return;
    
    setProfileData(prev => ({
      ...prev,
      resenas: prev.resenas.filter(r => r.id !== reviewToDelete.id)
    }));
    
    onDeleteReviewModalClose();
    setReviewToDelete(null);
    
    showModal(
      'Reseña eliminada',
      'La reseña ha sido eliminada correctamente.',
      'success'
    );
  };

  const handleTogglePublishReview = async (reviewId) => {
    try {
      const review = profileData.resenas.find(r => r.id === reviewId);
      const newStatus = !review.isPublished;
      
      // Actualizar estado optimísticamente
      setProfileData(prev => ({
        ...prev,
        resenas: prev.resenas.map(r => 
          r.id === reviewId ? { ...r, isPublished: newStatus } : r
        )
      }));
      
      // Llamar al backend
      await resenasAPI.togglePublish(reviewId);
      
      showModal(
        newStatus ? 'Reseña publicada' : 'Reseña despublicada',
        newStatus 
          ? 'La reseña ahora es visible en tu perfil público.' 
          : 'La reseña ya no es visible en tu perfil público.',
        'success'
      );
    } catch (error) {
      console.error('Error al cambiar estado de publicación:', error);
      // Revertir el cambio en caso de error
      await loadResenas();
      showModal(
        'Error',
        'No se pudo cambiar el estado de la reseña. Inténtalo de nuevo.',
        'error'
      );
    }
  };

  const openDeleteReviewModal = (resena) => {
    setReviewToDelete(resena);
    onDeleteReviewModalOpen();
  };

  // Función para buscar ubicaciones en tiempo real
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    setIsSearchingLocation(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=es&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Error al buscar ubicación');
      }

      const data = await response.json();
      console.log('🔍 Resultados de búsqueda:', data);

      setLocationSuggestions(data);
      setShowLocationSuggestions(true);
    } catch (error) {
      console.error('❌ Error buscando ubicación:', error);
      setLocationSuggestions([]);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  // Función para seleccionar una ubicación de las sugerencias
  const selectLocation = (location) => {
    const parsed = parseNominatimAddress(location);
    if (parsed.lat == null || parsed.lng == null) {
      toaster.create({
        title: 'Ubicación sin coordenadas',
        description: 'Por favor, introduce una dirección más específica (calle, número, ciudad) para obtener las coordenadas exactas.',
        type: 'warning',
        duration: 5000,
        closable: true,
        position: 'top',
      });
      return;
    }

    setProfileData((prev) => ({
      ...prev,
      localizacion: parsed.localizacion,
      lat: parsed.lat,
      lng: parsed.lng,
    }));

    locationJustSelectedRef.current = true;
    setLocationSearchQuery(location.display_name || parsed.localizacion.calle);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationJustSelectedRef.current) {
        locationJustSelectedRef.current = false;
        return;
      }
      if (locationSearchQuery) {
        searchLocation(locationSearchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [locationSearchQuery]);

  const handleSave = async () => {
    if (missingMandatoryForValidateLabels.length > 0) {
      toaster.create({
        title: 'Datos obligatorios para validar pendientes',
        description: `No se puede guardar hasta completar: ${missingMandatoryForValidateLabels.join(', ')}.`,
        type: 'warning',
        duration: 10000,
        closable: true,
        position: 'top',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Preparar datos para la API
      const ensayosToSend = (profileData.ensayos?.horarios || [])
        .filter(e => e.diaSemana && e.horaInicio && e.horaFin) // Solo ensayos válidos con campos requeridos
        .map(e => ({
          // Solo incluir ID si es válido (no temporal)
          ...(e.id && !e.id.toString().startsWith('temp-') ? { id: e.id } : {}),
          diaSemana: e.diaSemana,
          horaInicio: e.horaInicio,
          horaFin: e.horaFin,
          ubicacion: e.ubicacion || '',
          notas: e.notas || '',
          publico: profileData.ensayos?.publico || false, // El toggle global se aplica a todos
        }));

      console.log('📅 Ensayos a enviar:', ensayosToSend);

      const dataToUpdate = {
        nombre: profileData.nombre,
        bio: profileData.bio,
        nMusicos: profileData.nMusicos ? parseInt(profileData.nMusicos) : null,
        kmDispuestos: profileData.kmDispuestos ? parseInt(profileData.kmDispuestos) : null,
        anoFundacion: profileData.anoFundacion ? parseInt(profileData.anoFundacion) : null,
        localizacion: profileData.localizacion,
        lat: profileData.lat != null && profileData.lat !== '' ? Number(profileData.lat) : null,
        lng: profileData.lng != null && profileData.lng !== '' ? Number(profileData.lng) : null,
        contactoPublico: profileData.contactoPublico,
        redesSociales: profileData.redesSociales,
        especialidades: profileData.especialidades,
        videos: (profileData.videos || []).map(v => ({
          ...(typeof v.id === 'string' && v.id.length > 10 ? { id: v.id } : {}), // Solo IDs válidos de la BD
          titulo: v.titulo,
          url: v.url,
        })),
        cif: profileData.cif,
        personaResponsable: profileData.personaResponsable,
        // Añadir ensayos y repertorio
        ensayos: ensayosToSend,
        repertorio: (profileData.repertorio || []).map(r => ({
          id: typeof r.id === 'string' && r.id.length > 10 ? r.id : undefined,
          marchaId: r.marchaId || undefined,
          nombre: r.nombre,
          autor: r.autor,
          tipo: r.tipo,
        })),
      };
      
      console.log('💾 [FRONTEND] Datos a enviar:', {
        lat: dataToUpdate.lat,
        lng: dataToUpdate.lng,
        localizacion: dataToUpdate.localizacion
      });
      
      const result = await updateProfile(dataToUpdate);
      
      if (result.success) {
        // Actualizar el perfil en el contexto con los datos guardados
        setProfileData((prev) => ({
          ...prev,
          tipoGrupo: result.profile.tipoGrupo ?? prev.tipoGrupo,
          tipoAgrupacionInstrumentalId: result.profile.tipoAgrupacionInstrumentalId ?? result.profile.tipoAgrupacionInstrumental?.id ?? prev.tipoAgrupacionInstrumentalId,
          tipoAgrupacionInstrumental: result.profile.tipoAgrupacionInstrumental ?? prev.tipoAgrupacionInstrumental,
        }));
        
        // Actualizar el estado inicial para que ya no haya "cambios sin guardar"
        setInitialProfileData({
          nombre: profileData.nombre,
          bio: profileData.bio,
          nMusicos: profileData.nMusicos,
          avatar: profileData.avatar,
          fondoHeaderImagen: profileData.fondoHeaderImagen,
          imagenCabeceraWeb: profileData.imagenCabeceraWeb,
          fotos: profileData.fotos,
          videos: profileData.videos,
          contactoPublico: profileData.contactoPublico,
          redesSociales: profileData.redesSociales,
          especialidades: profileData.especialidades,
          tipoGrupo: profileData.tipoGrupo,
          tipoAgrupacionInstrumentalId: profileData.tipoAgrupacionInstrumentalId,
          tipoAgrupacionInstrumental: profileData.tipoAgrupacionInstrumental,
          anoFundacion: profileData.anoFundacion,
          localizacion: profileData.localizacion,
          cif: profileData.cif,
          personaResponsable: profileData.personaResponsable,
          kmDispuestos: profileData.kmDispuestos,
          ensayos: profileData.ensayos,
          repertorio: profileData.repertorio,
        });
        setHasUnsavedChanges(false);
        
        // Auto-trigger verification cuando los datos obligatorios para validar están completos (no exige el 100% de la barra total)
        if (isBandaMandatoryCompleteForValidate() && verificationStatus === 'not_requested') {
          try {
            const verResponse = await bandasAPI.requestVerification({
              cif: profileData.cif,
              personaResponsable: {
                nombre: profileData.personaResponsable.nombre,
                apellidos: profileData.personaResponsable.apellidos,
                dni: profileData.personaResponsable.dni || '',
                telefono: profileData.personaResponsable.telefono,
                email: profileData.personaResponsable.email,
              },
            });
            setVerificationStatus('pending');
            if (verResponse.data?.verificationCode) {
              setVerificationCode(verResponse.data.verificationCode);
            }
            showModal(
              '¡Listo para validar!',
              'Has completado los datos obligatorios para validar y hemos iniciado automáticamente la validación. Revisa la pestaña de Perfil público para ver tu código de validación y las instrucciones para completar el proceso.',
              'success'
            );
          } catch (verError) {
            console.error('Error auto-requesting verification:', verError);
            // Still show save success even if verification fails
            showModal(
              '¡Perfil Actualizado!',
              'Tus cambios han sido guardados correctamente.',
              'success'
            );
          }
        } else {
          showModal(
            '¡Perfil Actualizado!',
            'Tus cambios han sido guardados correctamente. Tu información está ahora disponible en la plataforma.',
            'success'
          );
        }
        trackProfileUpdate('BANDA', Object.keys(dataToUpdate));
        trackProfileCompletion('BANDA', calculateProfileCompletion());
      } else {
        showModal(
          'Error al Guardar',
          result.error || 'No se pudieron guardar los cambios. Por favor, verifica tu conexión e inténtalo de nuevo.',
          'error'
        );
      }
      
    } catch (error) {
      console.error('Error al guardar el perfil:', error);
      showModal(
        'Error al Guardar',
        'No se pudieron guardar los cambios. Por favor, verifica tu conexión e inténtalo de nuevo.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Función para generar URL del mapa
  const generateMapUrl = () => {
    const { calle, numero, ciudad, provincia, codigoPostal, pais } = profileData.localizacion;
    if (!calle || !ciudad || !provincia) return null;
    
    const address = `${calle} ${numero}, ${ciudad}, ${provincia} ${codigoPostal}, ${pais}`.trim();
    const encodedAddress = encodeURIComponent(address);
    
    // Usar Google Maps embed para un diseño más moderno
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dYBTTHiOUjdOL0&q=${encodedAddress}`;
  };

  const validateAndShowErrors = () => {
    const requiredFieldsWithNames = [
      { 
        field: profileData.nombre, 
        name: 'Nombre de la organización',
        key: 'nombre'
      },
      { 
        field: profileData.personaResponsable.nombre, 
        name: 'Nombre del responsable',
        key: 'personaResponsable.nombre'
      },
      { 
        field: profileData.personaResponsable.apellidos, 
        name: 'Apellidos del responsable',
        key: 'personaResponsable.apellidos'
      },
      { 
        field: profileData.personaResponsable.email, 
        name: 'Email del responsable',
        key: 'personaResponsable.email'
      },
      { 
        field: profileData.localizacion.calle, 
        name: 'Calle',
        key: 'localizacion.calle'
      },
      { 
        field: profileData.localizacion.ciudad, 
        name: 'Ciudad',
        key: 'localizacion.ciudad'
      },
      { 
        field: profileData.localizacion.provincia, 
        name: 'Provincia',
        key: 'localizacion.provincia'
      }
    ];

    const newErrors = {};
    
    requiredFieldsWithNames.forEach(item => {
      if (!item.field || item.field.toString().trim() === '') {
        newErrors[item.key] = `${item.name} es obligatorio`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const navigateToField = (fieldRef, tabIndex) => {
    // Validar formulario y mostrar errores
    validateAndShowErrors();
    
    // Cambiar a la pestaña correcta
    setActiveTab(tabIndex);
    
    // Esperar un momento para que la pestaña se renderice
    setTimeout(() => {
      if (fieldRef.current) {
        fieldRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        fieldRef.current.focus();
        
        // Añadir un efecto visual temporal al campo
        fieldRef.current.style.boxShadow = '0 0 0 3px rgba(245, 101, 101, 0.5)';
        setTimeout(() => {
          if (fieldRef.current) {
            fieldRef.current.style.boxShadow = '';
          }
        }, 2000);
      }
    }, 100);
  };

  const handleRequestVerification = async () => {
    // Validar que los campos requeridos estén completos
    const requiredFieldsWithNames = [
      { 
        field: profileData.nombre, 
        name: 'Nombre de la organización',
        ref: nombreRef,
        tab: 0,
        section: 'Perfil Público'
      },
      { 
        field: profileData.personaResponsable.nombre, 
        name: 'Nombre del responsable',
        ref: responsableNombreRef,
        tab: 4,
        section: 'Privado'
      },
      { 
        field: profileData.personaResponsable.apellidos, 
        name: 'Apellidos del responsable',
        ref: responsableApellidosRef,
        tab: 4,
        section: 'Privado'
      },
      { 
        field: profileData.personaResponsable.email, 
        name: 'Email del responsable',
        ref: responsableEmailRef,
        tab: 4,
        section: 'Privado'
      },
      { 
        field: profileData.localizacion.calle, 
        name: 'Calle',
        ref: calleRef,
        tab: 0,
        section: 'Perfil Público'
      },
      { 
        field: profileData.localizacion.ciudad, 
        name: 'Ciudad',
        ref: ciudadRef,
        tab: 0,
        section: 'Perfil Público'
      },
      { 
        field: profileData.localizacion.provincia, 
        name: 'Provincia',
        ref: provinciaRef,
        tab: 0,
        section: 'Perfil Público'
      }
    ];

    const missingFields = requiredFieldsWithNames.filter(
      item => !item.field || item.field.toString().trim() === ''
    );
    
    if (missingFields.length > 0) {
      setModalContent({
        title: 'Campos obligatorios faltantes',
        message: 'Completa los siguientes campos para solicitar la validación:',
        type: 'validation',
        missingFields: missingFields
      });
      onErrorModalOpen();
      return;
    }

    setIsLoading(true);
    try {
      // Llamar al API de verificación
      const response = await bandasAPI.requestVerification({
        cif: profileData.cif,
        personaResponsable: {
          nombre: profileData.personaResponsable.nombre,
          apellidos: profileData.personaResponsable.apellidos,
          dni: profileData.personaResponsable.dni || '',
          telefono: profileData.personaResponsable.telefono,
          email: profileData.personaResponsable.email,
        },
      });
      
      setVerificationStatus('pending');
      if (response.data?.verificationCode) {
        setVerificationCode(response.data.verificationCode);
      }
      console.log('Solicitud de verificación enviada para:', profileData.nombre);
      
      setModalContent({
        title: '¡Solicitud de validación enviada!',
        message: 'Se ha generado tu código de validación. Envíalo por mensaje directo a cualquiera de nuestros perfiles en redes sociales para completar el proceso.',
        type: 'success'
      });
      onVerificationModalOpen();
      
    } catch (error) {
      console.error('Error al solicitar verificación:', error);
      const errorMessage = error.response?.data?.message || 'No se pudo enviar la solicitud de validación. Por favor, inténtalo de nuevo más tarde.';
      showModal(
        'Error en la Validación',
        errorMessage,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeUsername = async () => {
    if (!newUsername.trim()) return;
    setIsChangingUsername(true);
    try {
      const response = await authAPI.changeUsername(newUsername);
      toaster.create({
        title: 'Username actualizado',
        description: `Tu nuevo usuario es @${response.data.username}. Te quedan ${response.data.changesRemaining} cambios este año.`,
        type: 'success',
        duration: 5000,
        closable: true,
      });
      onUsernameModalClose();
      // Recargar para obtener el nuevo username
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'No se pudo cambiar el nombre de usuario';
      toaster.create({
        title: 'Error',
        description: errorMsg,
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsChangingUsername(false);
    }
  };

  const getVerificationBadge = () => {
    switch (verificationStatus) {
      case 'pending':
        return {
          colorScheme: 'orange',
          text: 'Validación Pendiente',
          icon: FaClock
        };
      case 'verified':
        return {
          colorScheme: 'green',
          text: 'Validado',
          icon: FaCheckCircle
        };
      case 'rejected':
        return {
          colorScheme: 'red',
          text: 'Validación Rechazada',
          icon: FaExclamationTriangle
        };
      default:
        return {
          colorScheme: 'gray',
          text: 'No Validado',
          icon: FaShieldAlt
        };
    }
  };

  const renderVerificationStatusBadge = () => {
    if (verificationStatus === 'not_requested') return null;
    return (
      <Badge
        colorPalette={verificationStatus === 'pending' ? 'orange' : verificationStatus === 'verified' ? 'green' : 'red'}
        px={3}
        py={1}
        rounded="full"
        fontSize="xs"
      >
        <HStack gap={1}>
          <Icon
            as={
              verificationStatus === 'pending'
                ? FaClock
                : verificationStatus === 'verified'
                  ? FaCheckCircle
                  : FaExclamationTriangle
            }
            w={3}
            h={3}
          />
          <Text>
            {verificationStatus === 'pending'
              ? 'Pendiente de validación'
              : verificationStatus === 'verified'
                ? 'Validado'
                : 'Validación rechazada'}
          </Text>
        </HStack>
      </Badge>
    );
  };

  const renderVerificationNotRequestedAlert = () => (
    <Alert.Root status="info" rounded="lg">
      <Alert.Indicator />
      <VStack align="start" gap={1}>
        <Text fontSize="sm" fontWeight="medium">
          Completa tu perfil para validar tu cuenta
        </Text>
        <Text fontSize="xs">
          Completa los datos obligatorios para validar (nombre, tipo de agrupación, localización con calle, ciudad, provincia y ubicación en el mapa, teléfono y email de contacto) y guarda los cambios para iniciar el proceso de validación. Hasta que tu cuenta esté validada, tu perfil no será visible para el resto de la comunidad.
        </Text>
      </VStack>
    </Alert.Root>
  );

  const renderVerificationPendingContent = () => (
    <VStack gap={4} w="full">
      <Box w="full" bg="purple.50" border="2px solid" borderColor="purple.300" rounded="xl" p={6}>
        <Text
          fontSize={{ base: '2xl', sm: '3xl', md: '4xl' }}
          fontWeight="bold"
          color="purple.600"
          letterSpacing={{ base: '0.12em', md: '0.3em' }}
          fontFamily="mono"
          textAlign="center"
          wordBreak="break-all"
        >
          {verificationCode || '------'}
        </Text>
        <HStack mt={3} mb={1} justify="center">
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 1
          </Badge>
          <Text fontSize="sm" fontWeight="600" color="purple.700">
            Copia tu código de validación
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
          Este código es único para tu solicitud
        </Text>
      </Box>

      <Box w="full" bg="white" border="1px solid" borderColor="gray.200" rounded="xl" p={4}>
        <HStack mb={3}>
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 2
          </Badge>
          <Text fontSize="sm" fontWeight="600">
            Envíalo por mensaje directo
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Abre cualquiera de nuestros perfiles y envíanos un DM con el código:
        </Text>
        <VStack gap={2} align="stretch">
          <HStack
            as="a"
            href="https://www.instagram.com/cadencya_app/"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaInstagram} color="pink.500" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              Instagram
            </Text>
          </HStack>
          <HStack
            as="a"
            href="https://x.com/cadencya_app"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaXTwitter} color="gray.800" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              X
            </Text>
          </HStack>
          <HStack
            as="a"
            href="https://www.tiktok.com/@cadencya_app"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaTiktok} color="gray.800" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              TikTok
            </Text>
          </HStack>
        </VStack>
      </Box>

      <Box w="full" bg="white" border="1px solid" borderColor="gray.200" rounded="xl" p={4}>
        <HStack mb={2}>
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 3
          </Badge>
          <Text fontSize="sm" fontWeight="600">
            Espera nuestra confirmación
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          Revisaremos tu mensaje y validaremos tu perfil. Recibirás una notificación y un email cuando esté listo.
        </Text>
      </Box>

      <Alert.Root status="warning" rounded="lg" size="sm" bg="orange.50" borderColor="orange.200" borderWidth="1px">
        <Alert.Indicator color="orange.500" />
        <VStack align="start" gap={1}>
          <Text fontSize="sm" fontWeight="bold" color="orange.700">
            Atención: tu usuario en Cadencya
          </Text>
          <Text fontSize="xs" color="orange.800">
            El nombre de usuario de la red social desde la que nos envíes el código se convertirá en tu nombre de usuario en Cadencya (por ejemplo, @tu_banda).
            Asegúrate de enviarlo desde la cuenta oficial de tu {userType === 'banda' ? 'banda' : 'cofradía'}, con datos y actividad reales. No uses una cuenta vacía o recién creada.
          </Text>
        </VStack>
      </Alert.Root>

      <Alert.Root status="info" rounded="lg" size="sm">
        <Alert.Indicator />
        <VStack align="start" gap={1}>
          <Text fontSize="xs" fontWeight="medium">
            Perfil bloqueado temporalmente
          </Text>
          <Text fontSize="xs">
            Mientras tu validación está en proceso, no es posible modificar los datos del perfil. Hasta que tu perfil no sea validado por el equipo de Cadencya en las próximas 48 horas, no serás visible para el resto de usuarios en el apartado de Comunidad.
            Este proceso es para garantizar que todos los usuarios de Cadencya sean reales.
          </Text>
        </VStack>
      </Alert.Root>
    </VStack>
  );

  const renderVerificationVerifiedContent = () => (
    <VStack gap={3} w="full">
      <Alert.Root status="success" rounded="lg" size="sm">
        <Alert.Indicator />
        <VStack align="start" gap={1}>
          <Text fontSize="sm" fontWeight="medium">
            ¡Cuenta validada!
          </Text>
          <Text fontSize="xs">
            Tu {userType === 'banda' ? 'banda' : 'cofradía'} tiene el sello de validación y es visible para toda la comunidad de Cadencya.
          </Text>
        </VStack>
      </Alert.Root>

      {user?.username && (
        <Box w="full" p={4} bg="purple.50" rounded="xl" border="1px solid" borderColor="purple.200">
          <HStack justify="space-between" align="center">
            <VStack align="start" gap={0}>
              <Text fontSize="xs" color="gray.500">
                Tu usuario en Cadencya
              </Text>
              <Text fontSize="md" fontWeight="bold" color="purple.600">
                @{user.username}
              </Text>
            </VStack>
            <Button
              size="xs"
              variant="outline"
              colorPalette="purple"
              onClick={() => {
                setNewUsername(user.username || '');
                onUsernameModalOpen();
              }}
            >
              Cambiar
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.400" mt={1}>
            Puedes cambiarlo {Math.max(0, 2 - (user.usernameChangesThisYear || 0))}{' '}
            {Math.max(0, 2 - (user.usernameChangesThisYear || 0)) === 1 ? 'vez' : 'veces'} más este año
          </Text>
        </Box>
      )}
    </VStack>
  );

  return (
    <AppLayout 
      userType={userType} 
      userName={profileData.nombre || 'Mi Banda'}
      onNavigate={handleNavigationAttempt}
    >
      <Box bg={bg} py={{ base: 4, md: 8 }}>
        <Container maxW={{ base: "full", md: "6xl" }} px={{ base: 2, sm: 4, md: 6 }}>
        {/* Header */}
        <VStack gap={{ base: 3, md: 6 }} mb={{ base: 4, md: 8 }}>
          <VStack gap={{ base: 2, md: 4 }} w="full">
            <HStack gap={4} w="full" justify="space-between" align="center" flexWrap={{ base: "wrap", sm: "nowrap" }}>
              <VStack align="start" gap={1} flex={1} minW="0">
                <HStack gap={{ base: 2, md: 3 }}>
                  <Icon as={userType === 'banda' ? FaMusic : FaChurch} color="purple.600" w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                  <Heading size={{ base: "md", sm: "lg", md: "xl" }} lineClamp={1}>Mi perfil</Heading>
                </HStack>
                <Text color={textColor} fontSize={{ base: "xs", sm: "sm", md: "md" }}>
                  Gestiona la información de tu perfil
              </Text>
            </VStack>
            
            <HStack gap={2} flexWrap="wrap" justify="flex-end">
              <ShareProfileButton shareUrl={publicProfileShareUrl} />
              <Button
                data-tour="previsualizar"
                colorPalette="purple"
                variant="outline"
                size={{ base: "sm", md: "md" }}
                rounded="full"
                onClick={handleOpenPreview}><Icon as={FaEye} />Previsualizar perfil público
                              </Button>
            </HStack>
          </HStack>

          {/* Completitud (+ validación integrada si aún no está validada / pendiente con campos por completar) */}
          {showCompletenessSection && (
            <Box
              w="full"
              data-tour="completitud"
              p={4}
              bg={checklistBg}
              rounded="xl"
              border="1px solid"
              borderColor={
                integrateValidationWithCompleteness && verificationStatus === 'pending'
                  ? 'orange.300'
                  : checklistBorderColor
              }
            >
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color={checklistTitleColor} fontWeight="600">
                  Completitud del perfil
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="purple.600">
                  {profileCompletionPct}%
                </Text>
              </HStack>
              <Progress.Root
                value={profileCompletionPct}
                colorPalette="purple"
                size="sm"
                rounded="full">
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>

              {integrateValidationWithCompleteness ? (
                <Box mt={4}>
                  <HStack justify="space-between" align="center" mb={3}>
                    <Heading size="sm">
                      <Icon as={FaShieldAlt} mr={2} color="purple.600" />
                      Validación de cuenta
                    </Heading>
                    {renderVerificationStatusBadge()}
                  </HStack>
                  {verificationStatus === 'not_requested'
                    ? renderVerificationNotRequestedAlert()
                    : renderVerificationPendingContent()}
                </Box>
              ) : null}

              {/* Checklist: solo obligatorios para validar (oculto si ya validado) */}
              {verificationStatus !== 'verified' ? (
                <Box mt={4} pt={integrateValidationWithCompleteness ? 4 : 0} borderTopWidth={integrateValidationWithCompleteness ? '1px' : 0} borderColor={checklistBorderColor}>
                  {/* Título/subtítulo: no duplicar el disclaimer cuando aún no hay código */}
                  {verificationStatus !== 'not_requested' ? (
                    <HStack align="flex-start" gap={3} mb={3}>
                      <Icon as={FaInfoCircle} color="blue.500" mt={0.5} flexShrink={0} />
                      <VStack align="start" gap={1} flex={1}>
                        <Text fontSize="sm" fontWeight="600" color={checklistTitleColor}>
                          Campos obligatorios para validar
                        </Text>
                        <Text fontSize="xs" color={checklistSubtitleColor}>
                          Debes completarlos para poder guardar e iniciar la validación. El porcentaje de arriba también incluye otros datos recomendados que mejoran tu visibilidad.
                        </Text>
                      </VStack>
                    </HStack>
                  ) : null}
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={2} columnGap={6}>
                    {bandaMandatoryChecks.map((check) => {
                      const done = check.test(profileData);
                      return (
                        <HStack key={check.label} gap={3} align="flex-start">
                          <Box
                            w="20px"
                            h="20px"
                            mt="2px"
                            rounded="full"
                            border="2px solid"
                            borderColor={done ? 'green.500' : 'gray.300'}
                            bg={done ? 'green.500' : 'transparent'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            {done && (
                              <Icon color="white" boxSize={3} asChild>
                                <FaCheck />
                              </Icon>
                            )}
                          </Box>
                          <Text
                            fontSize="sm"
                            color={done ? checkDoneColor : checkTodoColor}
                            textDecoration={done ? 'line-through' : 'none'}
                          >
                            {check.label}
                          </Text>
                        </HStack>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              ) : null}

              {bandaOptionalPendingSlides.length > 0 ? (
                verificationStatus === 'verified' ? (
                  <Accordion.Root collapsible mt={4} defaultValue={[]}>
                    <Accordion.Item
                      border="1px solid"
                      borderColor={checklistBorderColor}
                      rounded="lg"
                      overflow="hidden"
                      value='item-0'>
                      <Accordion.ItemTrigger py={3} px={3} _expanded={{ bg: checklistBg }}>
                        <Box flex="1" textAlign="left">
                          <Text fontSize="sm" fontWeight="600" color={checklistTitleColor}>
                            Campos recomendados pendientes ({bandaOptionalPendingSlides.length})
                          </Text>
                          <Text fontSize="xs" color={checklistSubtitleColor}>
                            No son obligatorios, pero mejoran tu visibilidad en Cadencya.
                          </Text>
                        </Box>
                        <Accordion.ItemIndicator />
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent pb={3} pt={0} px={3} animation='none'><Accordion.ItemBody>
                          <ProfileTipsCarousel
                            status="warning"
                            mb={0}
                            slides={bandaOptionalPendingSlides}
                            aria-label="Campos recomendados del perfil"
                          />
                        </Accordion.ItemBody></Accordion.ItemContent>
                    </Accordion.Item>
                  </Accordion.Root>
                ) : (
                  <Box mt={4}>
                    <ProfileTipsCarousel
                      status="warning"
                      mb={0}
                      slides={bandaOptionalPendingSlides}
                      aria-label="Campos recomendados del perfil"
                    />
                  </Box>
                )
              ) : null}
            </Box>
          )}
        </VStack>

        {/* Tabs: ancho fijo al del contenedor (evita que chakra-tabs se encoja con paneles “ligeros” como Secciones) */}
        <Box w="full" maxW="full" minW={0}>
        <Tabs.Root
          lazyMount
          unmountOnExit
          value={tabsControlledValue(activeTab)}
          onValueChange={tabsOnValueChange((index) => {
            if (hideColaboradorProfileTabs) return;
            setActiveTab(index);
            if (index === 3 && isColaboradorBanda && canAccessTuWeb(user)) {
              navigate('/banda/perfil?tab=tuweb', { replace: true });
            } else if (index !== 3 && isColaboradorBanda) {
              navigate('/banda/perfil', { replace: true });
            }
            // Limpiar errores al cambiar de pestaña para mejor UX
            if (Object.keys(errors).length > 0) {
              setErrors({});
            }
          })}
          variant='subtle'
          colorPalette="purple"
          size={{ base: 'md', md: 'lg' }}
          w="full"
          maxW="full"
          minW={0}
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          css={{
            width: '100%',
            maxWidth: '100%',
            '& & > *': { width: '100%', maxWidth: '100%', minWidth: 0 },
            '& & [role="tabpanel"]': { width: '100%', maxWidth: '100%', minWidth: 0 }
          }}
        >
          <Tabs.List
            data-tour="tabs"
            display={hideColaboradorProfileTabs ? 'none' : undefined}
            flexWrap={{ base: 'wrap', sm: 'nowrap' }}
            gap={2}
            bg="gray.100"
            p={2}
            rounded="full"
            w="full"
            maxW="full"
          >
            <Tab value="0" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaUser} mr={2} />
              Perfil público
            </Tab>
            <Tab value="1" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaHandshake} mr={2} />
              Avales
            </Tab>
            <Tab value="2" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaComments} mr={2} />
              Reseñas
            </Tab>
            <Tab value="3" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaGlobe} mr={2} />
              Tu Web
            </Tab>
            <Tab value="4" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaShieldAlt} mr={2} />
              Privado
            </Tab>
            <Tab value="5" fontSize={{ base: "sm", md: "md" }} px={{ base: 3, sm: 5 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaCrown} mr={2} />
              Suscripción
            </Tab>
          </Tabs.List>

          <TabPanels w="full" maxW="full" minW={0}>
            {/* Perfil Público */}
            <TabPanel value="0" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              <VStack gap={{ base: 4, md: 6, lg: 8 }} align="stretch">
                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  gap={{ base: 4, md: 6, lg: 8 }}
                  alignItems="start"
                >
                  <VStack
                    gap={{ base: 4, md: 6, lg: 8 }}
                    align="stretch"
                    w="full"
                    order={{ base: showValidationCardInSidebar && verificationStatus === 'pending' ? 2 : 1, lg: 1 }}
                  >
                  {/* Información Básica */}
                  <Card.Root data-tour="info-basica" bg={cardBg} shadow="lg" rounded="2xl">
                  <Card.Header>
                    <Heading size="md">Información básica</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={6}>
                      {/* Avatar */}
                      <VStack gap={4}>
                        <Avatar.Root size="2xl" bg="purple.100" color="purple.600"><Avatar.Fallback name={profileData.nombre} /><Avatar.Image src={profileData.avatar} /></Avatar.Root>
                        <VStack gap={2}>
                          <HStack gap={2}>
                            <Button
                              size="md"
                              onClick={() => fileInputRef.current?.click()}
                              bg="white"
                              color="purple.600"
                              border="2px"
                              borderColor="purple.200"
                              rounded="full"
                              fontWeight="600"
                              _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}><Icon as={FaCamera} />{profileData.avatar ? 'Cambiar' : 'Subir foto'}</Button>
                            {profileData.avatar && (
                              <Button
                                size="md"
                                onClick={handleEditCurrentAvatar}
                                bg="white"
                                color="purple.600"
                                border="2px"
                                borderColor="purple.200"
                                rounded="full"
                                fontWeight="600"
                                _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}><Icon as={FaCrop} />Editar
                                                              </Button>
                            )}
                          </HStack>
                          <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleAvatarUpload}
                          />
                          <Text fontSize="xs" color={textColor} textAlign="center">
                            JPG, PNG. Máximo 5MB.
                          </Text>
                        </VStack>
                      </VStack>

                      {/* Imagen de Fondo del Header */}
                      <VStack gap={4} w="full">
                        <Text fontWeight="medium" alignSelf="start">Imagen de fondo del perfil</Text>
                        <Box
                          w="full"
                          h="120px"
                          bg="gray.100"
                          rounded="lg"
                          border="2px dashed"
                          borderColor="gray.300"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          position="relative"
                          overflow="hidden"
                          style={profileData.fondoHeaderImagen ? {
                            backgroundImage: `url(${profileData.fondoHeaderImagen})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          } : {}}
                          ref={(el) => {
                            if (el && profileData.fondoHeaderImagen) {
                              console.log('Box con fondo renderizado, tiene imagen:', !!profileData.fondoHeaderImagen);
                              console.log('Estilo aplicado:', el.style.backgroundImage ? 'SÍ' : 'NO');
                            }
                          }}
                        >
                          {!profileData.fondoHeaderImagen ? (
                            <VStack gap={2} color="gray.500">
                              <Icon as={FaCamera} w={8} h={8} />
                              <Text fontSize="sm" textAlign="center">
                                Sube una imagen de fondo
                              </Text>
                            </VStack>
                          ) : (
                            <Box
                              position="absolute"
                              top="2"
                              right="2"
                              bg="blackAlpha.700"
                              rounded="full"
                              p={2}
                            >
                              <Icon as={FaCamera} color="white" w={4} h={4} />
                            </Box>
                          )}
                        </Box>
                        <HStack gap={3} w="full" flexWrap="wrap" justifyContent="center">
                          <Button
                            size="md"
                            onClick={() => document.getElementById('fondo-header-input').click()}
                            bg="white"
                            color="purple.600"
                            border="2px"
                            borderColor="purple.200"
                            rounded="full"
                            fontWeight="600"
                            _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}><Icon as={FaCamera} />{profileData.fondoHeaderImagen ? 'Cambiar' : 'Subir Fondo'}</Button>
                          {profileData.fondoHeaderImagen && (
                            <>
                              <Button
                                size="md"
                                onClick={handleEditCurrentFondo}
                                bg="white"
                                color="purple.600"
                                border="2px"
                                borderColor="purple.200"
                                rounded="full"
                                fontWeight="600"
                                _hover={{ bg: 'purple.50', borderColor: 'purple.300' }}><Icon as={FaCrop} />Editar
                                                              </Button>
                              <Button
                                size="md"
                                onClick={() => handleInputChange('fondoHeaderImagen', null)}
                                bg="red.500"
                                color="white"
                                rounded="full"
                                fontWeight="600"
                                _hover={{ bg: 'red.600' }}><Icon as={FaTimes} />Quitar
                                                              </Button>
                            </>
                          )}
                        </HStack>
                        <input
                          id="fondo-header-input"
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleFondoHeaderUpload}
                        />
                        <Text fontSize="xs" color={mutedColor} textAlign="center">
                          JPG, PNG. Máximo 5MB. Esta imagen aparecerá como fondo en tu perfil público.
                        </Text>
                      </VStack>

                                              {/* Nombre */}
                        <Field.Root  invalid={errors.nombre}>
                          <Field.Label>Nombre de la banda</Field.Label>
                        <Input
                          ref={nombreRef}
                          value={profileData.nombre}
                          onChange={(e) => {
                            handleInputChange('nombre', e.target.value);
                            if (errors.nombre && e.target.value.trim()) {
                              setErrors(prev => ({ ...prev, nombre: undefined }));
                            }
                          }}
                          placeholder="Nombre de tu banda"
                          maxLength={300}
                        />
                        <Field.HelperText color={profileData.nombre?.length >= 40 ? 'red.500' : 'gray.500'}>
                          {profileData.nombre?.length || 0}/300 caracteres
                        </Field.HelperText>
                        <Field.ErrorText>{errors.nombre}</Field.ErrorText>
                      </Field.Root>

                        {/* Tipo de banda (solo lectura; se fija en el registro) */}
                        <Field.Root>
                          <Field.Label>Tipo de banda</Field.Label>
                          <Input
                            readOnly
                            value={
                              profileData.tipoAgrupacionInstrumental?.nombre || profileData.tipoGrupo || '—'
                            }
                            bg={inputBg}
                            cursor="not-allowed"
                          />
                          <Alert.Root status="info" variant="subtle" rounded="md" mt={2}>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">No editable</Alert.Title>
                              <Alert.Description fontSize="xs">
                                El tipo de banda se eligió al crear la cuenta y no se puede cambiar. Define cómo te
                                clasifican las cofradías y entidades en búsquedas y filtros.
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                        </Field.Root>

                      {/* Bio */}
                      <Field.Root>
                        <Field.Label>Descripción</Field.Label>
                        <Textarea
                          value={profileData.bio}
                          onChange={(e) => handleInputChange('bio', e.target.value)}
                          placeholder="Describe tu banda, estilo musical, experiencia..."
                          rows={4}
                          resize="vertical"
                        />
                        <Field.HelperText>
                          Esta información será visible para otros usuarios
                        </Field.HelperText>
                      </Field.Root>

                      {/* Nº Músicos */}
                      <Field.Root>
                        <Field.Label>Número de músicos</Field.Label>
                        <NumberInput.Root
                          value={profileData.nMusicos == null || profileData.nMusicos === '' ? '' : String(profileData.nMusicos)}
                          onValueChange={(details) =>
                            handleInputChange(
                              'nMusicos',
                              details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                            )
                          }
                          min={0}
                        >
                          <NumberInput.Input placeholder="Indica el número de componentes" />
                              <NumberInput.Control>
                              <NumberInput.IncrementTrigger />
                              <NumberInput.DecrementTrigger />
                          </NumberInput.Control>
                        </NumberInput.Root>
                        <Field.HelperText>
                          Esta información será visible para otros usuarios
                        </Field.HelperText>
                      </Field.Root>

                      {/* Año de fundación */}
                      <Field.Root>
                        <Field.Label>Año de fundación</Field.Label>
                        <NumberInput.Root
                          value={profileData.anoFundacion == null || profileData.anoFundacion === '' ? '' : String(profileData.anoFundacion)}
                          onValueChange={(details) =>
                            handleInputChange(
                              'anoFundacion',
                              details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                            )
                          }
                          min={1800}
                          max={new Date().getFullYear()}
                        >
                          <NumberInput.Input placeholder="Ej: 1950" />
                          <NumberInput.Control>
                            <NumberInput.IncrementTrigger />
                            <NumberInput.DecrementTrigger />
                          </NumberInput.Control>
                        </NumberInput.Root>
                        <Field.HelperText>
                          Indica el año en que se fundó la agrupación
                        </Field.HelperText>
                      </Field.Root>

                      <Field.Root data-testid="profile-desplazamiento-privado">
                        <HStack justify="space-between" align="center" mb={1}>
                          <Field.Label mb={0}>Kilómetros dispuestos a desplazarse</Field.Label>
                          <Badge colorPalette="purple" variant="subtle" fontSize="2xs">
                            Dato privado
                          </Badge>
                        </HStack>
                        <NumberInput.Root
                          value={profileData.kmDispuestos == null || profileData.kmDispuestos === '' ? '' : String(profileData.kmDispuestos)}
                          onValueChange={(details) =>
                            handleInputChange(
                              'kmDispuestos',
                              details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                            )
                          }
                          min={0}
                          max={1000}
                        >
                          <NumberInput.Input placeholder="50" />
                          <NumberInput.Control>
                            <NumberInput.IncrementTrigger />
                            <NumberInput.DecrementTrigger />
                          </NumberInput.Control>
                        </NumberInput.Root>
                        <Alert.Root
                          status="info"
                          variant='subtle'
                          rounded="md"
                          mt={2}
                          py={2}
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator boxSize={4} />
                          <Alert.Description fontSize="xs">
                            Solo lo usamos de forma interna para valorar contrataciones. No aparece en tu perfil público ni en la web.
                          </Alert.Description>
                        </Alert.Root>
                      </Field.Root>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                  {/* Redes Sociales (columna izquierda: evita el hueco junto a la columna derecha) */}
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaInstagram} mr={2} color="purple.600" />
                        Redes sociales
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Añade tus redes sociales para que las cofradías puedan conocerte mejor
                      </Text>
                    </Card.Header>
                    <Card.Body>
                      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        <Field.Root>
                          <Field.Label>
                            <HStack gap={2}>
                              <Icon as={FaInstagram} color="pink.500" />
                              <Text>Instagram</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={profileData.redesSociales.instagram}
                            onChange={(e) => handleNestedInputChange('redesSociales', 'instagram', e.target.value)}
                            placeholder="@tu_banda_musical"
                          />
                          <Field.HelperText>
                            Usuario de Instagram (con o sin @)
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>
                            <HStack gap={2}>
                              <Icon as={FaFacebook} color="blue.500" />
                              <Text>Facebook</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={profileData.redesSociales.facebook}
                            onChange={(e) => handleNestedInputChange('redesSociales', 'facebook', e.target.value)}
                            placeholder="https://www.facebook.com/tu.perfil"
                            type="url"
                          />
                          <Field.HelperText>
                            Enlace a tu perfil o página de Facebook
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>
                            <HStack gap={2}>
                              <Icon as={FaYoutube} color="red.500" />
                              <Text>YouTube</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={profileData.redesSociales.youtube}
                            onChange={(e) => handleNestedInputChange('redesSociales', 'youtube', e.target.value)}
                            placeholder="@tu_canal o https://www.youtube.com/@tu_canal"
                          />
                          <Field.HelperText>
                            Usuario/canal o enlace completo de YouTube
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>
                            <HStack gap={2}>
                              <Icon as={FaXTwitter} color="gray.800" />
                              <Text>X</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={profileData.redesSociales.twitter || ''}
                            onChange={(e) => handleNestedInputChange('redesSociales', 'twitter', e.target.value)}
                            placeholder="@tu_banda o https://x.com/tu_banda"
                          />
                          <Field.HelperText>
                            Usuario de X (con o sin @) o enlace completo
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>
                            <HStack gap={2}>
                              <Icon as={FaTiktok} color="gray.800" />
                              <Text>TikTok</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={profileData.redesSociales.tiktok}
                            onChange={(e) => handleNestedInputChange('redesSociales', 'tiktok', e.target.value)}
                            placeholder="@tu_banda_musical"
                          />
                          <Field.HelperText>
                            Usuario de TikTok (con o sin @)
                          </Field.HelperText>
                        </Field.Root>
                      </SimpleGrid>
                    </Card.Body>
                  </Card.Root>

                  </VStack>

                {/* Localización y Contacto */}
                <VStack
                  gap={{ base: 6, md: 8 }}
                  align="stretch"
                  w="full"
                  order={{ base: showValidationCardInSidebar && verificationStatus === 'pending' ? 1 : 2, lg: 2 }}
                >
                  {/* Validación de cuenta (lateral): solo si ya validó, o sin barra de completitud */}
                  {showValidationCardInSidebar ? (
                  <Card.Root
                    bg={cardBg}
                    shadow="lg"
                    rounded="2xl"
                    w="full"
                    borderWidth={verificationStatus === 'pending' ? '2px' : '1px'}
                    borderColor={verificationStatus === 'pending' ? 'orange.300' : verificationStatus === 'verified' ? 'green.300' : 'gray.200'}
                  >
                    <Card.Header>
                      <HStack justify="space-between" align="center">
                        <Heading size="md">
                          <Icon as={FaShieldAlt} mr={2} color="purple.600" />
                          Validación de cuenta
                        </Heading>
                        {renderVerificationStatusBadge()}
                      </HStack>
                    </Card.Header>
                    <Card.Body pt={0}>
                      {verificationStatus === 'not_requested' && renderVerificationNotRequestedAlert()}
                      {verificationStatus === 'pending' && renderVerificationPendingContent()}
                      {verificationStatus === 'verified' && renderVerificationVerifiedContent()}
                    </Card.Body>
                  </Card.Root>
                  ) : null}

                  {/* Localización */}
                  <Card.Root data-tour="localizacion" bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaMapMarkerAlt} mr={2} color="purple.600" />
                        Localización
                      </Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={6}>
                        {/* Campo de búsqueda de ubicación */}
                        <Field.Root >
                          <Field.Label>Buscar ubicación</Field.Label>
                          <Box position="relative">
                            <InputGroup>
                              <InputLeftElement pointerEvents="none">
                                <Icon as={FaMapMarkerAlt} color="gray.400" />
                              </InputLeftElement>
                              <Input
                                value={locationSearchQuery}
                                onChange={(e) => setLocationSearchQuery(e.target.value)}
                                placeholder="Escribe una dirección, ciudad o lugar..."
                                onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                              />
                              {isSearchingLocation && (
                                <InputRightElement>
                                  <Spinner size="sm" color="purple.500" />
                                </InputRightElement>
                              )}
                            </InputGroup>
                            
                            {/* Lista de sugerencias */}
                            {showLocationSuggestions && locationSuggestions.length > 0 && (
                              <Box
                                position="absolute"
                                top="100%"
                                left={0}
                                right={0}
                                mt={1}
                                bg={cardBg}
                                border="1px solid"
                                borderColor={borderColorAlt}
                                borderRadius="md"
                                shadow="lg"
                                maxH="300px"
                                overflowY="auto"
                                zIndex={1000}
                              >
                                {locationSuggestions.map((suggestion, index) => (
                                  <Box
                                    key={index}
                                    p={3}
                                    cursor="pointer"
                                    _hover={{ bg: reviewCardBg }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      selectLocation(suggestion);
                                    }}
                                    onClick={() => selectLocation(suggestion)}
                                    borderBottom="1px solid"
                                    borderColor={subtleBorderColor}
                                  >
                                    <HStack gap={2}>
                                      <Icon as={FaMapMarkerAlt} color="purple.500" />
                                      <Text fontSize="sm">{suggestion.display_name}</Text>
                                    </HStack>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Field.HelperText>
                            Escribe para buscar tu ubicación. Las sugerencias aparecerán automáticamente.
                          </Field.HelperText>
                        </Field.Root>

                        {/* Campos autocompletados (solo lectura) */}
                        {(profileData.localizacion.ciudad || profileData.localizacion.provincia) && (
                          <Box w="full" p={4} bg={reviewCardBg} borderRadius="md">
                            <Text fontSize="sm" fontWeight="medium" mb={3} color={textColor}>
                              Información de la ubicación:
                            </Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                              {profileData.localizacion.calle && (
                                <HStack>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.500">Calle:</Text>
                                  <Text fontSize="xs">{profileData.localizacion.calle} {profileData.localizacion.numero}</Text>
                                </HStack>
                              )}
                              {profileData.localizacion.ciudad && (
                                <HStack>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.500">Ciudad:</Text>
                                  <Text fontSize="xs">{profileData.localizacion.ciudad}</Text>
                                </HStack>
                              )}
                              {profileData.localizacion.provincia && (
                                <HStack>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.500">Provincia:</Text>
                                  <Text fontSize="xs">{profileData.localizacion.provincia}</Text>
                                </HStack>
                              )}
                              {profileData.localizacion.codigoPostal && (
                                <HStack>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.500">C.P.:</Text>
                                  <Text fontSize="xs">{profileData.localizacion.codigoPostal}</Text>
                                </HStack>
                              )}
                              {(profileData.lat && profileData.lng) && (
                                <HStack gridColumn={{ base: 1, md: "1 / 3" }}>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.500">Coordenadas:</Text>
                                  <Text fontSize="xs">{Number(profileData.lat).toFixed(6)}, {Number(profileData.lng).toFixed(6)}</Text>
                                </HStack>
                              )}
                            </SimpleGrid>
                          </Box>
                        )}

                        {/* Mini Mapa de Previsualización con OpenStreetMap */}
                        {profileData.localizacion?.ciudad && profileData.localizacion?.provincia && (
                          <Box w="full">
                            <Text fontSize="sm" fontWeight="medium" mb={3} color={textColor}>
                              Vista previa de la ubicación:
                            </Text>
                            <LocationMap 
                              address={profileData.localizacion}
                              height="250px"
                              nombre={profileData.nombre}
                            />
                          </Box>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>

                  {/* Información de contacto (no visible en el perfil público de la plataforma) */}
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaPhone} mr={2} color="purple.600" />
                        Información de contacto
                      </Heading>
                      <Text fontSize="xs" color="gray.600" mt={2} lineHeight="short">
                      El teléfono y el email no aparecerán en tu perfil público en Cadencya; los usamos para la gestión de la cuenta y
                        para comunicarnos contigo cuando sea necesario.
                      </Text>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={4}>
                        <Field.Root>
                          <Field.Label>Teléfono de Contacto</Field.Label>
                          <Input
                            value={profileData.contactoPublico.telefono}
                            onChange={(e) => handleNestedInputChange('contactoPublico', 'telefono', e.target.value)}
                            placeholder="+34 600 123 456"
                            type="tel"
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>Email de Contacto</Field.Label>
                          <Input
                            value={profileData.contactoPublico.email}
                            onChange={(e) => handleNestedInputChange('contactoPublico', 'email', e.target.value)}
                            placeholder="contacto@ejemplo.com"
                            type="email"
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>Sitio web</Field.Label>
                          {currentSlug && (
                            <HStack justify="space-between" mb={2} p={2.5} rounded="md" borderWidth="1px" borderColor="purple.100" bg="purple.50">
                              <VStack align="start" gap={0} flex={1}>
                                <Text fontSize="sm" fontWeight="600" color="purple.700">Usar tu web pública</Text>
                                <Text fontSize="xs" color="purple.600">{currentSlug}.{micrositeDomain}</Text>
                              </VStack>
                              <Switch
                                colorPalette="purple"
                                checked={useMicrositeUrlAsWebsite}
                                onChange={(e) => {
                                  const enabled = e.target.checked;
                                  setUseMicrositeUrlAsWebsite(enabled);
                                  if (enabled) {
                                    handleNestedInputChange('contactoPublico', 'website', micrositePublicUrl);
                                  }
                                }}
                              />
                            </HStack>
                          )}
                          <Input
                            value={profileData.contactoPublico.website}
                            onChange={(e) => handleNestedInputChange('contactoPublico', 'website', e.target.value)}
                            placeholder="https://www.ejemplo.com"
                            type="url"
                            disabled={useMicrositeUrlAsWebsite}
                          />
                          {currentSlug && useMicrositeUrlAsWebsite && (
                            <Field.HelperText color="purple.600">
                              Se usa automáticamente la URL de tu web pública. Desactiva el switch para editar este campo.
                            </Field.HelperText>
                          )}
                        </Field.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>

                </VStack>
              </SimpleGrid>

              {/* Especialidades — ancho completo (debajo del grid de dos columnas) */}
              <Card.Root data-tour="especialidades" bg={cardBg} shadow="lg" rounded="2xl" w="full">
                <Card.Header>
                  <Heading size="md">
                    <Icon as={FaMusic} mr={2} color="purple.600" />
                    Especialidades musicales
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    Indica qué tipos de música y eventos domina tu banda
                  </Text>
                </Card.Header>
                <Card.Body>
                  <SkillsEditor
                    title="Especialidades"
                    helperText="Añade los estilos musicales y tipos de eventos en los que tu banda tiene experiencia"
                    placeholder="Ej: Marchas Procesionales, Música Sacra..."
                    skills={profileData.especialidades}
                    onSkillsChange={(newSkills) => handleInputChange('especialidades', newSkills)}
                    suggestions={especialidadesSugeridas}
                    maxSkills={15}
                  />
                </Card.Body>
              </Card.Root>

              {/* Repertorio */}
              <Card.Root data-tour="repertorio" bg={cardBg} shadow="lg" rounded="2xl" w="full">
                <Card.Header>
                  <HStack justify="space-between" align="center" flexWrap="wrap">
                    <VStack align="start" gap={1}>
                      <Heading size="md">
                        <Icon as={FaMusic} mr={2} color="purple.600" />
                        Repertorio
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Añade las marchas y obras que tu banda interpreta
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Al escanear un PDF se analizan hasta 10 páginas a la vez (máximo 10 escaneos al día).
                      </Text>
                    </VStack>
                    <HStack gap={2}>
                      <input
                        type="file"
                        accept="application/pdf"
                        ref={pdfInputRef}
                        style={{ display: 'none' }}
                        onChange={handlePdfUpload}
                      />
                      <Button
                        colorPalette="purple"
                        variant="outline"
                        size="sm"
                        onClick={handleOpenImportArchivoModal}
                        loading={archivoMusicaLoading && !archivoMusicaLoaded}
                        loadingText="Cargando…"><Icon as={FaUpload} />Importar archivo
                                              </Button>
                      <Button
                        colorPalette="green"
                        variant="outline"
                        size="sm"
                        onClick={() => pdfInputRef.current?.click()}
                        loading={isScanningPdf}
                        loadingText="Escaneando..."><Icon as={FaFileAlt} />Escanear PDF
                                              </Button>
                      {profileData.repertorio.length > 0 && (
                        <Button
                          colorPalette="purple"
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadPDF}><Icon as={FaArrowRight} />Descargar PDF
                                                  </Button>
                      )}
                    </HStack>
                  </HStack>
                </Card.Header>
                    <Card.Body>
                      <VStack gap={4} align="stretch">
                        {/* Input con autocompletado */}
                        <Field.Root position="relative">
                          <Field.Label fontSize="sm" fontWeight="600">
                            Añadir marcha u obra
                          </Field.Label>
                          <HStack>
                            <Box position="relative" flex={1}>
                              <Input
                                placeholder="Busca en nuestra base de datos o escribe manualmente..."
                                value={repertorioInput}
                                onChange={(e) => handleRepertorioInputChange(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && marchasFiltradas.length === 0) {
                                    handleAddMarchaManual();
                                  }
                                }}
                                bg={inputBg}
                              />
                              
                              {/* Dropdown de sugerencias */}
                              {showSuggestions && marchasFiltradas.length > 0 && (
                                <Box
                                  position="absolute"
                                  top="100%"
                                  left={0}
                                  right={0}
                                bg={inputBg}
                                border="1px solid"
                                borderColor={borderColorAlt}
                                borderRadius="md"
                                mt={1}
                                maxH="300px"
                                overflowY="auto"
                                zIndex={10}
                                shadow="lg"
                              >
                                  {marchasFiltradas.map((marcha) => (
                                    <Box
                                      key={marcha.id}
                                      p={3}
                                      cursor="pointer"
                                      _hover={{ bg: purpleHoverBg }}
                                      onClick={() => handleAddMarchaFromDB(marcha)}
                                      borderBottom="1px solid"
                                      borderColor={subtleBorderColorB}
                                    >
                                      <Text fontWeight="600" fontSize="sm">
                                        {marcha.nombre}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {marcha.autor} • {marcha.tipo}
                                      </Text>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                            
                            <Button
                              colorPalette="purple"
                              onClick={handleAddMarchaManual}
                              disabled={!repertorioInput.trim()}><Icon as={FaPlus} />Añadir
                                                          </Button>
                          </HStack>
                          <Field.HelperText fontSize="xs">
                            Escribe al menos 2 caracteres para buscar en la base de datos. Si no encuentras la marcha, puedes añadirla manualmente.
                          </Field.HelperText>
                        </Field.Root>

                        {/* Lista de marchas en el repertorio */}
                        {profileData.repertorio.length > 0 && (
                          <VStack gap={3} align="stretch" mt={4}>
                            <Text fontSize="sm" fontWeight="600" color="gray.600">
                              Marchas en tu repertorio ({profileData.repertorio.length})
                            </Text>
                            <Box 
                              border="1px solid" 
                              borderColor={borderColorAlt} 
                              rounded="lg"
                              maxHeight="400px"
                              overflowY="auto"
                              css={{
                                '& &::-webkit-scrollbar': {
                                  width: '8px',
                                },

                                '& &::-webkit-scrollbar-track': {
                                  background: subtleBorderColor,
                                  borderRadius: '10px',
                                },

                                '& &::-webkit-scrollbar-thumb': {
                                  background: scrollbarThumbBg,
                                  borderRadius: '10px',
                                  '&:hover': {
                                    background: scrollbarThumbHoverBg,
                                  },
                                }
                              }}
                            >
                              {profileData.repertorio.map((marcha, index) => (
                                <Flex
                                  key={marcha.id}
                                  p={3}
                                  align="center"
                                  justify="space-between"
                                  bg={index % 2 === 0 ? cardBg : reviewCardBg}
                                  borderBottom={index < profileData.repertorio.length - 1 ? '1px solid' : 'none'}
                                  borderColor={borderColorAlt}
                                  _hover={{ bg: purpleHoverBg }}
                                  transition="background 0.2s"
                                >
                                  <HStack gap={3} flex={1}>
                                    <Text 
                                      fontSize="sm" 
                                      fontWeight="500" 
                                      color="gray.500" 
                                      minW="30px"
                                    >
                                      {index + 1}.
                                    </Text>
                                    <VStack align="start" gap={0} flex={1}>
                                      <HStack gap={2} flexWrap="wrap">
                                        <Text fontWeight="600" fontSize="sm">
                                          {marcha.nombre}
                                        </Text>
                                        {marcha.fromDB && (
                                          <Badge colorPalette="purple" fontSize="2xs">
                                            Validado
                                          </Badge>
                                        )}
                                      </HStack>
                                      <HStack gap={2} flexWrap="wrap">
                                        {marcha.autor && (
                                          <Text fontSize="xs" color="gray.500">
                                            {marcha.autor}
                                          </Text>
                                        )}
                                        {marcha.autor && marcha.tipo && (
                                          <Text fontSize="xs" color="gray.400">•</Text>
                                        )}
                                        {marcha.tipo && (
                                          <Text fontSize="xs" color="gray.500">
                                            {marcha.tipo}
                                          </Text>
                                        )}
                                      </HStack>
                                    </VStack>
                                  </HStack>
                                  <IconButton
                                    size="sm"
                                    colorPalette="red"
                                    variant="ghost"
                                    onClick={() => handleRemoveMarcha(marcha.id)}
                                    aria-label="Eliminar marcha"><Icon as={FaTrash} /></IconButton>
                                </Flex>
                              ))}
                            </Box>
                          </VStack>
                        )}

                        {profileData.repertorio.length === 0 && (
                          <Alert.Root status="info" rounded="lg">
                            <Alert.Indicator />
                            <Text fontSize="sm">
                              Aún no has añadido ninguna marcha a tu repertorio. Empieza buscando en nuestra base de datos o añadiendo obras manualmente.
                            </Text>
                          </Alert.Root>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>

              {/* Horarios de Ensayo */}
              {userType === 'banda' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <HStack justify="space-between" align="center" flexWrap="wrap">
                      <VStack align="start" gap={1}>
                        <Heading size="md">
                          <Icon as={FaDrum} mr={2} color="purple.600" />
                          Horarios de ensayo
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Comparte cuándo ensaya tu banda para que otros puedan asistir
                        </Text>
                      </VStack>
                      <HStack gap={3}>
                        <Field.Root display="flex" alignItems="center">
                          <Field.Label htmlFor="ensayos-publicos" mb="0" fontSize="sm" fontWeight="600">
                            Mostrar públicamente
                          </Field.Label>
                          <Switch
                            id="ensayos-publicos"
                            colorPalette="purple"
                            checked={profileData.ensayos.publico}
                            onValueChange={handleToggleEnsayosPublicos}
                          />
                        </Field.Root>
                      </HStack>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                      {profileData.ensayos.publico && (
                        <Alert.Root status="info" rounded="lg">
                          <Alert.Indicator />
                          <VStack align="start" gap={1}>
                            <Text fontSize="sm" fontWeight="600">
                              Ensayos públicos activados
                            </Text>
                            <Text fontSize="xs">
                              Otras bandas y cofradías podrán ver tus horarios de ensayo en tu perfil público y solicitar asistir como oyentes.
                            </Text>
                          </VStack>
                        </Alert.Root>
                      )}

                      {/* Botón para añadir ensayo */}
                      <Button
                        colorPalette="purple"
                        variant="outline"
                        onClick={handleAddEnsayo}
                        size="sm"><Icon as={FaPlus} />Añadir horario de ensayo
                                              </Button>

                      {/* Lista de ensayos */}
                      {profileData.ensayos.horarios.length > 0 ? (
                        <VStack gap={3} align="stretch">
                          {profileData.ensayos.horarios.map((ensayo) => (
                            <Card.Root
                              key={ensayo.id}
                              size="sm"
                              variant="outline"
                              bg={reviewCardBg}
                              borderWidth="2px"
                              borderColor={borderColorAlt}
                            >
                              <Card.Body>
                                <HStack justify="space-between" align="start" w="full">
                                  <VStack align="start" gap={2} flex={1}>
                                    <HStack gap={2}>
                                      <Icon as={FaClock} color="purple.500" />
                                      <Text fontWeight="bold" fontSize="md">
                                        {ensayo.diaSemana || 'Día no especificado'}
                                      </Text>
                                    </HStack>
                                    
                                    {(ensayo.horaInicio || ensayo.horaFin) && (
                                      <HStack gap={2}>
                                        <Icon as={FaClock} color="gray.500" boxSize={3} />
                                        <Text fontSize="sm" color="gray.600">
                                          {ensayo.horaInicio || '--:--'} - {ensayo.horaFin || '--:--'}
                                        </Text>
                                      </HStack>
                                    )}
                                    
                                    {ensayo.ubicacion && (
                                      <HStack gap={2}>
                                        <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={3} />
                                        <Text fontSize="sm" color="gray.600">
                                          {ensayo.ubicacion}
                                        </Text>
                                      </HStack>
                                    )}
                                    
                                    {ensayo.notas && (
                                      <Box 
                                        bg={checklistBg} 
                                        p={2} 
                                        rounded="md" 
                                        w="full"
                                      >
                                        <Text fontSize="xs" color="gray.700">
                                          <strong>Notas:</strong> {ensayo.notas}
                                        </Text>
                                      </Box>
                                    )}
                                  </VStack>
                                  
                                  <HStack gap={1}>
                                    <IconButton
                                      size="sm"
                                      colorPalette="blue"
                                      variant="ghost"
                                      onClick={() => handleEditEnsayo(ensayo)}
                                      aria-label="Editar ensayo"><Icon as={FaEdit} /></IconButton>
                                    <IconButton
                                      size="sm"
                                      colorPalette="red"
                                      variant="ghost"
                                      onClick={() => handleRemoveEnsayo(ensayo.id)}
                                      aria-label="Eliminar ensayo"><Icon as={FaTrash} /></IconButton>
                                  </HStack>
                                </HStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                        </VStack>
                      ) : (
                        <Alert.Root status="info" rounded="lg">
                          <Alert.Indicator />
                          <Text fontSize="sm">
                            No has añadido ningún horario de ensayo. Haz clic en "Añadir horario de ensayo" para empezar.
                          </Text>
                        </Alert.Root>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>
              )}

              {/* Galería de Fotos */}
              <Card.Root data-tour="galeria" bg={cardBg} shadow="lg" rounded="2xl">
                <Card.Header>
                  <HStack justify="space-between">
                    <Heading size="md">
                      <Icon as={FaCamera} mr={2} color="purple.600" />
                      Galería de fotos
                    </Heading>
                    <Text fontSize="sm" color={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.500' : 'gray.500'} fontWeight="medium">
                      {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS}
                    </Text>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <VStack gap={6}>
                    {/* Zona de Drag & Drop */}
                    <Box
                      w="full"
                      h="200px"
                      border="2px dashed"
                      borderColor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.300' : isDragOver ? 'purple.400' : 'gray.300'}
                      borderRadius="lg"
                      bg={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.50' : isDragOver ? 'purple.50' : 'gray.50'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'not-allowed' : 'pointer'}
                      opacity={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 0.6 : 1}
                      transition="all 0.3s ease"
                      onDragOver={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragOver}
                      onDragLeave={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragLeave}
                      onDrop={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDrop}
                      onClick={() => {
                        if ((profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS) {
                          showModal('Límite alcanzado', `Has alcanzado el límite de ${MAX_GALLERY_PHOTOS} fotos. Elimina alguna para subir más.`, 'warning');
                          return;
                        }
                        galleryInputRef.current?.click();
                      }}
                      _hover={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? {} : {
                        borderColor: 'purple.400',
                        bg: 'purple.50',
                        transform: 'translateY(-2px)',
                        shadow: 'lg'
                      }}
                      position="relative"
                      overflow="hidden"
                    >
                      {isUploading ? (
                        <VStack gap={3}>
                          <Progress.Root indeterminate colorPalette="purple" w="200px" rounded="full">
                            <Progress.Track>
                              <Progress.Range />
                            </Progress.Track>
                          </Progress.Root>
                          <Text color="purple.600" fontWeight="medium">
                            Procesando imágenes...
                          </Text>
                        </VStack>
                      ) : (
                        <VStack gap={3} textAlign="center">
                          <Icon 
                            as={FaCamera} 
                            w={12} 
                            h={12} 
                            color={isDragOver ? 'purple.500' : 'gray.400'}
                            transition="color 0.3s ease"
                          />
                          <VStack gap={1}>
                            <Text 
                              fontSize="lg" 
                              fontWeight="semibold" 
                              color={isDragOver ? 'purple.600' : 'gray.600'}
                            >
                              {isDragOver ? '¡Suelta las imágenes aquí!' : 'Arrastra y suelta tus fotos'}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              o haz clic para seleccionar archivos
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              JPG, PNG, WebP • Máx. 5MB por imagen • {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS} fotos
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                      
                      <input
                        ref={galleryInputRef}
                        type="file"
                        hidden
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                      />
                    </Box>

                    {/* Galería de Fotos Subidas */}
                    {profileData.fotos && profileData.fotos.length > 0 && (
                      <VStack gap={4} w="full">
                        <Separator />
                        <HStack gap={2} w="full" justify="space-between">
                          <Text fontSize="md" fontWeight="medium">
                            Fotos subidas ({profileData.fotos.length})
                          </Text>
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            onClick={onDeleteAllPhotosModalOpen}><Icon as={FaTrash} />Eliminar todas
                                                      </Button>
                        </HStack>
                        
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={4} w="full">
                          {profileData.fotos.map((photoUrl, index) => (
                            <Box 
                              key={index} 
                              position="relative" 
                              rounded="lg" 
                              overflow="hidden"
                              bg="white"
                              shadow="md"
                              _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }}
                              transition="all 0.3s ease"
                            >
                              <Image
                                src={photoUrl}
                                alt="Foto del perfil"
                                w="full"
                                h="32"
                                objectFit="cover"
                                cursor="pointer"
                                onClick={() => handleImageClick(photoUrl)}
                              />

                              {/* Botón eliminar */}
                              <IconButton
                                size="sm"
                                colorPalette="red"
                                variant="solid"
                                position="absolute"
                                top={2}
                                right={2}
                                onClick={() => removePhoto(photoUrl)}
                                aria-label="Eliminar foto"
                                shadow="lg"
                                _hover={{ transform: 'scale(1.1)' }}
                                transition="transform 0.2s ease"><Icon as={FaTrash} /></IconButton>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </VStack>
                    )}
                    {/* Videos de YouTube */}
                    <Card.Root bg="white" shadow="md" rounded="xl" w="full">
                      <Card.Body>
                        <VStack gap={4} align="stretch">
                          <HStack>
                            <Icon as={FaYoutube} mr={2} color="red.500" />
                            <Heading size="sm">Videos de YouTube</Heading>
                          </HStack>
                          
                          {/* Formulario para añadir video */}
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            <Field.Root>
                              <Field.Label fontSize="sm">Título del video</Field.Label>
                              <Input
                                value={newVideoTitle}
                                onChange={(e) => setNewVideoTitle(e.target.value)}
                                placeholder="Ej: Concierto Semana Santa 2024"
                                size="sm"
                              />
                            </Field.Root>
                            <Field.Root>
                              <Field.Label fontSize="sm">URL de YouTube o Short</Field.Label>
                              <Input
                                value={newVideoUrl}
                                onChange={(e) => setNewVideoUrl(e.target.value)}
                                placeholder="youtube.com/watch?v=... · youtube.com/shorts/..."
                                size="sm"
                              />
                            </Field.Root>
                          </SimpleGrid>
                          
                          <Button
                            colorPalette="red"
                            variant="outline"
                            size="sm"
                            onClick={handleAddVideo}
                            alignSelf="flex-start"><Icon as={FaPlus} />Añadir Video
                                                      </Button>
                          
                          {/* Lista de videos — separados por tipo */}
                          {profileData.videos && profileData.videos.length > 0 && (() => {
                            const normalVideos = profileData.videos.filter(v => !isYouTubeShort(v.url));
                            const shorts       = profileData.videos.filter(v =>  isYouTubeShort(v.url));
                            const videoRowKey = (v, i, prefix) => {
                              if (v?.id != null && String(v.id) !== '') return `vid-${v.id}`;
                              return `${prefix}-${i}-${extractYouTubeId(v?.url) || 'u'}`;
                            };
                            const renderCard = (video, rowKey) => {
                              const videoId = extractYouTubeId(video.url);
                              const short = isYouTubeShort(video.url);
                              return (
                                <Box key={rowKey} rounded="lg" overflow="hidden" shadow="md" position="relative">
                                  {videoId ? (
                                    <Box position="relative" pb={short ? '177.78%' : '56.25%'}>
                                      <iframe
                                        src={`https://www.youtube.com/embed/${videoId}`}
                                        title={video.titulo}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                      />
                                      {short && (
                                        <Badge position="absolute" top={2} left={2} colorPalette="red" fontSize="2xs" rounded="md" px={1.5} zIndex={1} pointerEvents="none">
                                          Short
                                        </Badge>
                                      )}
                                    </Box>
                                  ) : (
                                    <Box h="150px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                      <Icon as={FaYoutube} w={12} h={12} color="gray.400" />
                                    </Box>
                                  )}
                                  <HStack p={2} bg="white" justify="space-between">
                                    <Text fontSize="sm" fontWeight="medium" isTruncated>{video.titulo}</Text>
                                    <IconButton
                                      size="xs"
                                      colorPalette="red"
                                      variant="ghost"
                                      onClick={() => handleRemoveVideo(video.id)}
                                      aria-label="Eliminar video"><Icon as={FaTrash} /></IconButton>
                                  </HStack>
                                </Box>
                              );
                            };
                            return (
                              <VStack gap={4} align="stretch" mt={4}>
                                {normalVideos.length > 0 && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Vídeos</Text>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                      {normalVideos.map((v, i) => renderCard(v, videoRowKey(v, i, 'n')))}
                                    </SimpleGrid>
                                  </Box>
                                )}
                                {shorts.length > 0 && (
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Shorts</Text>
                                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                                      {shorts.map((v, i) => renderCard(v, videoRowKey(v, i, 's')))}
                                    </SimpleGrid>
                                  </Box>
                                )}
                              </VStack>
                            );
                          })()}
                          
                          {(!profileData.videos || profileData.videos.length === 0) && (
                            <Box textAlign="center" py={6} color="gray.500">
                              <Icon as={FaYoutube} w={10} h={10} mb={2} />
                              <Text fontSize="sm">No hay videos añadidos</Text>
                              <Text fontSize="xs">Añade enlaces de YouTube para mostrar tus videos</Text>
                            </Box>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                  </VStack>
                </Card.Body>
              </Card.Root>
              </VStack>
            </TabPanel>

            {/* Avales de Confianza */}
            <TabPanel value="1" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              <VStack gap={{ base: 6, md: 8 }}>
                {/* Explicación del sistema de avales */}
                <Alert.Root status="info" rounded="xl" shadow="sm">
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="bold">¿Qué son los Avales de Confianza?</Text>
                    <Text fontSize="sm">
                      Los avales son reconocimientos de cofradías con las que has trabajado antes de unirte a Cadencya. 
                      Solicita avales a tus colaboradores habituales para aumentar tu credibilidad en la plataforma.
                    </Text>
                  </Box>
                </Alert.Root>

                {/* Solicitar aval */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <Heading size="lg">
                      <Icon as={FaPaperPlane} mr={3} color="green.600" />
                      Solicitar aval
                    </Heading>
                    <Text fontSize="md" color={textColor} mt={2}>
                      Envía una solicitud a una cofradía para que avale tu trayectoria profesional
                    </Text>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4}>
                      <Field.Root>
                        <Field.Label>Email de la cofradía</Field.Label>
                        <Input
                          type="email"
                          placeholder="cofradia@ejemplo.com"
                          value={avalRequestEmail}
                          onChange={(e) => setAvalRequestEmail(e.target.value)}
                          size="lg"
                        />
                        <Field.HelperText>
                          La cofradía recibirá un correo con instrucciones para otorgarte un aval
                        </Field.HelperText>
                      </Field.Root>
                      <Button
                        colorPalette="green"
                        size="lg"
                        onClick={handleRequestAval}
                        loading={isRequestingAval}
                        loadingText="Enviando..."
                        w="full"
                        disabled={!avalRequestEmail}><Icon as={FaPaperPlane} />Enviar solicitud
                                              </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Avales pendientes de aceptar */}
                {avalesPendientes.length > 0 && (
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaClock} mr={3} color="orange.600" />
                        Avales pendientes de aceptar ({avalesPendientes.length})
                      </Heading>
                      <Text fontSize="sm" color={textColor} mt={2}>
                        Cofradías que te han avalado - acepta o rechaza los avales
                      </Text>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={4} align="stretch">
                        {avalesPendientes.map((aval) => (
                          <Card.Root key={aval.id} bg="orange.50" shadow="sm" rounded="lg" borderLeft="4px solid" borderColor="orange.400">
                            <Card.Body>
                              <HStack gap={4} justify="space-between" flexWrap="wrap">
                                <HStack gap={3} flex={1}>
                                  <Avatar.Root size="md"><Avatar.Fallback name={aval.cofradia?.nombre} /><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
                                  <VStack gap={0} align="start" flex={1}>
                                    <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                      {aval.cofradia?.nombre}
                                    </Text>
                                    {aval.texto && (
                                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                                        "{aval.texto}"
                                      </Text>
                                    )}
                                    <Text fontSize="xs" color="gray.500">
                                      {new Date(aval.createdAt).toLocaleDateString('es-ES', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                  </VStack>
                                </HStack>
                                <HStack gap={2}>
                                  <Button
                                    size="sm"
                                    colorPalette="green"
                                    onClick={() => handleAcceptAval(aval.id)}
                                    loading={isProcessingAval === aval.id}
                                    rounded="full"><Icon as={FaCheck} />Aceptar
                                                                      </Button>
                                  <Button
                                    size="sm"
                                    colorPalette="red"
                                    variant="ghost"
                                    onClick={() => handleRejectAval(aval.id)}
                                    loading={isProcessingAval === aval.id}
                                    rounded="full"><Icon as={FaTimes} />Rechazar
                                                                      </Button>
                                </HStack>
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        ))}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                )}

                {/* Solicitudes de aval enviadas */}
                {solicitudesAval.filter(s => s.status === 'pending').length > 0 && (
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaEnvelope} mr={3} color="blue.600" />
                        Solicitudes enviadas ({solicitudesAval.filter(s => s.status === 'pending').length})
                      </Heading>
                      <Text fontSize="sm" color={textColor} mt={2}>
                        Cofradías a las que has enviado una solicitud de aval
                      </Text>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={3} align="stretch">
                        {solicitudesAval
                          .filter(s => s.status === 'pending')
                          .map((solicitud) => (
                            <Card.Root key={solicitud.id} bg="blue.50" shadow="sm" rounded="lg" borderLeft="4px solid" borderColor="blue.400">
                              <Card.Body>
                                <HStack gap={3}>
                                  <Icon as={FaEnvelope} color="blue.500" w={5} h={5} />
                                  <VStack gap={0} align="start" flex={1}>
                                    <Text fontWeight="bold" fontSize="sm">
                                      {solicitud.email}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                      Enviado el {new Date(solicitud.createdAt).toLocaleDateString('es-ES', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                  </VStack>
                                  <Badge colorPalette="blue" rounded="full" px={3}>Pendiente</Badge>
                                </HStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                )}

                {/* Avales recibidos y aceptados */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <VStack align="start" gap={2} w="full">
                      <HStack>
                        <Heading size="lg">
                          <Icon as={FaHandshake} mr={3} color="green.600" />
                          Mis avales de confianza
                        </Heading>
                        {avalesRecibidos.length > 0 && (
                          <Badge colorPalette="green" fontSize="md" px={3} py={1} rounded="full">
                            {avalesRecibidos.length}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="sm" color={textColor}>
                        Cofradías que avalan tu trayectoria profesional. Estos avales aparecen en tu perfil público.
                      </Text>
                    </VStack>
                  </Card.Header>
                  <Card.Body>
                    {avalesRecibidos.length === 0 ? (
                      <VStack gap={4} py={8}>
                        <Icon as={FaHandshake} w={16} h={16} color="gray.300" />
                        <Text fontSize="lg" color={textColor} textAlign="center">
                          Aún no tienes avales de confianza
                        </Text>
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Solicita avales a las cofradías con las que has trabajado para aumentar tu credibilidad
                        </Text>
                      </VStack>
                    ) : (
                      <VStack gap={4} align="stretch">
                        {avalesRecibidos.map((aval) => (
                          <Card.Root 
                            key={aval.id} 
                            bg="green.50" 
                            shadow="md" 
                            rounded="lg"
                            borderLeft="4px solid"
                            borderColor="green.400"
                          >
                            <Card.Body>
                              <HStack gap={4}>
                                <Avatar.Root size="lg"><Avatar.Fallback name={aval.cofradia?.nombre} /><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
                                <VStack gap={1} align="start" flex={1}>
                                  <HStack>
                                    <Text fontWeight="bold" fontSize="md">
                                      {aval.cofradia?.nombre}
                                    </Text>
                                    <Badge colorPalette="green" rounded="full">
                                      <Icon as={FaCheckCircle} mr={1} />
                                      Validado
                                    </Badge>
                                  </HStack>
                                  {aval.texto && (
                                    <Text fontSize="sm" color="gray.600" fontStyle="italic">
                                      "{aval.texto}"
                                    </Text>
                                  )}
                                  <Text fontSize="xs" color="gray.500">
                                    Avalado desde {new Date(aval.acceptedAt).toLocaleDateString('es-ES', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </Text>
                                </VStack>
                                <Icon as={FaAward} w={8} h={8} color="green.500" />
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>

                {/* Badges de gamificación */}
                {avalesRecibidos.length > 0 && (
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon as={FaAward} mr={3} color="yellow.500" />
                        Tu nivel de confianza
                      </Heading>
                    </Card.Header>
                    <Card.Body>
                      <HStack gap={6} justify="center" flexWrap="wrap">
                        <VStack>
                          <Box
                            p={4}
                            rounded="full"
                            bg={avalesRecibidos.length >= 1 ? "yellow.100" : "gray.100"}
                            borderWidth={3}
                            borderColor={avalesRecibidos.length >= 1 ? "yellow.400" : "gray.300"}
                          >
                            <Icon 
                              as={FaAward} 
                              w={8} h={8} 
                              color={avalesRecibidos.length >= 1 ? "yellow.600" : "gray.400"} 
                            />
                          </Box>
                          <Text fontWeight="bold" fontSize="sm" color={avalesRecibidos.length >= 1 ? "yellow.700" : "gray.400"}>
                            Entidad Avalada
                          </Text>
                          <Text fontSize="xs" color="gray.500">1+ aval</Text>
                        </VStack>
                        <VStack>
                          <Box
                            p={4}
                            rounded="full"
                            bg={avalesRecibidos.length >= 3 ? "gray.200" : "gray.100"}
                            borderWidth={3}
                            borderColor={avalesRecibidos.length >= 3 ? "gray.500" : "gray.300"}
                          >
                            <Icon 
                              as={FaAward} 
                              w={8} h={8} 
                              color={avalesRecibidos.length >= 3 ? "gray.600" : "gray.400"} 
                            />
                          </Box>
                          <Text fontWeight="bold" fontSize="sm" color={avalesRecibidos.length >= 3 ? "gray.700" : "gray.400"}>
                            Entidad de Confianza
                          </Text>
                          <Text fontSize="xs" color="gray.500">3+ avales</Text>
                        </VStack>
                        <VStack>
                          <Box
                            p={4}
                            rounded="full"
                            bg={avalesRecibidos.length >= 5 ? "yellow.300" : "gray.100"}
                            borderWidth={3}
                            borderColor={avalesRecibidos.length >= 5 ? "yellow.500" : "gray.300"}
                          >
                            <Icon 
                              as={FaAward} 
                              w={8} h={8} 
                              color={avalesRecibidos.length >= 5 ? "yellow.700" : "gray.400"} 
                            />
                          </Box>
                          <Text fontWeight="bold" fontSize="sm" color={avalesRecibidos.length >= 5 ? "yellow.800" : "gray.400"}>
                            Entidad Consolidada
                          </Text>
                          <Text fontSize="xs" color="gray.500">5+ avales</Text>
                        </VStack>
                      </HStack>
                    </Card.Body>
                  </Card.Root>
                )}
              </VStack>
            </TabPanel>

            {/* Reseñas */}
            <TabPanel value="2" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              {isMvpMode ? (
                <ComingSoonSection
                  title="Sistema de Reseñas - Próximamente"
                  message="El sistema de reseñas estará disponible muy pronto"
                  features={[
                    'Valoraciones automáticas tras cada evento',
                    'Puntuaciones en comunicación, puntualidad y calidad artística',
                    'Comentarios públicos y privados',
                    'Estadísticas de tu desempeño',
                  ]}
                />
              ) : (
              <VStack gap={{ base: 6, md: 8 }}>
                <Alert.Root status="info" rounded="xl" shadow="sm">
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="bold">Sistema de Reseñas</Text>
                    <Text fontSize="sm">
                      Cuando el evento termina (según su fecha y hora de fin), Cadencya te avisa por email y notificación para que puedas reseñar a la entidad: tu opinión ayuda a mejorar y posicionar su perfil en la comunidad.
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Las valoraciones que dejas son <strong>anónimas</strong>. En el perfil público de la entidad solo se muestra la <strong>puntuación total agregada</strong>; no se revela quién ha reseñado.
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Las reseñas que recibes de las entidades puedes publicarlas u ocultarlas en tu perfil y tu web; las publicadas son visibles para quien visite tu ficha.
                    </Text>
                  </Box>
                </Alert.Root>

                {/* Gestión de reseñas recibidas */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <VStack align="start" gap={2} w="full">
                      <Heading size="lg">
                        <Icon as={FaComments} mr={3} color="purple.600" />
                        Gestión de reseñas recibidas
                      </Heading>
                      <Text fontSize="sm" color={textColor}>
                        Gestiona la visibilidad de las reseñas en tu perfil público. Solo las reseñas publicadas serán visibles.
                      </Text>
                      {profileData.resenas.length > 0 && (
                        <HStack gap={4} pt={2} fontSize="sm" color={textColor}>
                          <HStack>
                            <Icon as={FaGlobe} color="green.500" />
                            <Text fontWeight="medium">
                              Publicadas: {profileData.resenas.filter(r => r.isPublished).length}
                            </Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaEyeSlash} color="orange.500" />
                            <Text fontWeight="medium">
                              Sin publicar: {profileData.resenas.filter(r => !r.isPublished).length}
                            </Text>
                          </HStack>
                        </HStack>
                      )}
                    </VStack>
                  </Card.Header>
                  <Card.Body>
                    {profileData.resenas.length === 0 ? (
                      <VStack gap={4} py={8}>
                        <Icon as={FaComments} w={16} h={16} color="gray.300" />
                        <Text fontSize="lg" color={textColor} textAlign="center">
                          Aún no has recibido reseñas
                        </Text>
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Las reseñas de las cofradías con las que trabajes aparecerán aquí
                        </Text>
                      </VStack>
                    ) : (
                      <VStack gap={4} align="stretch">
                        {profileData.resenas
                          .sort((a, b) => new Date(b.receivedDate) - new Date(a.receivedDate))
                          .map((resena) => (
                            <Card.Root 
                              key={resena.id} 
                              bg={resena.isPublished ? reviewCardBg : 'orange.50'} 
                              shadow="md" 
                              rounded="lg"
                              borderLeft="4px solid"
                              borderColor={resena.isPublished ? 'green.400' : 'orange.400'}
                            >
                              <Card.Body>
                                <VStack gap={4} align="start" w="full">
                                  {/* Header con información de la cofradía y estado */}
                                  <HStack gap={3} w="full" justify="space-between" align="start">
                                    <HStack gap={3} flex={1}>
                                      <Avatar.Root size={{ base: "sm", md: "md" }}><Avatar.Fallback name={resena.cofradiaName} /><Avatar.Image src={resena.cofradiaAvatar} /></Avatar.Root>
                                      <VStack gap={0} align="start" flex={1}>
                                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                          {resena.cofradiaName}
                                        </Text>
                                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                                          Recibida el {new Date(resena.receivedDate).toLocaleDateString('es-ES', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                          })}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                    
                                    {/* Badge de estado */}
                                    <Badge
                                      colorPalette={resena.isPublished ? 'green' : 'orange'}
                                      variant="subtle"
                                      px={3}
                                      py={1}
                                      rounded="full"
                                      fontSize="xs"
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Icon as={resena.isPublished ? FaGlobe : FaEyeSlash} />
                                      {resena.isPublished ? 'Publicada' : 'Sin publicar'}
                                    </Badge>
                                  </HStack>

                                  {/* Evento asociado */}
                                  {resena.eventoNombre && (
                                    <HStack gap={2} color="gray.600" fontSize="sm">
                                      <Icon as={FaCalendarAlt} />
                                      <Text>Evento: <strong>{resena.eventoNombre}</strong></Text>
                                      {resena.eventoFecha && (
                                        <Text>({new Date(resena.eventoFecha).toLocaleDateString('es-ES')})</Text>
                                      )}
                                    </HStack>
                                  )}
                                  
                                  {/* Valoraciones detalladas */}
                                  {resena.ratingGeneral && (
                                    <Box bg="gray.50" p={4} rounded="lg" w="full">
                                      <HStack gap={4} mb={3}>
                                      <HStack>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <Icon
                                            key={star}
                                            as={star <= Math.round(resena.averageRating) ? FaStar : FaRegStar}
                                            color={star <= Math.round(resena.averageRating) ? 'yellow.400' : 'gray.300'}
                                            w={5}
                                            h={5}
                                          />
                                        ))}
                                      </HStack>
                                      <Text fontWeight="bold" fontSize="lg">{resena.averageRating.toFixed(1)}/5</Text>
                                    </HStack>
                                    </Box>
                                  )}
                                  
                                  {/* Comentario de la reseña */}
                                  {resena.comment && (
                                    <Box
                                      bg={reviewCommentBg}
                                      p={4}
                                      rounded="md"
                                      w="full"
                                      borderLeft="3px solid"
                                      borderColor={resena.isPublished ? 'green.300' : 'orange.300'}
                                    >
                                      <Text fontSize="md" color={textColor} fontStyle="italic">
                                        "{resena.comment}"
                                      </Text>
                                    </Box>
                                  )}
                                  
                                  {/* Acciones */}
                                  <Separator />
                                  <HStack gap={3} w="full" justify="space-between">
                                    <HStack gap={2}>
                                      <Button
                                        size="sm"
                                        colorPalette={resena.isPublished ? 'orange' : 'green'}
                                        variant="solid"
                                        onClick={() => handleTogglePublishReview(resena.id)}
                                        rounded="full"
                                        fontWeight="600"><Icon as={resena.isPublished ? FaEyeSlash : FaGlobe} />{resena.isPublished ? 'Despublicar' : 'Publicar'}</Button>
                                      
                                      <Button
                                        size="sm"
                                        colorPalette="red"
                                        variant="ghost"
                                        onClick={() => openDeleteReviewModal(resena)}
                                        rounded="full"
                                        fontWeight="600"
                                        _hover={{ bg: 'red.50' }}><Icon as={FaTrash} />Eliminar
                                                                              </Button>
                                    </HStack>
                                    
                                    <Text fontSize="xs" color="gray.500">
                                      {resena.cofradiaEmail}
                                    </Text>
                                  </HStack>
                                  
                                  {/* Mensaje informativo si no está publicada */}
                                  {!resena.isPublished && (
                                    <Alert.Root status="warning" rounded="md" size="sm">
                                      <Alert.Indicator />
                                      <Text fontSize="xs">
                                        Esta reseña no es visible en tu perfil público ni en tu web pública. Puedes publicarla cuando quieras.
                                      </Text>
                                    </Alert.Root>
                                  )}
                                </VStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>
              </VStack>
              )}
            </TabPanel>

            {/* Tu Web - Micrositio */}
            <TabPanel value="3" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              {isMvpMode ? (
                <ComingSoonSection
                  title="Tu Web - Próximamente"
                  message="Pronto podrás tener tu propia web con el perfil de tu banda"
                  features={[
                    'URL personalizada con tu nombre de banda',
                    'Página pública con tu perfil, repertorio y galería',
                    'Estilos y colores personalizables',
                    'Reseñas y avales visibles para el público',
                  ]}
                />
              ) : (
              <Flex
                align="stretch"
                gap={{ base: 4, lg: 6 }}
                direction={{ base: 'column', lg: 'row' }}
                w="full"
                maxW="full"
              >
                <Box
                  flexShrink={0}
                  w={{ base: 'full', lg: '270px' }}
                  bg={cardBg}
                  rounded="2xl"
                  borderWidth="1px"
                  borderColor="gray.200"
                  p={2}
                  shadow="sm"
                  data-tour="tuweb-sidebar"
                >
                  <VStack gap={2} align="stretch">
                    {TU_WEB_SIDEBAR_ITEMS.map((item) => {
                      const active = tuWebMenu === item.id;
                      return (
                        <Button
                          key={item.id}
                          data-tour={`tuweb-${item.id}`}
                          variant={active ? 'solid' : 'ghost'}
                          colorPalette={active ? 'purple' : 'gray'}
                          justifyContent="flex-start"
                          h="auto"
                          py={3}
                          px={3}
                          rounded="xl"
                          onClick={() => setTuWebMenu(item.id)}
                          _hover={{ bg: active ? 'purple.700' : 'gray.100', transform: 'translateX(3px)' }}
                          transition="all 0.2s"
                        >
                          <HStack gap={3} w="full" align="center">
                            <Box
                              p={2}
                              rounded="lg"
                              bg={active ? 'whiteAlpha.300' : `${item.color}.50`}
                              flexShrink={0}
                            >
                              <Icon
                                as={item.icon}
                                color={active ? 'white' : `${item.color}.500`}
                                boxSize={4}
                              />
                            </Box>
                            <VStack gap={0} align="start" flex={1}>
                              <Text fontWeight="700" fontSize="sm" lineHeight="1.2">
                                {item.label}
                              </Text>
                              <Text
                                fontSize="xs"
                                fontWeight="normal"
                                opacity={active ? 0.9 : 0.65}
                                lineHeight="1.3"
                                textAlign="left"
                                whiteSpace="normal"
                              >
                                {item.desc}
                              </Text>
                            </VStack>
                          </HStack>
                        </Button>
                      );
                    })}
                  </VStack>
                </Box>

                <Box
                  flex="1 1 0%"
                  minW={0}
                  w="full"
                  maxW="full"
                  alignSelf="stretch"
                >
                {tuWebMenu === 'dominio' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" maxW="full">
                  <Card.Header>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'flex-start' }}
                      justify="space-between"
                      gap={4}
                      w="full"
                    >
                      <VStack align="start" gap={1} flex={1} minW={0}>
                        <Heading size="md">
                          <Icon as={FaGlobe} mr={2} color="purple.500" />
                          Dominio y URL
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Subdominio en Cadencya y, más adelante, dominio propio
                        </Text>
                      </VStack>
                      <Tooltip content={tuWebPreviewTooltipLabel} showArrow positioning={{
                        placement: "top"
                      }}>
                        <Button
                          variant="outline"
                          colorPalette="purple"
                          size="sm"
                          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                          flexShrink={0}
                          disabled={!currentSlug}
                          onClick={openTuWebMicrositePreview}><Icon as={FaEye} />Previsualizar mi web
                                                  </Button>
                      </Tooltip>
                    </Flex>
                  </Card.Header>
                  <Card.Body pt={0}>
                    <VStack gap={5} align="stretch">
                      <Box>
                        <Text fontWeight="600" mb={2} fontSize="sm">URL de tu web (subdominio Cadencya)</Text>
                        <HStack>
                          <InputGroup>
                            <Input
                              value={slugInput}
                              onChange={(e) => {
                                const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/--+/g, '-');
                                setSlugInput(val);
                                setSlugAvailable(null);
                                if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
                                if (val.length >= 3) {
                                  setSlugChecking(true);
                                  slugCheckTimeout.current = setTimeout(async () => {
                                    try {
                                      const res = await bandasAPI.checkSlug(val, profile?.id);
                                      // El servidor sanea el slug; mantener UI alineada.
                                      if (res.data?.slug && res.data.slug !== val) {
                                        setSlugInput(res.data.slug);
                                      }
                                      setSlugAvailable(res.data.available);
                                    } catch { setSlugAvailable(null); }
                                    finally { setSlugChecking(false); }
                                  }, 500);
                                } else { setSlugChecking(false); }
                              }}
                              placeholder="mi-banda"
                              fontSize="sm"
                              borderColor={slugAvailable === true ? 'green.400' : slugAvailable === false ? 'red.400' : 'gray.300'}
                            />
                            <InputRightAddon fontSize="sm" bg="gray.100">.{micrositeDomain}</InputRightAddon>
                          </InputGroup>
                          <Button colorPalette="purple" size="md" loading={slugSaving}
                            disabled={!slugInput || slugInput.length < 3 || slugAvailable === false || slugInput === currentSlug}
                            onClick={async () => {
                              try {
                                setSlugSaving(true);
                                // Revalidar en servidor para evitar carreras (otro usuario pudo reservarlo).
                                const check = await bandasAPI.checkSlug(slugInput, profile?.id);
                                const finalSlug = check.data?.slug || slugInput;
                                if (finalSlug !== slugInput) setSlugInput(finalSlug);
                                setSlugAvailable(check.data?.available ?? null);
                                if (check.data?.available === false) {
                                  toaster.create({
                                    title: 'Nombre no disponible',
                                    description: 'Ese subdominio ya está en uso. Prueba con otro nombre.',
                                    type: 'error',
                                    duration: 5000,
                                  });
                                  return;
                                }

                                await bandasAPI.updateSlug(finalSlug);
                                setCurrentSlug(finalSlug);
                                toaster.create({ title: 'Web actualizada', description: `Tu web esta disponible en ${finalSlug}.${micrositeDomain}`, type: 'success', duration: 4000 });
                              } catch (err) {
                                toaster.create({ title: 'Error', description: err.response?.data?.message || 'No se pudo actualizar el slug', type: 'error', duration: 4000 });
                              } finally { setSlugSaving(false); }
                            }}
                          >Guardar</Button>
                        </HStack>
                        {slugChecking && <Text fontSize="xs" color="gray.500" mt={1}>Comprobando disponibilidad...</Text>}
                        {slugAvailable === true && !slugChecking && <Text fontSize="xs" color="green.500" mt={1}>Disponible</Text>}
                        {slugAvailable === false && !slugChecking && <Text fontSize="xs" color="red.500" mt={1}>Este nombre ya esta en uso</Text>}
                        {slugInput && slugInput.length < 3 && <Text fontSize="xs" color="orange.500" mt={1}>Minimo 3 caracteres</Text>}
                      </Box>
                      {currentSlug && (
                        <Alert.Root status="success" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Tu web esta activa</Text>
                            <Link
                              href={`https://${currentSlug}.${micrositeDomain}`}
                              color="purple.600"
                              fontWeight="600"
                              fontSize="sm"
                              target='_blank'
                              rel='noopener noreferrer'>
                              {currentSlug}.{micrositeDomain} <Icon as={FaExternalLinkAlt} ml={1} boxSize={3} />
                            </Link>
                          </Box>
                        </Alert.Root>
                      )}
                      {!currentSlug && (
                        <Alert.Root status="info" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Activa tu micrositio</Text>
                            <Text fontSize="xs">Elige un nombre para tener tu propia web con tu perfil de banda.</Text>
                          </Box>
                        </Alert.Root>
                      )}
                      <Separator />
                      <Box>
                        <Text fontWeight="600" fontSize="sm" mb={2}>Dominio personalizado</Text>
                        <Alert.Root status="info" rounded="lg" size="sm">
                          <Alert.Indicator />
                          <Box>
                            <Text fontSize="sm" fontWeight="600">Próximamente</Text>
                            <Text fontSize="xs" mt={1}>
                              Podrás enlazar tu propio dominio (por ejemplo www.tubanda.es) apuntando a tu micrositio. Te avisaremos cuando esté disponible.
                            </Text>
                          </Box>
                        </Alert.Root>
                      </Box>
                    </VStack>
                  </Card.Body>
                </Card.Root>
                )}

                {tuWebMenu === 'publicar' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" maxW="full">
                  <Card.Header>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'flex-start' }}
                      justify="space-between"
                      gap={4}
                      w="full"
                    >
                      <VStack align="start" gap={1} flex={1} minW={0}>
                        <Heading size="md">
                          <Icon as={FaBullhorn} mr={2} color="orange.500" />
                          Publicar
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Guarda el estado actual de Estilos y Contenido y lo publica en tu subdominio (queda en el histórico de versiones).
                        </Text>
                      </VStack>
                      <HStack gap={2} flexShrink={0} flexWrap="wrap" justify={{ base: 'stretch', sm: 'flex-end' }}>
                        <Tooltip content={tuWebPreviewTooltipLabel} showArrow positioning={{
                          placement: "top"
                        }}>
                          <Button
                            variant="outline"
                            colorPalette="purple"
                            size="sm"
                            disabled={!currentSlug}
                            onClick={openTuWebMicrositePreview}><Icon as={FaEye} />Previsualizar borrador
                                                      </Button>
                        </Tooltip>
                        <Tooltip
                          content={currentSlug ? 'Abre tu web pública (versión publicada) en una nueva pestaña.' : 'Primero activa tu URL en la sección «Dominio».'}
                          showArrow
                          positioning={{
                            placement: "top"
                          }}
                        >
                          <Button
                            variant="solid"
                            colorPalette="orange"
                            size="sm"
                            disabled={!currentSlug}
                            onClick={() => window.open(`https://${currentSlug}.${micrositeDomain}`, '_blank', 'noopener,noreferrer')}><Icon as={FaExternalLinkAlt} />Abrir web publicada
                                                      </Button>
                        </Tooltip>
                      </HStack>
                    </Flex>
                  </Card.Header>
                  <Card.Body pt={0}>
                    <VStack gap={5} align="stretch">
                      {profile?.micrositePublishedAt ? (
                        <Alert.Root status="success" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Última publicación</Text>
                            <Text fontSize="xs">
                              {new Date(profile.micrositePublishedAt).toLocaleString()}
                            </Text>
                          </Box>
                        </Alert.Root>
                      ) : (
                        <Alert.Root status="warning" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Aún no has publicado tu web</Text>
                            <Text fontSize="xs">
                              Configura Estilos y Contenido si quieres, y pulsa «Publicar ahora». No hace falta un paso aparte de guardar borrador.
                            </Text>
                          </Box>
                        </Alert.Root>
                      )}

                      {!currentSlug ? (
                        <Alert.Root status="warning" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Primero activa tu URL</Text>
                            <Text fontSize="xs">
                              Necesitas elegir un subdominio en «Dominio» antes de poder publicar.
                            </Text>
                          </Box>
                        </Alert.Root>
                      ) : (
                        <Alert.Root status="info" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">Tu subdominio sirve la versión publicada</Text>
                            <Text fontSize="xs">
                              «Publicar ahora» guarda el estado actual (estilos y contenido) y lo hace público. En Contenido también puedes usar «Guardar cambios» solo para el borrador, sin publicar.
                            </Text>
                          </Box>
                        </Alert.Root>
                      )}

                      <Button
                        colorPalette="orange"
                        size="md"
                        disabled={!currentSlug}
                        onClick={async () => {
                          try {
                            setMsConfigSaving(true);
                            if (paletteSaveDebounceRef.current) {
                              clearTimeout(paletteSaveDebounceRef.current);
                              paletteSaveDebounceRef.current = null;
                            }
                            if (animSaveDebounceRef.current) {
                              clearTimeout(animSaveDebounceRef.current);
                              animSaveDebounceRef.current = null;
                            }
                            const draftPayload =
                              pendingPaletteSaveRef.current ||
                              pendingAnimMsConfigRef.current ||
                              msConfig;
                            pendingPaletteSaveRef.current = null;
                            pendingAnimMsConfigRef.current = null;
                            // Persistir borrador antes de publicar (primera vez o cambios sin «Guardar cambios»).
                            await bandasAPI.updateMicrositeConfig(draftPayload);
                            setMicrositeContenidoSavedDigest(
                              computeMicrositeContenidoDigest(draftPayload, profileDataRef.current, user?.userType),
                            );
                            await bandasAPI.publishMicrosite();
                            await refreshMicrositePublicaciones();
                            if (user?.userType) await loadProfile(user.userType);
                            toaster.create({
                              title: 'Publicado',
                              description: `Tu web publicada está disponible en ${currentSlug}.${micrositeDomain}`,
                              type: 'success',
                              duration: 4000,
                            });
                          } catch (err) {
                            toaster.create({
                              title: 'Error',
                              description: err.response?.data?.message || 'No se pudo publicar',
                              type: 'error',
                              duration: 5000,
                            });
                          } finally {
                            setMsConfigSaving(false);
                          }
                        }}
                        loading={msConfigSaving}><Icon as={FaBullhorn} />Publicar ahora
                                              </Button>

                      <Separator />

                      <Box>
                        <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                          <Text fontWeight="700" fontSize="sm">Histórico de publicaciones</Text>
                          <Button
                            size="sm"
                            variant="outline"
                            loading={micrositePublicacionesLoading}
                            onClick={refreshMicrositePublicaciones}
                          >
                            Actualizar
                          </Button>
                        </HStack>

                        {micrositePublicaciones.length === 0 ? (
                          <Text fontSize="sm" color="gray.600">
                            Aún no hay publicaciones.
                          </Text>
                        ) : (
                          <VStack align="stretch" gap={2}>
                            {micrositePublicaciones.map((p) => (
                              <HStack
                                key={p.id}
                                justify="space-between"
                                align="center"
                                p={3}
                                bg="white"
                                rounded="lg"
                                borderWidth="1px"
                                borderColor="gray.100"
                                flexWrap="wrap"
                                gap={2}
                              >
                                <Box minW={0} flex={1}>
                                  <Text fontWeight="600" fontSize="sm">
                                    {new Date(p.createdAt).toLocaleString()}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                    {p.slug ? `Slug: ${p.slug}` : 'Slug: (sin slug)'}
                                  </Text>
                                </Box>
                                <HStack gap={2} flexShrink={0} flexWrap="wrap">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorPalette="purple"
                                    onClick={async () => {
                                      try {
                                        setMsConfigSaving(true);
                                        await bandasAPI.restoreMicrositePublicacionDraft(p.id);
                                        if (user?.userType) await loadProfile(user.userType);
                                        toaster.create({
                                          title: 'Borrador restaurado',
                                          description: 'Se restauró esta versión como borrador. Abre “Previsualizar borrador” para verla y publica cuando quieras.',
                                          type: 'success',
                                          duration: 4500,
                                        });
                                      } catch (err) {
                                        toaster.create({
                                          title: 'Error',
                                          description: err.response?.data?.message || 'No se pudo restaurar el borrador',
                                          type: 'error',
                                          duration: 5000,
                                        });
                                      } finally {
                                        setMsConfigSaving(false);
                                      }
                                    }}
                                    loading={msConfigSaving}
                                  >
                                    Restaurar como borrador
                                  </Button>
                                </HStack>
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </Box>
                    </VStack>
                  </Card.Body>
                </Card.Root>
                )}

                {tuWebMenu === 'estilos' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" maxW="full">
                  <Card.Header>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'flex-start' }}
                      justify="space-between"
                      gap={3}
                      w="full"
                    >
                      <VStack align="start" gap={1} flex={1} minW={0}>
                        <Heading size="md">
                          <Icon as={FaPalette} mr={2} color="purple.500" />
                          Estilo de tu web
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Elige el diseno visual de tu micrositio
                        </Text>
                      </VStack>
                      <Tooltip content={tuWebPreviewTooltipLabel} showArrow positioning={{
                        placement: "top"
                      }}>
                        <Button
                          variant="outline"
                          colorPalette="purple"
                          size="sm"
                          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                          flexShrink={0}
                          disabled={!currentSlug}
                          onClick={openTuWebMicrositePreview}><Icon as={FaEye} />Previsualizar mi web
                                                  </Button>
                      </Tooltip>
                    </Flex>
                  </Card.Header>
                  <Card.Body pt={0}>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                      {[
                        {
                          id: 'vortex',
                          name: 'Vortex',
                          desc: 'Inmersivo y oscuro: gradientes fluidos, capas luminosas y sensación de movimiento',
                          icon: '🌀',
                        },
                        {
                          id: 'glass',
                          name: 'Glass',
                          desc: 'Cristal líquido: transparencias, blur y bloques flotantes de estilo futurista',
                          icon: '🧊',
                        },
                        {
                          id: 'kinetic',
                          name: 'Kinetic',
                          desc: 'Scroll narrativo de alto contraste con bloques enérgicos y llamadas visuales',
                          icon: '🎯',
                        },
                        {
                          id: 'moderno',
                          name: 'Moderno',
                          desc: 'Hero a pantalla completa, navbar con transparencia, secciones amplias',
                          icon: '🚀',
                        },
                        {
                          id: 'clasico',
                          name: 'Clasico',
                          desc: 'Header compacto, tarjetas con bordes, tipografia serif, estilo institucional',
                          icon: '🏛️',
                        },
                        {
                          id: 'elegante',
                          name: 'Elegante',
                          desc: 'Hero dividido, tipografia fina, galeria masonry, estilo portfolio minimalista',
                          icon: '✨',
                        },
                        {
                          id: 'aurora',
                          name: 'Aurora',
                          desc: 'Oscuro tipo producto digital: mallas de color, cristal y acento luminoso',
                          icon: '🌌',
                        },
                        {
                          id: 'editorial',
                          name: 'Editorial',
                          desc: 'Portada de revista: hero en dos columnas, mucho aire y barra de acento plana',
                          icon: '📰',
                        },
                        {
                          id: 'pulso',
                          name: 'Pulso',
                          desc: 'Neo-brutalista: tipografia en mayusculas, bordes gruesos y sombras duras',
                          icon: '⚡',
                        },
                      ].map((layout) => {
                        const isSelected = (msConfig.tema || 'moderno') === layout.id;
                        const pal = getMicrositePalette(msConfig, layout.id);
                        return (
                          <Box
                            key={layout.id}
                            p={4} rounded="xl" cursor="pointer"
                            border="2px solid"
                            borderColor={isSelected ? 'purple.500' : 'gray.200'}
                            bg={isSelected ? 'purple.50' : 'white'}
                            _hover={{ borderColor: isSelected ? 'purple.500' : 'gray.400', shadow: 'md' }}
                            transition="all 0.2s"
                            onClick={async () => {
                              cancelPaletteSaveDebounce();
                              let nextForApi;
                              setMsConfig((prev) => {
                                nextForApi = { ...prev, tema: layout.id };
                                return nextForApi;
                              });
                              try {
                                await bandasAPI.updateMicrositeConfig(nextForApi);
                                toaster.create({ title: 'Estilo actualizado', description: `Tu web ahora usa el estilo ${layout.name}`, type: 'success', duration: 3000 });
                              } catch (err) {
                                toaster.create({ title: 'Error', description: 'No se pudo cambiar el estilo', type: 'error', duration: 4000 });
                              }
                            }}
                          >
                            <VStack gap={3} align="start">
                              <HStack justify="space-between" w="full">
                                <Text fontSize="2xl">{layout.icon}</Text>
                                {isSelected && <Icon as={FaCheckCircle} color="purple.500" boxSize={5} />}
                              </HStack>
                              <Box w="full" h="6px" rounded="full" overflow="hidden" display="flex">
                                <Box flex={1} bg={pal.primario} />
                                <Box flex={1} bg={pal.secundario} />
                                <Box flex={1} bg={pal.fondo} />
                              </Box>
                              <VStack gap={0} align="start">
                                <Text fontWeight="700" fontSize="sm" color={isSelected ? 'purple.700' : 'gray.800'}>{layout.name}</Text>
                                <Text fontSize="xs" color="gray.500" lineHeight="1.4">{layout.desc}</Text>
                              </VStack>
                              <Box w="full" pt={1} maxW="100%" overflow="hidden" onClick={(e) => e.stopPropagation()}>
                                <HStack justify="space-between" align="center" w="full" mb={1.5} gap={2}>
                                  <Text
                                    fontSize="10px"
                                    color="gray.500"
                                    fontWeight="600"
                                    textTransform="uppercase"
                                    letterSpacing="0.04em"
                                    lineHeight="1.3"
                                    flex={1}
                                    minW={0}
                                  >
                                    Colores de estilo
                                  </Text>
                                  <Tooltip content="Restaurar colores predeterminados de este estilo" showArrow positioning={{
                                    placement: "top"
                                  }}>
                                    <IconButton
                                      type="button"
                                      aria-label="Restaurar colores predeterminados de este estilo"
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="gray"
                                      flexShrink={0}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        persistPaletteReset(layout.id);
                                      }}><Icon as={FaUndo} boxSize={3.5} /></IconButton>
                                  </Tooltip>
                                </HStack>
                                <HStack gap={2} w="full" align="flex-end">
                                  {[
                                    { field: 'primario', label: 'Acento' },
                                    { field: 'secundario', label: 'Contraste' },
                                    { field: 'fondo', label: 'Fondo' },
                                  ].map(({ field, label }) => (
                                    <Field.Root key={field} flex={1}>
                                      <Field.Label fontSize="9px" mb={0.5} color="gray.500" fontWeight="500">{label}</Field.Label>
                                      <Input
                                        type="color"
                                        value={pal[field]}
                                        onChange={(e) => persistPaletteColor(layout.id, field, e.target.value)}
                                        h="36px"
                                        p={0}
                                        borderRadius="md"
                                        cursor="pointer"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                      />
                                    </Field.Root>
                                  ))}
                                </HStack>
                              </Box>
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>

                    <Separator my={6} />

                    <VStack align="start" gap={3}>
                      <HStack><Icon as={FaEdit} color="purple.500" /><Text fontWeight="600" fontSize="sm">Tipografia</Text></HStack>
                      <Text fontSize="xs" color="gray.500">Elige la fuente para los titulos y textos de tu web</Text>
                      <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3} w="full">
                        {FONT_OPTIONS.map((font) => {
                          const isSelected = (msConfig.tipografia || 'default') === font.id;
                          return (
                            <Box
                              key={font.id}
                              p={3} rounded="lg" cursor="pointer"
                              border="2px solid"
                              borderColor={isSelected ? 'purple.500' : 'gray.200'}
                              bg={isSelected ? 'purple.50' : 'white'}
                              _hover={{ borderColor: isSelected ? 'purple.500' : 'gray.400' }}
                              transition="all 0.2s"
                              onClick={async () => {
                                cancelPaletteSaveDebounce();
                                let nextForApi;
                                setMsConfig((prev) => {
                                  nextForApi = { ...prev, tipografia: font.id };
                                  return nextForApi;
                                });
                                try {
                                  await bandasAPI.updateMicrositeConfig(nextForApi);
                                  toaster.create({ title: 'Tipografia actualizada', type: 'success', duration: 2000 });
                                } catch (err) {
                                  toaster.create({ title: 'Error', description: 'No se pudo cambiar la tipografia', type: 'error', duration: 3000 });
                                }
                              }}
                            >
                              <VStack gap={1} align="center">
                                <Text
                                  fontSize="lg" fontWeight="600" color={isSelected ? 'purple.700' : 'gray.700'}
                                  fontFamily={font.family || undefined}
                                  lineHeight="1.2" textAlign="center" lineClamp={1}
                                >
                                  Aa
                                </Text>
                                <Text fontSize="xs" color={isSelected ? 'purple.600' : 'gray.500'} fontWeight={isSelected ? '600' : '400'} textAlign="center" lineClamp={1}>
                                  {font.name}
                                </Text>
                              </VStack>
                            </Box>
                          );
                        })}
                      </SimpleGrid>
                    </VStack>

                    <Separator my={6} />

                    <VStack align="start" gap={4} w="full">
                      <HStack gap={2}>
                        <Icon as={FaBolt} color="purple.500" />
                        <Text fontWeight="600" fontSize="sm">Animaciones</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        Controla si tu web pública anima las secciones al hacer scroll, qué tan marcado es el movimiento y la rapidez de la transición.
                      </Text>
                      {(() => {
                        const anim = normalizeAnimacionesMicrositio(msConfig.animacionesMicrositio);
                        return (
                          <>
                            <Field.Root display="flex" alignItems="center" justifyContent="space-between" w="full" gap={4}>
                              <Box>
                                <Field.Label htmlFor="ms-anim-enabled" mb={0} fontWeight="600" fontSize="sm">
                                  Animaciones al scroll
                                </Field.Label>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  Desactivalo para una web estática (sin entradas al pasar por cada sección).
                                </Text>
                              </Box>
                              <Switch
                                id="ms-anim-enabled"
                                colorPalette="purple"
                                checked={anim.enabled}
                                onChange={(e) => patchMicrositeAnimaciones({ enabled: e.target.checked })}
                              />
                            </Field.Root>

                            <Field.Root disabled={!anim.enabled}>
                              <Field.Label fontSize="sm" fontWeight="600" mb={2}>Tipo de animación</Field.Label>
                              <RadioGroup.Root
                                disabled={!anim.enabled}
                                value={anim.modo}
                                onValueChange={(details) =>
                                  patchMicrositeAnimaciones({ modo: details?.value ?? details })
                                }>
                                <Stack gap={2}>
                                  <RadioGroup.Item value="vertical" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Vertical (aparece desde arriba o abajo)
                                                                        </RadioGroup.ItemText></RadioGroup.Item>
                                  <RadioGroup.Item value="horizontal" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Horizontal (deslizamiento lateral)
                                                                        </RadioGroup.ItemText></RadioGroup.Item>
                                  <RadioGroup.Item value="opacidad" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Solo opacidad (sin desplazamiento)
                                                                        </RadioGroup.ItemText></RadioGroup.Item>
                                </Stack>
                              </RadioGroup.Root>
                            </Field.Root>

                            <Field.Root disabled={!anim.enabled} w="full">
                              <Flex justify="space-between" align="center" mb={2} gap={3} wrap="wrap">
                                <Field.Label fontSize="sm" fontWeight="600" mb={0}>Intensidad</Field.Label>
                                <Text fontSize="xs" color="gray.500">Menos movimiento — Más movimiento</Text>
                              </Flex>
                              <SliderRoot
                                disabled={!anim.enabled}
                                aria-label="Intensidad de animaciones del micrositio"
                                min={0}
                                max={100}
                                step={1}
                                value={anim.intensidad}
                                onValueChange={(v) => patchMicrositeAnimaciones({ intensidad: v })}
                                colorPalette="purple"
                              >
                                <SliderTrack bg="gray.200">
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb boxSize={5} />
                              </SliderRoot>
                              <Text fontSize="xs" color="gray.500" mt={1}>Valor: {anim.intensidad}</Text>
                            </Field.Root>

                            <Field.Root disabled={!anim.enabled} w="full">
                              <Flex justify="space-between" align="center" mb={2} gap={3} wrap="wrap">
                                <Field.Label fontSize="sm" fontWeight="600" mb={0}>Rapidez de la transición</Field.Label>
                                <Text fontSize="xs" color="gray.500">Más lenta — Más rápida</Text>
                              </Flex>
                              <SliderRoot
                                disabled={!anim.enabled}
                                aria-label="Rapidez de las animaciones al scroll del micrositio"
                                min={0}
                                max={100}
                                step={1}
                                value={anim.rapidez}
                                onValueChange={(v) => patchMicrositeAnimaciones({ rapidez: v })}
                                colorPalette="purple"
                              >
                                <SliderTrack bg="gray.200">
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb boxSize={5} />
                              </SliderRoot>
                              <Text fontSize="xs" color="gray.500" mt={1}>Valor: {anim.rapidez}</Text>
                            </Field.Root>
                          </>
                        );
                      })()}
                    </VStack>
                  </Card.Body>
                </Card.Root>
                )}

                {tuWebMenu === 'secciones' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" maxW="full">
                  <Card.Header>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'flex-start' }}
                      justify="space-between"
                      gap={3}
                      w="full"
                    >
                      <VStack align="start" gap={1} flex={1} minW={0}>
                        <Heading size="md">
                          <Icon as={FaList} mr={2} color="purple.500" />
                          Secciones de tu web
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Activa o desactiva bloques en la web pública. La cabecera (inicio) siempre va la primera. Los cambios se guardan al instante.
                        </Text>
                      </VStack>
                      <Tooltip content={tuWebPreviewTooltipLabel} showArrow positioning={{
                        placement: "top"
                      }}>
                        <Button
                          variant="outline"
                          colorPalette="purple"
                          size="sm"
                          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                          flexShrink={0}
                          disabled={!currentSlug}
                          onClick={openTuWebMicrositePreview}><Icon as={FaEye} />Previsualizar mi web
                                                  </Button>
                      </Tooltip>
                    </Flex>
                  </Card.Header>
                  <Card.Body pt={0}>
                    <Alert.Root status="info" variant="subtle" colorPalette="purple" rounded="lg" mb={4}>
                      <Alert.Indicator />
                      <Box>
                        <Text fontSize="sm" fontWeight="700">
                          Orden de las secciones
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Puedes modificar el orden en que aparecen en tu web (menú, bloques y pestaña Contenido): arrastra cada fila por el botón con rayas verticales a la izquierda. La cabecera no se mueve.
                        </Text>
                      </Box>
                    </Alert.Root>
                    <DndContext
                      sensors={micrositeSeccionesSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleMicrositeSeccionesDragEnd}
                    >
                      <SortableContext items={tuWebMicrositeBodyOrderIds} strategy={verticalListSortingStrategy}>
                        <VStack gap={4} align="stretch">
                          {tuWebMicrositeBodyOrderIds.map((secId) => {
                            const sec = MICROSITE_SECTION_CATALOG.find((s) => s.id === secId);
                            if (!sec) return null;
                            const visible = !isMicrositeSectionHidden(sec.id, msConfig);
                            return (
                              <SortableTuWebMicrositeSeccionRow
                                key={sec.id}
                                sec={sec}
                                visible={visible}
                                onVisibleChange={persistMicrositeSectionVisible}
                              />
                            );
                          })}
                        </VStack>
                      </SortableContext>
                    </DndContext>
                  </Card.Body>
                </Card.Root>
                )}

                {tuWebMenu === 'contenido' && (
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" maxW="full" overflow="visible">
                  <Card.Header
                    borderBottomWidth="1px"
                    borderColor="gray.100"
                    roundedTop="2xl"
                  >
                    <Flex
                      justify="space-between"
                      align={{ base: 'stretch', md: 'flex-start' }}
                      direction={{ base: 'column', md: 'row' }}
                      gap={3}
                      w="full"
                    >
                      <VStack align="start" gap={1} flex={1} minW={0}>
                        <Heading size="md">
                          <Icon as={FaEdit} mr={2} color="purple.500" />
                          Contenido exclusivo de tu web
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Elige una sección a la izquierda (o en el desplegable en móvil), edita su bloque y guarda cuando aparezca el aviso inferior. Mismo conjunto y orden que en «Secciones».
                        </Text>
                      </VStack>
                      <HStack gap={2} flexShrink={0} flexWrap="wrap" justify={{ base: 'stretch', md: 'flex-end' }}>
                        <Tooltip content={tuWebPreviewTooltipLabel} showArrow positioning={{
                          placement: "top"
                        }}>
                          <Button
                            variant="outline"
                            colorPalette="purple"
                            size="sm"
                            disabled={!currentSlug}
                            onClick={openTuWebMicrositePreview}><Icon as={FaEye} />Previsualizar mi web
                                                      </Button>
                        </Tooltip>
                      </HStack>
                    </Flex>
                  </Card.Header>
                  <Card.Body pt={0} pb={4}>
                    <VStack gap={5} align="stretch">
                      <Alert.Root status="info" rounded="lg" size="sm">
                        <Alert.Indicator />
                        <Text fontSize="xs">
                          Solo ves las secciones activas en «Secciones». En el menú aparece «Sin guardar» junto a cada sección con cambios pendientes. La barra naranja del perfil no guarda este bloque: cuando cambies textos u opciones que afecten a la web, pulsa{' '}
                          <Text as="span" fontWeight="600">Guardar cambios</Text> en la barra inferior (solo si hay pendientes). Biografía y especialidades de «Sobre nosotros» siguen guardándose con la barra naranja del perfil.
                        </Text>
                      </Alert.Root>

                      <SimpleGrid columns={{ base: 1, lg: 12 }} gap={{ base: 4, lg: 6 }} alignItems="start" w="full">
                        <GridItem colSpan={{ base: 1, lg: 3 }}>
                          <Field.Root display={{ base: 'block', lg: 'none' }} mb={1}>
                            <Field.Label fontSize="sm" fontWeight="600">Sección a editar</Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                value={tuWebContenidoPanelId}
                                onChange={(e) => setTuWebContenidoPanelId(e.target.value)}
                                size="sm"
                                borderRadius="lg"
                                bg="white">
                                {tuWebContenidoNavItems.map((item) => {
                                  const pending =
                                    item.id !== TU_WEB_CONTENIDO_CABECERA_ID &&
                                    tuWebContenidoUnsavedSectionIds.has(item.id);
                                  return (
                                    <option key={item.id} value={item.id}>
                                      {item.label}{pending ? ' · sin guardar' : ''}
                                    </option>
                                  );
                                })}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2} display={{ base: 'none', lg: 'block' }}>
                            Secciones
                          </Text>
                          <VStack gap={1} align="stretch" display={{ base: 'none', lg: 'flex' }}>
                            {tuWebContenidoNavItems.map((item) => {
                              const active = tuWebContenidoPanelId === item.id;
                              const ItemIcon =
                                item.id === TU_WEB_CONTENIDO_CABECERA_ID
                                  ? FaImages
                                  : (TU_WEB_CONTENIDO_SECTION_ICONS[item.id] || FaInfoCircle);
                              const scheme = item.color || 'purple';
                              const pending =
                                item.id !== TU_WEB_CONTENIDO_CABECERA_ID &&
                                tuWebContenidoUnsavedSectionIds.has(item.id);
                              return (
                                <Button
                                  key={item.id}
                                  size="sm"
                                  variant={active ? 'solid' : 'ghost'}
                                  colorPalette={active ? scheme : 'gray'}
                                  onClick={() => setTuWebContenidoPanelId(item.id)}
                                  h="auto"
                                  py={2}
                                  px={3}
                                  w="full"
                                >
                                  <HStack gap={2} w="full" justify="flex-start" align="center">
                                    <Icon as={ItemIcon} flexShrink={0} boxSize={4} />
                                    <Text
                                      as="span"
                                      flex={1}
                                      textAlign="left"
                                      whiteSpace="normal"
                                      fontWeight={active ? '700' : '500'}
                                      lineHeight="short"
                                    >
                                      {item.label}
                                    </Text>
                                    {pending && (
                                      <Badge colorPalette="orange" fontSize="2xs" px={1.5} py={0.5} flexShrink={0}>
                                        Sin guardar
                                      </Badge>
                                    )}
                                  </HStack>
                                </Button>
                              );
                            })}
                          </VStack>
                        </GridItem>
                        <GridItem colSpan={{ base: 1, lg: 9 }}>
                          <VStack gap={6} align="stretch" w="full" minH="120px">

                      {tuWebContenidoPanelId === TU_WEB_CONTENIDO_CABECERA_ID && isBandaAccountUserType(user?.userType) && (
                        <Box rounded="xl" borderWidth="1px" borderColor="gray.200" p={4} bg="gray.50">
                          <HStack mb={3} gap={3} align="start">
                            <Icon as={FaImages} color="purple.500" boxSize={5} mt={0.5} flexShrink={0} />
                            <VStack align="start" gap={0} flex={1} minW={0}>
                              <Text fontWeight="700" fontSize="sm">Configura la cabecera de tu web</Text>
                              <Text fontSize="xs" color="gray.600">
                                Logo, imagen de portada del micrositio y qué datos se muestran en el bloque principal (hero). El logo es el mismo que en «Perfil público». La imagen de cabecera no sustituye al fondo de la tarjeta de tu perfil en la comunidad.
                              </Text>
                            </VStack>
                          </HStack>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} alignItems="start">
                            <VStack align="stretch" gap={3}>
                              <Text fontWeight="600" fontSize="sm">Logo de la banda</Text>
                              <HStack gap={4} align="center" flexWrap="wrap">
                                <Avatar.Root size="xl" bg="purple.100" color="purple.700"><Avatar.Fallback name={profileData.nombre} /><Avatar.Image src={profileData.avatar} /></Avatar.Root>
                                <VStack align="start" gap={2}>
                                  <HStack gap={2} flexWrap="wrap">
                                    <Button
                                      size="sm"
                                      colorPalette="purple"
                                      variant="outline"
                                      loading={isUploading}
                                      disabled={verificationStatus === 'pending'}
                                      onClick={() => tuWebLogoInputRef.current?.click()}><Icon as={FaCamera} />{profileData.avatar ? 'Cambiar logo' : 'Subir logo'}</Button>
                                    {profileData.avatar && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        colorPalette="purple"
                                        disabled={verificationStatus === 'pending'}
                                        onClick={handleEditCurrentAvatar}><Icon as={FaCrop} />Recortar
                                                                              </Button>
                                    )}
                                  </HStack>
                                  <Text fontSize="xs" color="gray.500">
                                    Cuadrado, visible en la portada y en la navegación. Mismo archivo que en Perfil público.
                                  </Text>
                                </VStack>
                              </HStack>
                              <input
                                ref={tuWebLogoInputRef}
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleAvatarUpload}
                              />
                            </VStack>

                            <VStack align="stretch" gap={3}>
                              <Text fontWeight="600" fontSize="sm">Imagen de cabecera (hero)</Text>
                              <Box
                                w="full"
                                h="120px"
                                rounded="lg"
                                borderWidth="1px"
                                borderColor="gray.200"
                                overflow="hidden"
                                bg="gray.100"
                                backgroundSize="cover"
                                backgroundPosition="center"
                                backgroundImage={profileData.imagenCabeceraWeb ? `url(${profileData.imagenCabeceraWeb})` : undefined}
                              />
                              <Text fontSize="xs" color="gray.600">
                                Recomendado <Text as="span" fontWeight="600">1920 × 1080 px</Text> (relación 16:9), JPG o WebP, máximo 5 MB. Se recorta al subir a 16:9. Si no subes ninguna, en la web se usará el logo ampliado como fondo.
                              </Text>
                              <HStack gap={2} flexWrap="wrap">
                                <Button
                                  size="sm"
                                  colorPalette="purple"
                                  variant="outline"
                                  loading={isUploading}
                                  disabled={verificationStatus === 'pending'}
                                  onClick={() => imagenCabeceraWebInputRef.current?.click()}><Icon as={FaCamera} />{profileData.imagenCabeceraWeb ? 'Cambiar imagen' : 'Subir imagen'}</Button>
                                {profileData.imagenCabeceraWeb && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      colorPalette="purple"
                                      disabled={verificationStatus === 'pending'}
                                      onClick={handleEditCurrentImagenCabeceraWeb}><Icon as={FaCrop} />Recortar
                                                                          </Button>
                                    <Button
                                      size="sm"
                                      colorPalette="red"
                                      variant="ghost"
                                      loading={isUploading}
                                      disabled={verificationStatus === 'pending'}
                                      onClick={handleRemoveImagenCabeceraWeb}><Icon as={FaTimes} />Quitar
                                                                          </Button>
                                  </>
                                )}
                              </HStack>
                              <input
                                ref={imagenCabeceraWebInputRef}
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImagenCabeceraWebUpload}
                              />
                            </VStack>
                          </SimpleGrid>

                          <Separator my={6} borderColor="gray.200" />

                          <Text fontWeight="600" fontSize="sm" mb={1}>
                            Información visible en el hero
                          </Text>
                          <Text fontSize="xs" color="gray.600" mb={3}>
                            Activa o desactiva cada elemento en la portada de tu web pública. Los cambios se guardan al instante.
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                            {TU_WEB_HERO_CAMPOS.map(({ key, label }) => {
                              const checked = normalizeCabeceraHero(msConfig.cabeceraHero)[key];
                              return (
                                <Field.Root
                                  key={key}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  rounded="md"
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                  bg="white"
                                  px={3}
                                  py={2}
                                >
                                  <Field.Label htmlFor={`tuweb-hero-${key}`} mb={0} fontSize="sm" fontWeight="500" cursor="pointer" flex={1}>
                                    {label}
                                  </Field.Label>
                                  <Switch
                                    id={`tuweb-hero-${key}`}
                                    colorPalette="purple"
                                    checked={checked}
                                    onChange={(e) => persistCabeceraHeroCampo(key, e.target.checked)}
                                  />
                                </Field.Root>
                              );
                            })}
                          </SimpleGrid>
                        </Box>
                      )}

                      {tuWebContenidoSectionsVisibles.length === 0 && (
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          No hay secciones visibles. Activa al menos una en «Secciones» para editar su contenido aquí.
                        </Text>
                      )}

                      {tuWebContenidoSectionsVisibles.map((sec) => {
                        if (sec.id !== tuWebContenidoPanelId) return null;
                        const SecIcon = TU_WEB_CONTENIDO_SECTION_ICONS[sec.id] || FaInfoCircle;
                        return (
                          <React.Fragment key={sec.id}>
                            <Box rounded="xl" borderWidth="1px" borderColor="gray.200" p={4} bg="gray.50">
                              <HStack mb={3} gap={3} align="start">
                                <Icon as={SecIcon} color={`${sec.color}.500`} boxSize={5} mt={0.5} flexShrink={0} />
                                <VStack align="start" gap={0} flex={1} minW={0}>
                                  <Text fontWeight="700" fontSize="sm">{sec.label}</Text>
                                  <Text fontSize="xs" color="gray.600">{sec.desc}</Text>
                                </VStack>
                              </HStack>

                              {sec.id === 'nosotros' && (
                                <VStack gap={4} align="stretch">
                                  <Text fontSize="xs" color="gray.600">
                                    Misma información que en <Text as="span" fontWeight="600">Perfil público</Text>. Cuando termines, guarda con la barra naranja <Text as="span" fontWeight="600">Guardar cambios del perfil</Text>.
                                  </Text>
                                  <Field.Root>
                                    <Field.Label fontSize="sm">Biografía (descripción pública)</Field.Label>
                                    <Textarea
                                      value={profileData.bio}
                                      onChange={(e) => handleInputChange('bio', e.target.value)}
                                      placeholder="Describe tu banda, estilo musical, experiencia..."
                                      rows={4}
                                      resize="vertical"
                                      fontSize="sm"
                                    />
                                    <Field.HelperText fontSize="xs">
                                      Se muestra en tu web y en el perfil público de la comunidad.
                                    </Field.HelperText>
                                  </Field.Root>
                                  <Field.Root
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    rounded="md"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    bg="white"
                                    px={3}
                                    py={2}
                                  >
                                    <Field.Label htmlFor="tuweb-especialidades-nosotros" mb={0} fontSize="sm" fontWeight="500" cursor="pointer" flex={1}>
                                      Mostrar especialidades musicales en la web
                                    </Field.Label>
                                    <Switch
                                      id="tuweb-especialidades-nosotros"
                                      colorPalette="purple"
                                      checked={readMostrarEspecialidadesNosotros(msConfig)}
                                      onChange={(e) => persistMostrarEspecialidadesNosotros(e.target.checked)}
                                    />
                                  </Field.Root>
                                  {readMostrarEspecialidadesNosotros(msConfig) && (
                                    <Box bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3}>
                                      <SkillsEditor
                                        title="Especialidades musicales"
                                        helperText="Estilos y tipos de evento en los que tenéis experiencia (máx. 15). Se guardan con la barra naranja del perfil."
                                        placeholder="Ej: Marchas procesionales, Música sacra..."
                                        skills={profileData.especialidades}
                                        onSkillsChange={(newSkills) => handleInputChange('especialidades', newSkills)}
                                        suggestions={especialidadesSugeridas}
                                        maxSkills={15}
                                      />
                                    </Box>
                                  )}
                                  <Text fontSize="xs" color="gray.500">
                                    El interruptor anterior solo controla si las especialidades se listan en «Sobre nosotros» del micrositio; los datos siguen siendo los del perfil.
                                  </Text>
                                  <Field.Root
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    rounded="md"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    bg="white"
                                    px={3}
                                    py={2}
                                  >
                                    <Field.Label htmlFor="tuweb-plantilla-musicos" mb={0} fontSize="sm" fontWeight="500" cursor="pointer" flex={1}>
                                      Mostrar plantilla de músicos en la web
                                    </Field.Label>
                                    <Switch
                                      id="tuweb-plantilla-musicos"
                                      colorPalette="purple"
                                      checked={readMostrarPlantillaMusicosNosotros(msConfig)}
                                      onChange={(e) => persistMostrarPlantillaMusicosNosotros(e.target.checked)}
                                    />
                                  </Field.Root>
                                  <Text fontSize="xs" color="gray.500">
                                    Si lo activas, en «Sobre nosotros» de tu micrositio se listan los músicos <Text as="span" fontWeight="600">activos</Text> de{' '}
                                    <Text as="span" fontWeight="600">Gestión → Mis músicos</Text>: foto, nombre e instrumento (sin voz). Se guarda al instante.
                                  </Text>
                                  {readMostrarPlantillaMusicosNosotros(msConfig) &&
                                    hayMusicosActivosConVariosInstrumentos(tuWebMusicosPlantilla) && (
                                    <Box
                                      bg="white"
                                      rounded="lg"
                                      borderWidth="1px"
                                      borderColor="gray.100"
                                      p={3}
                                      mt={2}
                                    >
                                      <Text fontSize="sm" fontWeight="600" mb={2}>
                                        Músicos con varios instrumentos
                                      </Text>
                                      <Text fontSize="xs" color="gray.500" mb={3}>
                                        Elige si en la web se muestran todas sus asignaciones en la misma tarjeta o solo una. Si eliges un instrumento por músico, ese criterio es el que usa la web para ordenar la plantilla por familias (madera, metal, etc.).
                                      </Text>
                                      <RadioGroup.Root
                                        value={readPlantillaMusicosInstrumentosModo(msConfig)}
                                        onValueChange={(v) => persistPlantillaMusicosInstrumentosModo(v)}>
                                        <Stack gap={2}>
                                          <RadioGroup.Item value="todos" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Mostrar todos los instrumentos en cada músico
                                                                                        </RadioGroup.ItemText></RadioGroup.Item>
                                          <RadioGroup.Item value="uno" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Un instrumento por músico (elegir cuál mostrar)
                                                                                        </RadioGroup.ItemText></RadioGroup.Item>
                                        </Stack>
                                      </RadioGroup.Root>
                                      {readPlantillaMusicosInstrumentosModo(msConfig) === 'uno' && (
                                        <VStack align="stretch" gap={3} mt={4}>
                                          {tuWebMusicosActivosMultiInstrumento.map((m) => {
                                            const rows = m.instrumentosAsignados || [];
                                            const map = readPlantillaMusicosInstrumentoPorMusico(msConfig);
                                            const selRaw = map[m.id] || rows[0]?.id || '';
                                            const validSel = rows.some((r) => r.id === selRaw)
                                              ? selRaw
                                              : (rows[0]?.id || '');
                                            return (
                                              <Field.Root key={m.id}>
                                                <Field.Label fontSize="xs" mb={1}>
                                                  {m.nombre} {m.apellidos || ''} — instrumento en la web
                                                </Field.Label>
                                                <NativeSelect.Root>
                                                  <NativeSelect.Field
                                                    size="sm"
                                                    value={validSel}
                                                    onChange={(e) => {
                                                      const next = {
                                                        ...readPlantillaMusicosInstrumentoPorMusico(msConfig),
                                                        [m.id]: e.target.value,
                                                      };
                                                      persistPlantillaMusicosInstrumentoPorMusico(next);
                                                    }}>
                                                    {rows.map((r) => (
                                                      <option key={r.id} value={r.id}>
                                                        {labelAsignacionInstrumentoRow(r)}
                                                      </option>
                                                    ))}
                                                  </NativeSelect.Field>
                                                  <NativeSelect.Indicator />
                                                </NativeSelect.Root>
                                              </Field.Root>
                                            );
                                          })}
                                        </VStack>
                                      )}
                                    </Box>
                                  )}
                                </VStack>
                              )}

                              {sec.id === 'historia' && (
                                <>
                                  <Textarea
                                    value={msConfig.historia}
                                    onChange={(e) => setMsConfig(p => ({ ...p, historia: e.target.value }))}
                                    placeholder="Fundada en 1920, nuestra banda tiene una larga tradicion..."
                                    rows={5} fontSize="sm" maxLength={5000}
                                  />
                                  <Text fontSize="xs" color="gray.400" textAlign="right" mt={1}>{(msConfig.historia || '').length}/5000</Text>
                                  <Text fontSize="xs" color="gray.500" mt={2}>
                                    Para conservar este texto en tu web, pulsa <Text as="span" fontWeight="700">Guardar cambios</Text> cuando aparezca la barra naranja abajo.
                                  </Text>
                                </>
                              )}

                              {sec.id === 'noticias' && (
                                <>
                                  <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                                    <Text fontSize="sm" color="gray.600">
                                      Activas: <Text as="span" fontWeight="800">
                                        {(msConfig.noticias || []).filter((n) => n.activo).length}/10
                                      </Text>
                                    </Text>
                                    <Button
                                      size="xs"
                                      colorPalette="purple"
                                      variant="outline"
                                      onClick={() => openNoticiaEditor(null)}><Icon as={FaPlus} />Añadir noticia
                                                                          </Button>
                                  </HStack>

                                  {(msConfig.noticias || []).length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">
                                      No hay noticias todavía. Crea una para publicarla en tu web.
                                    </Text>
                                  ) : (
                                    <VStack gap={3} align="stretch">
                                      {(msConfig.noticias || []).map((n, i) => {
                                        const raw = String(n.texto || '');
                                        const tLine = raw.split('\n')[0] || '';
                                        const title = tLine.replace(/^\s*#\s+/, '').trim() || `Noticia ${i + 1}`;
                                        return (
                                          <Box key={n.id || i} p={4} bg="white" rounded="xl" borderWidth="1px" borderColor="gray.100">
                                            <HStack justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                                              <VStack align="start" gap={0} minW={0} flex={1}>
                                                <Text fontWeight="900" fontSize="sm" lineClamp={1}>
                                                  {title}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                  {n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ''}
                                                </Text>
                                              </VStack>
                                              <HStack gap={2} flexWrap="wrap" justify="flex-end" align="center">
                                                <Field.Root display="flex" alignItems="center" w="auto">
                                                  <Field.Label mb={0} fontSize="xs" mr={2}>
                                                    Activa
                                                  </Field.Label>
                                                  <Switch
                                                    colorPalette="purple"
                                                    checked={n.activo === true}
                                                    onChange={(e) => {
                                                      const nextChecked = e.target.checked;
                                                      setMsConfig((p) => {
                                                        const ns = [...(p.noticias || [])];
                                                        const cur = ns[i];
                                                        if (!cur || cur.id !== n.id) return p;
                                                        if (nextChecked) {
                                                          const activeCount = ns.filter((x) => x.activo).length;
                                                          if (activeCount >= 10) {
                                                            toaster.create({
                                                              title: 'Máximo 10 noticias activas',
                                                              description: 'Desactiva otra noticia o archívala antes de activar esta.',
                                                              type: 'warning',
                                                              duration: 4500,
                                                            });
                                                            return p;
                                                          }
                                                        }
                                                        ns[i] = { ...cur, activo: nextChecked };
                                                        return { ...p, noticias: ns };
                                                      });
                                                    }}
                                                  />
                                                </Field.Root>
                                                <Tooltip content="Editar" showArrow positioning={{
                                                  placement: "top"
                                                }}>
                                                  <IconButton
                                                    aria-label="Editar noticia"
                                                    size="sm"
                                                    variant="ghost"
                                                    colorPalette="purple"
                                                    onClick={() => openNoticiaEditor(i)}><Icon as={FaEdit} /></IconButton>
                                                </Tooltip>
                                                <Tooltip content="Eliminar" showArrow positioning={{
                                                  placement: "top"
                                                }}>
                                                  <IconButton
                                                    aria-label="Eliminar noticia"
                                                    size="sm"
                                                    variant="ghost"
                                                    colorPalette="red"
                                                    onClick={() => {
                                                      setMsConfig((p) => {
                                                        const ns = [...(p.noticias || [])];
                                                        const row = ns[i];
                                                        if (row?.imagen) {
                                                          bandasAPI.deleteMicrositeNoticiaImagen(row.imagen).catch(() => {});
                                                        }
                                                        ns.splice(i, 1);
                                                        return { ...p, noticias: ns };
                                                      });
                                                    }}><Icon as={FaTrash} /></IconButton>
                                                </Tooltip>
                                              </HStack>
                                            </HStack>
                                          </Box>
                                        );
                                      })}
                                    </VStack>
                                  )}

                                  <input
                                    ref={noticiaThumbInputRef}
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleNoticiaThumbChange}
                                  />
                                  <input
                                    ref={noticiaMediaInputRef}
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleNoticiaMediaChange}
                                  />

                                  <Dialog.Root open={noticiaEditorOpen} size={{ base: 'full', md: '2xl' }} placement='center' scrollBehavior="inside" onOpenChange={e => {
                                    if (!e.open) {
                                      closeNoticiaEditor();
                                    }
                                  }}>
                                    <Portal>

                                      <Dialog.Backdrop />
                                      <Dialog.Positioner>
                                        <Dialog.Content rounded={{ base: '0', md: '2xl' }}>
                                          <Dialog.Header fontWeight="900">Editar noticia</Dialog.Header>
                                          <Dialog.CloseTrigger />
                                          <Dialog.Body pb={6}>
                                            <VStack align="stretch" gap={4}>
                                              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                                <Field.Root display="flex" alignItems="center" w="auto">
                                                  <Field.Label mb={0} fontSize="sm" mr={2}>
                                                    Activa
                                                  </Field.Label>
                                                  <Switch
                                                    colorPalette="purple"
                                                    checked={noticiaEditorDraft.activo === true}
                                                    onChange={(e) => {
                                                      const nextChecked = e.target.checked;
                                                      if (nextChecked) {
                                                        const activeCount = (msConfig.noticias || []).filter((x) => x.activo).length;
                                                        if (activeCount >= 10 && !(noticiaEditorIndex != null && (msConfig.noticias || [])[noticiaEditorIndex]?.activo)) {
                                                          toaster.create({
                                                            title: 'Máximo 10 noticias activas',
                                                            description: 'Desactiva otra noticia o archívala antes de activar esta.',
                                                            type: 'warning',
                                                            duration: 4500,
                                                          });
                                                          return;
                                                        }
                                                      }
                                                      setNoticiaEditorDraft((d) => ({ ...d, activo: nextChecked }));
                                                    }}
                                                  />
                                                </Field.Root>
                                                <HStack gap={2}>
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorPalette="purple"
                                                    loading={noticiaThumbUploading}
                                                    onClick={handlePickNoticiaThumb}><Icon as={FaCamera} />{noticiaEditorDraft.imagen ? 'Cambiar portada' : 'Subir imagen de portada'}</Button>
                                                  {noticiaEditorDraft.imagen && (
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      colorPalette="red"
                                                      onClick={() => {
                                                        const url = noticiaEditorDraft.imagen;
                                                        setNoticiaEditorDraft((d) => ({ ...d, imagen: '' }));
                                                        if (url) bandasAPI.deleteMicrositeNoticiaImagen(url).catch(() => {});
                                                      }}
                                                    >
                                                      Quitar
                                                    </Button>
                                                  )}
                                                </HStack>
                                              </HStack>

                                              {noticiaEditorDraft.imagen && (
                                                <Box
                                                  rounded="xl"
                                                  overflow="hidden"
                                                  borderWidth="1px"
                                                  borderColor="gray.200"
                                                  bg="gray.50"
                                                >
                                                  <Image
                                                    src={noticiaEditorDraft.imagen}
                                                    alt="Portada de la noticia"
                                                    w="full"
                                                    maxH={{ base: '240px', md: '320px' }}
                                                    objectFit="contain"
                                                  />
                                                </Box>
                                              )}

                                              <Field.Root required>
                                                <Field.Label fontSize="sm">Título</Field.Label>
                                                <Input
                                                  value={noticiaEditorDraft.titulo}
                                                  onChange={(e) => setNoticiaEditorDraft((d) => ({ ...d, titulo: e.target.value }))}
                                                  placeholder="Título de la noticia"
                                                  maxLength={120}
                                                />
                                              </Field.Root>

                                              <Field.Root required>
                                                <Field.Label fontSize="sm">Encabezado</Field.Label>
                                                <Input
                                                  value={noticiaEditorDraft.encabezado}
                                                  onChange={(e) => setNoticiaEditorDraft((d) => ({ ...d, encabezado: e.target.value }))}
                                                  placeholder="Subtítulo o entradilla"
                                                  maxLength={180}
                                                />
                                              </Field.Root>

                                              <Field.Root required>
                                                <Field.Label fontSize="sm">Cuerpo</Field.Label>
                                                <HStack gap={2} mb={2} flexWrap="wrap">
                                                  <Button size="xs" variant="ghost" onClick={() => wrapBodySelection('**', '**')}>Negrita</Button>
                                                  <Button size="xs" variant="ghost" onClick={() => wrapBodySelection('_', '_')}>Cursiva</Button>
                                                  <Button size="xs" variant="ghost" onClick={() => wrapBodySelection('++', '++')}>Subrayado</Button>
                                                  <Button
                                                    size="xs"
                                                    variant="outline"
                                                    colorPalette="purple"
                                                    loading={noticiaMediaUploading}
                                                    onClick={handleInsertImageUpload}
                                                  >
                                                    Insertar imagen
                                                  </Button>
                                                  <Button
                                                    size="xs"
                                                    variant="outline"
                                                    onClick={() => {
                                                      setNoticiaLinkDraft('https://');
                                                      setNoticiaLinkModalOpen(true);
                                                    }}
                                                  >
                                                    Insertar URL
                                                  </Button>
                                                  <Button
                                                    size="xs"
                                                    variant="outline"
                                                    onClick={() => {
                                                      setNoticiaVideoDraft('https://');
                                                      setNoticiaVideoModalOpen(true);
                                                    }}
                                                  >
                                                    Insertar vídeo
                                                  </Button>
                                                </HStack>
                                                <Textarea
                                                  ref={noticiaBodyRef}
                                                  value={noticiaEditorDraft.cuerpo}
                                                  onChange={(e) => setNoticiaEditorDraft((d) => ({ ...d, cuerpo: e.target.value }))}
                                                  placeholder="Escribe el cuerpo de la noticia…"
                                                  rows={10}
                                                  fontSize="sm"
                                                  maxLength={4000}
                                                />
                                                <Text fontSize="xs" color="gray.400" textAlign="right" mt={1}>
                                                  {(noticiaEditorDraft.cuerpo || '').length}/4000
                                                </Text>
                                              </Field.Root>

                                              <Box rounded="xl" borderWidth="1px" borderColor="gray.200" bg="gray.50" p={4}>
                                                <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                                                  <Text fontWeight="900" fontSize="sm">
                                                    Vista previa
                                                  </Text>
                                                  <Text fontSize="xs" color="gray.600">
                                                    Así se verá en tu web (incluye imágenes y vídeos)
                                                  </Text>
                                                </HStack>
                                                {renderNoticiaPreview(
                                                  `# ${String(noticiaEditorDraft.titulo || '').trim()}\n` +
                                                  `## ${String(noticiaEditorDraft.encabezado || '').trim()}\n` +
                                                  `${String(noticiaEditorDraft.cuerpo || '').trim()}`,
                                                )}
                                              </Box>

                                              <HStack justify="flex-end">
                                                <Button variant="ghost" onClick={closeNoticiaEditor}>Cancelar</Button>
                                                <Button
                                                  colorPalette="purple"
                                                  onClick={saveNoticiaEditor}
                                                  disabled={
                                                    !String(noticiaEditorDraft.titulo || '').trim() ||
                                                    !String(noticiaEditorDraft.encabezado || '').trim() ||
                                                    !String(noticiaEditorDraft.cuerpo || '').trim()
                                                  }
                                                >
                                                  Guardar noticia
                                                </Button>
                                              </HStack>

                                              <Dialog.Root open={noticiaLinkModalOpen} placement='center' size={{ base: 'full', md: 'lg' }} onOpenChange={e => {
                                                if (!e.open) {
                                                  setNoticiaLinkModalOpen(false);
                                                }
                                              }}>
                                                <Portal>

                                                  <Dialog.Backdrop />
                                                  <Dialog.Positioner>
                                                    <Dialog.Content rounded={{ base: '0', md: '2xl' }}>
                                                      <Dialog.Header fontWeight="900">Insertar URL</Dialog.Header>
                                                      <Dialog.CloseTrigger />
                                                      <Dialog.Body pb={6}>
                                                        <VStack align="stretch" gap={4}>
                                                          <Field.Root>
                                                            <Field.Label fontSize="sm">Enlace</Field.Label>
                                                            <Input
                                                              value={noticiaLinkDraft}
                                                              onChange={(e) => setNoticiaLinkDraft(e.target.value)}
                                                              placeholder="https://…"
                                                            />
                                                            <Field.HelperText fontSize="xs">
                                                              Se insertará como enlace clicable en la noticia.
                                                            </Field.HelperText>
                                                          </Field.Root>
                                                          <HStack justify="flex-end">
                                                            <Button variant="ghost" onClick={() => setNoticiaLinkModalOpen(false)}>
                                                              Cancelar
                                                            </Button>
                                                            <Button
                                                              colorPalette="purple"
                                                              onClick={() => {
                                                                const url = String(noticiaLinkDraft || '').trim();
                                                                if (!/^https?:\/\//i.test(url)) {
                                                                  toaster.create({ title: 'URL no válida', description: 'Debe empezar por http:// o https://', type: 'warning', duration: 4000 });
                                                                  return;
                                                                }
                                                                insertAtBodyCursor(`[enlace](${url})`);
                                                                setNoticiaLinkModalOpen(false);
                                                              }}
                                                              disabled={!String(noticiaLinkDraft || '').trim()}
                                                            >
                                                              Insertar
                                                            </Button>
                                                          </HStack>
                                                        </VStack>
                                                      </Dialog.Body>
                                                    </Dialog.Content>
                                                  </Dialog.Positioner>

                                                </Portal>
                                              </Dialog.Root>

                                              <Dialog.Root open={noticiaVideoModalOpen} placement='center' size={{ base: 'full', md: 'lg' }} onOpenChange={e => {
                                                if (!e.open) {
                                                  setNoticiaVideoModalOpen(false);
                                                }
                                              }}>
                                                <Portal>

                                                  <Dialog.Backdrop />
                                                  <Dialog.Positioner>
                                                    <Dialog.Content rounded={{ base: '0', md: '2xl' }}>
                                                      <Dialog.Header fontWeight="900">Insertar vídeo</Dialog.Header>
                                                      <Dialog.CloseTrigger />
                                                      <Dialog.Body pb={6}>
                                                        <VStack align="stretch" gap={4}>
                                                          <Field.Root>
                                                            <Field.Label fontSize="sm">Enlace de YouTube</Field.Label>
                                                            <Input
                                                              value={noticiaVideoDraft}
                                                              onChange={(e) => setNoticiaVideoDraft(e.target.value)}
                                                              placeholder="https://youtube.com/…"
                                                            />
                                                            <Field.HelperText fontSize="xs">
                                                              Pegando un enlace de YouTube se incrustará el vídeo automáticamente.
                                                            </Field.HelperText>
                                                          </Field.Root>
                                                          <HStack justify="flex-end">
                                                            <Button variant="ghost" onClick={() => setNoticiaVideoModalOpen(false)}>
                                                              Cancelar
                                                            </Button>
                                                            <Button
                                                              colorPalette="purple"
                                                              onClick={() => {
                                                                const url = String(noticiaVideoDraft || '').trim();
                                                                const id = parseYouTubeId(url);
                                                                if (!id) {
                                                                  toaster.create({ title: 'URL de YouTube no válida', type: 'warning', duration: 4000 });
                                                                  return;
                                                                }
                                                                insertAtBodyCursor(`\n{{youtube:${id}}}\n`);
                                                                setNoticiaVideoModalOpen(false);
                                                              }}
                                                              disabled={!String(noticiaVideoDraft || '').trim()}
                                                            >
                                                              Insertar
                                                            </Button>
                                                          </HStack>
                                                        </VStack>
                                                      </Dialog.Body>
                                                    </Dialog.Content>
                                                  </Dialog.Positioner>

                                                </Portal>
                                              </Dialog.Root>
                                            </VStack>
                                          </Dialog.Body>
                                        </Dialog.Content>
                                      </Dialog.Positioner>

                                    </Portal>
                                  </Dialog.Root>
                                </>
                              )}

                              {sec.id === 'directiva' && (
                                <>
                                  <input
                                    ref={directivaFotoInputRef}
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleDirectivaFotoFileChange}
                                  />
                                  <HStack justify="flex-end" mb={2}>
                                    <Button
                                      size="xs"
                                      colorPalette="purple"
                                      variant="outline"
                                      onClick={() => setMsConfig(p => ({ ...p, directiva: [...p.directiva, newDirectivaMember()] }))}><Icon as={FaPlus} />Anadir</Button>
                                  </HStack>
                                  <VStack gap={3} align="stretch">
                                    {msConfig.directiva.map((m, i) => (
                                      <HStack key={m.id || i} gap={3} p={3} bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" align="flex-start">
                                        <VStack gap={2} align="center" flexShrink={0}>
                                          <Avatar.Root size="lg" bg="purple.100" color="purple.700"><Avatar.Fallback name={m.nombre || '?'} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                                          {directivaFotoUploadingIndex === i ? (
                                            <Spinner size="sm" color="purple.500" />
                                          ) : (
                                            <HStack gap={1}>
                                              <Button
                                                size="xs"
                                                variant="outline"
                                                colorPalette="purple"
                                                onClick={() => {
                                                  let memberId = m.id;
                                                  if (!memberId) {
                                                    memberId = typeof crypto !== 'undefined' && crypto.randomUUID
                                                      ? crypto.randomUUID()
                                                      : `dir-${Date.now()}`;
                                                    setMsConfig((p) => {
                                                      const d = [...p.directiva];
                                                      d[i] = { ...d[i], id: memberId };
                                                      return { ...p, directiva: d };
                                                    });
                                                  }
                                                  queueDirectivaFotoPick(i, memberId, m.foto);
                                                }}><Icon as={FaCamera} />{m.foto ? 'Cambiar' : 'Foto'}</Button>
                                              {m.foto && (
                                                <Button size="xs" variant="ghost" colorPalette="red" onClick={() => removeDirectivaFotoAt(i)}>
                                                  Quitar
                                                </Button>
                                              )}
                                            </HStack>
                                          )}
                                        </VStack>
                                        <VStack flex={1} gap={2} align="stretch">
                                          <Input size="sm" placeholder="Nombre" value={m.nombre}
                                            onChange={(e) => { const d = [...msConfig.directiva]; d[i] = { ...d[i], nombre: e.target.value }; setMsConfig(p => ({ ...p, directiva: d })); }}
                                          />
                                          <Input size="sm" placeholder="Cargo (ej: Presidente, Director)" value={m.cargo}
                                            onChange={(e) => { const d = [...msConfig.directiva]; d[i] = { ...d[i], cargo: e.target.value }; setMsConfig(p => ({ ...p, directiva: d })); }}
                                          />
                                          <Text fontSize="xs" color="gray.500">
                                            Misma norma que la foto del músico: imagen, máx. 5 MB; se optimiza en servidor (JPEG, lado máx. 384 px).
                                          </Text>
                                        </VStack>
                                        <IconButton
                                          size="sm"
                                          colorPalette="red"
                                          variant="ghost"
                                          aria-label="Eliminar"
                                          onClick={() => {
                                            if (m.foto) {
                                              bandasAPI.deleteMicrositeDirectivaFoto(m.foto).catch(() => {});
                                            }
                                            setMsConfig(p => ({ ...p, directiva: p.directiva.filter((_, j) => j !== i) }));
                                          }}><Icon as={FaTrash} /></IconButton>
                                      </HStack>
                                    ))}
                                    {msConfig.directiva.length === 0 && <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No has anadido miembros todavia</Text>}
                                  </VStack>
                                </>
                              )}

                              {sec.id === 'galeria' && (
                                <VStack gap={4} align="stretch">
                                  <Text fontSize="xs" color="gray.600">
                                    Misma galería que en <Text as="span" fontWeight="600">Perfil público</Text>. Subida y borrado se aplican al instante en el servidor.
                                  </Text>
                                  <HStack justify="space-between" align="center">
                                    <Text fontSize="sm" fontWeight="600">
                                      Fotos en la galería
                                    </Text>
                                    <Text fontSize="sm" color={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.500' : 'gray.500'} fontWeight="medium">
                                      {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS}
                                    </Text>
                                  </HStack>
                                  <Box
                                    w="full"
                                    h="200px"
                                    border="2px dashed"
                                    borderColor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.300' : isDragOver ? 'purple.400' : 'gray.300'}
                                    borderRadius="lg"
                                    bg={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.50' : isDragOver ? 'purple.50' : 'white'}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    cursor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'not-allowed' : 'pointer'}
                                    opacity={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 0.6 : 1}
                                    transition="all 0.3s ease"
                                    onDragOver={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragOver}
                                    onDragLeave={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragLeave}
                                    onDrop={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDrop}
                                    onClick={() => {
                                      if ((profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS) {
                                        showModal('Límite alcanzado', `Has alcanzado el límite de ${MAX_GALLERY_PHOTOS} fotos. Elimina alguna para subir más.`, 'warning');
                                        return;
                                      }
                                      galleryTuWebInputRef.current?.click();
                                    }}
                                    _hover={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? {} : {
                                      borderColor: 'purple.400',
                                      bg: 'purple.50',
                                      transform: 'translateY(-2px)',
                                      shadow: 'lg',
                                    }}
                                    position="relative"
                                    overflow="hidden"
                                  >
                                    {isUploading ? (
                                      <VStack gap={3}>
                                        <Progress.Root indeterminate colorPalette="purple" w="200px" rounded="full">
                                          <Progress.Track>
                                            <Progress.Range />
                                          </Progress.Track>
                                        </Progress.Root>
                                        <Text color="purple.600" fontWeight="medium">
                                          Procesando imágenes...
                                        </Text>
                                      </VStack>
                                    ) : (
                                      <VStack gap={3} textAlign="center" px={3}>
                                        <Icon
                                          as={FaCamera}
                                          w={12}
                                          h={12}
                                          color={isDragOver ? 'purple.500' : 'gray.400'}
                                          transition="color 0.3s ease"
                                        />
                                        <VStack gap={1}>
                                          <Text fontSize="lg" fontWeight="semibold" color={isDragOver ? 'purple.600' : 'gray.600'}>
                                            {isDragOver ? '¡Suelta las imágenes aquí!' : 'Arrastra y suelta tus fotos'}
                                          </Text>
                                          <Text fontSize="sm" color="gray.500">
                                            o haz clic para seleccionar archivos
                                          </Text>
                                          <Text fontSize="xs" color="gray.400">
                                            JPG, PNG, WebP • Máx. 5MB por imagen • {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS} fotos
                                          </Text>
                                        </VStack>
                                      </VStack>
                                    )}
                                    <input
                                      ref={galleryTuWebInputRef}
                                      type="file"
                                      hidden
                                      multiple
                                      accept="image/jpeg,image/jpg,image/png,image/webp"
                                      onChange={handlePhotoUpload}
                                    />
                                  </Box>

                                  {profileData.fotos && profileData.fotos.length > 0 && (
                                    <VStack gap={4} w="full" align="stretch">
                                      <Separator borderColor="gray.200" />
                                      <HStack gap={2} w="full" justify="space-between">
                                        <Text fontSize="md" fontWeight="medium">
                                          Fotos subidas ({profileData.fotos.length})
                                        </Text>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          colorPalette="red"
                                          onClick={onDeleteAllPhotosModalOpen}><Icon as={FaTrash} />Eliminar todas
                                                                                  </Button>
                                      </HStack>
                                      <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={4} w="full">
                                        {profileData.fotos.map((photoUrl, index) => (
                                          <Box
                                            key={index}
                                            position="relative"
                                            rounded="lg"
                                            overflow="hidden"
                                            bg="white"
                                            shadow="md"
                                            _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }}
                                            transition="all 0.3s ease"
                                          >
                                            <Image
                                              src={photoUrl}
                                              alt="Foto del perfil"
                                              w="full"
                                              h="32"
                                              objectFit="cover"
                                              cursor="pointer"
                                              onClick={() => handleImageClick(photoUrl)}
                                            />
                                            <IconButton
                                              size="sm"
                                              colorPalette="red"
                                              variant="solid"
                                              position="absolute"
                                              top={2}
                                              right={2}
                                              onClick={() => removePhoto(photoUrl)}
                                              aria-label="Eliminar foto"
                                              shadow="lg"
                                              _hover={{ transform: 'scale(1.1)' }}
                                              transition="transform 0.2s ease"><Icon as={FaTrash} /></IconButton>
                                          </Box>
                                        ))}
                                      </SimpleGrid>
                                    </VStack>
                                  )}
                                </VStack>
                              )}

                              {sec.id === 'videos' && (
                                <VStack gap={4} align="stretch">
                                  <Text fontSize="xs" color="gray.600">
                                    Misma lista de vídeos que en <Text as="span" fontWeight="600">Perfil público</Text>. Al añadir o quitar enlaces, confirma con la barra naranja <Text as="span" fontWeight="600">Guardar cambios del perfil</Text> para publicarlos.
                                  </Text>
                                  <Box bg="white" rounded="xl" borderWidth="1px" borderColor="gray.200" p={4} shadow="sm">
                                    <VStack gap={4} align="stretch">
                                      <HStack>
                                        <Icon as={FaYoutube} mr={2} color="red.500" boxSize={5} />
                                        <Heading size="sm">Videos de YouTube</Heading>
                                      </HStack>
                                      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                        <Field.Root>
                                          <Field.Label fontSize="sm">Título del video</Field.Label>
                                          <Input
                                            value={newVideoTitle}
                                            onChange={(e) => setNewVideoTitle(e.target.value)}
                                            placeholder="Ej: Concierto Semana Santa 2024"
                                            size="sm"
                                          />
                                        </Field.Root>
                                        <Field.Root>
                                          <Field.Label fontSize="sm">URL de YouTube o Short</Field.Label>
                                          <Input
                                            value={newVideoUrl}
                                            onChange={(e) => setNewVideoUrl(e.target.value)}
                                            placeholder="youtube.com/watch?v=... · youtube.com/shorts/..."
                                            size="sm"
                                          />
                                        </Field.Root>
                                      </SimpleGrid>
                                      <Button
                                        colorPalette="red"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddVideo}
                                        alignSelf="flex-start"><Icon as={FaPlus} />Añadir Video
                                                                              </Button>
                                      {profileData.videos && profileData.videos.length > 0 && (() => {
                                        const normalVideos = profileData.videos.filter((v) => !isYouTubeShort(v.url));
                                        const shorts = profileData.videos.filter((v) => isYouTubeShort(v.url));
                                        const videoRowKey = (v, i, prefix) => {
                                          if (v?.id != null && String(v.id) !== '') return `vid-${v.id}`;
                                          return `${prefix}-${i}-${extractYouTubeId(v?.url) || 'u'}`;
                                        };
                                        const renderCard = (video, rowKey) => {
                                          const videoId = extractYouTubeId(video.url);
                                          const short = isYouTubeShort(video.url);
                                          return (
                                            <Box key={rowKey} rounded="lg" overflow="hidden" shadow="md" position="relative">
                                              {videoId ? (
                                                <Box position="relative" pb={short ? '177.78%' : '56.25%'}>
                                                  <iframe
                                                    src={`https://www.youtube.com/embed/${videoId}`}
                                                    title={video.titulo}
                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                  />
                                                  {short && (
                                                    <Badge position="absolute" top={2} left={2} colorPalette="red" fontSize="2xs" rounded="md" px={1.5} zIndex={1} pointerEvents="none">
                                                      Short
                                                    </Badge>
                                                  )}
                                                </Box>
                                              ) : (
                                                <Box h="150px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                                  <Icon as={FaYoutube} w={12} h={12} color="gray.400" />
                                                </Box>
                                              )}
                                              <HStack p={2} bg="white" justify="space-between">
                                                <Text fontSize="sm" fontWeight="medium" isTruncated>
                                                  {video.titulo}
                                                </Text>
                                                <IconButton
                                                  size="xs"
                                                  colorPalette="red"
                                                  variant="ghost"
                                                  onClick={() => handleRemoveVideo(video.id)}
                                                  aria-label="Eliminar video"><Icon as={FaTrash} /></IconButton>
                                              </HStack>
                                            </Box>
                                          );
                                        };
                                        return (
                                          <VStack gap={4} align="stretch" mt={2}>
                                            {normalVideos.length > 0 && (
                                              <Box>
                                                <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
                                                  Vídeos
                                                </Text>
                                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                                  {normalVideos.map((v, i) => renderCard(v, videoRowKey(v, i, 'n')))}
                                                </SimpleGrid>
                                              </Box>
                                            )}
                                            {shorts.length > 0 && (
                                              <Box>
                                                <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
                                                  Shorts
                                                </Text>
                                                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                                                  {shorts.map((v, i) => renderCard(v, videoRowKey(v, i, 's')))}
                                                </SimpleGrid>
                                              </Box>
                                            )}
                                          </VStack>
                                        );
                                      })()}
                                      {(!profileData.videos || profileData.videos.length === 0) && (
                                        <Box textAlign="center" py={6} color="gray.500">
                                          <Icon as={FaYoutube} w={10} h={10} mb={2} />
                                          <Text fontSize="sm">No hay videos añadidos</Text>
                                          <Text fontSize="xs">Añade enlaces de YouTube para mostrar tus videos</Text>
                                        </Box>
                                      )}
                                    </VStack>
                                  </Box>
                                </VStack>
                              )}

                              {sec.id === 'repertorio' && (
                                <VStack gap={4} align="stretch">
                                  <Text fontSize="xs" color="gray.600">
                                    Mismo repertorio que en <Text as="span" fontWeight="600">Perfil público</Text>. Puedes añadir obras a mano, buscar en el catálogo Cadencya, escanear un PDF o importar desde tu{' '}
                                    <Link href="/banda/repertorio" color="purple.600" fontWeight="600">
                                      archivo de partituras
                                    </Link>
                                    . Los cambios se publican con la barra naranja <Text as="span" fontWeight="600">Guardar cambios del perfil</Text>.
                                  </Text>
                                  <Tabs.Root size="sm" variant='enclosed' colorPalette="purple">
                                    <Tabs.List flexWrap="wrap">
                                      <Tab value="0">Manual y catálogo</Tab>
                                      <Tab value="1">Archivo de partituras</Tab>
                                    </Tabs.List>
                                    <TabPanels>
                                      <TabPanel value="0" px={0} pt={4}>
                                        <VStack gap={4} align="stretch">
                                          <Box bg="white" rounded="lg" borderWidth="1px" borderColor="gray.200" p={3}>
                                            <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase">
                                              Añadir manualmente
                                            </Text>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                              <Field.Root>
                                                <Field.Label fontSize="xs">Obra</Field.Label>
                                                <Input
                                                  size="sm"
                                                  placeholder="Nombre de la obra"
                                                  value={repertorioManualNombre}
                                                  onChange={(e) => setRepertorioManualNombre(e.target.value)}
                                                  bg={inputBg}
                                                />
                                              </Field.Root>
                                              <Field.Root>
                                                <Field.Label fontSize="xs">Autor</Field.Label>
                                                <Input
                                                  size="sm"
                                                  placeholder="Compositor (opcional)"
                                                  value={repertorioManualAutor}
                                                  onChange={(e) => setRepertorioManualAutor(e.target.value)}
                                                  bg={inputBg}
                                                />
                                              </Field.Root>
                                            </SimpleGrid>
                                            <Field.Root mt={3}>
                                              <Field.Label fontSize="xs">Tipo</Field.Label>
                                              <NativeSelect.Root>
                                                <NativeSelect.Field
                                                  size="sm"
                                                  placeholder="Tipo de obra"
                                                  value={repertorioManualTipo}
                                                  onChange={(e) => setRepertorioManualTipo(e.target.value)}
                                                  bg={inputBg}>
                                                  {tiposDeObra.map((tipo, idx) => (
                                                    <option key={idx} value={tipo}>{tipo}</option>
                                                  ))}
                                                </NativeSelect.Field>
                                                <NativeSelect.Indicator />
                                              </NativeSelect.Root>
                                            </Field.Root>
                                            <Button
                                              mt={3}
                                              size="sm"
                                              colorPalette="purple"
                                              onClick={handleAddRepertorioManualTuWeb}
                                              disabled={!repertorioManualNombre.trim() || !repertorioManualTipo}><Icon as={FaPlus} />Añadir obra
                                                                                          </Button>
                                          </Box>
                                          <Box bg="white" rounded="lg" borderWidth="1px" borderColor="gray.200" p={3}>
                                            <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase">
                                              Catálogo Cadencya
                                            </Text>
                                            <Field.Root position="relative">
                                              <Field.Label fontSize="xs">Buscar marcha u obra</Field.Label>
                                              <HStack align="start">
                                                <Box position="relative" flex={1}>
                                                  <Input
                                                    size="sm"
                                                    placeholder="Mín. 2 caracteres…"
                                                    value={repertorioInput}
                                                    onChange={(e) => handleRepertorioInputChange(e.target.value)}
                                                    onKeyPress={(e) => {
                                                      if (e.key === 'Enter' && marchasFiltradas.length === 0) {
                                                        handleAddMarchaManual();
                                                      }
                                                    }}
                                                    bg={inputBg}
                                                  />
                                                  {showSuggestions && marchasFiltradas.length > 0 && (
                                                    <Box
                                                      position="absolute"
                                                      top="100%"
                                                      left={0}
                                                      right={0}
                                                      bg={inputBg}
                                                      border="1px solid"
                                                      borderColor={borderColorAlt}
                                                      borderRadius="md"
                                                      mt={1}
                                                      maxH="220px"
                                                      overflowY="auto"
                                                      zIndex={10}
                                                      shadow="lg"
                                                    >
                                                      {marchasFiltradas.map((marcha) => (
                                                        <Box
                                                          key={marcha.id}
                                                          p={2}
                                                          cursor="pointer"
                                                          _hover={{ bg: purpleHoverBg }}
                                                          onClick={() => handleAddMarchaFromDB(marcha)}
                                                          borderBottom="1px solid"
                                                          borderColor={subtleBorderColorB}
                                                        >
                                                          <Text fontWeight="600" fontSize="xs">{marcha.nombre}</Text>
                                                          <Text fontSize="2xs" color="gray.500">{marcha.autor} · {marcha.tipo}</Text>
                                                        </Box>
                                                      ))}
                                                    </Box>
                                                  )}
                                                </Box>
                                                <Button
                                                  size="sm"
                                                  colorPalette="purple"
                                                  onClick={handleAddMarchaManual}
                                                  disabled={!repertorioInput.trim()}><Icon as={FaPlus} />Manual
                                                                                                  </Button>
                                              </HStack>
                                              <Field.HelperText fontSize="2xs">Si no aparece en la lista, «Manual» abre el formulario con el nombre escrito.</Field.HelperText>
                                            </Field.Root>
                                            <HStack mt={3} flexWrap="wrap" gap={2}>
                                              <input
                                                type="file"
                                                accept="application/pdf"
                                                ref={pdfTuWebRepertorioInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handlePdfUpload}
                                              />
                                              <Button
                                                size="sm"
                                                colorPalette="green"
                                                variant="outline"
                                                onClick={() => pdfTuWebRepertorioInputRef.current?.click()}
                                                loading={isScanningPdf}
                                                loadingText="Escaneando…"><Icon as={FaFileAlt} />Escanear PDF
                                                                                              </Button>
                                              <Text fontSize="2xs" color="gray.500" w="full">
                                                Máximo 10 páginas por escaneo y 10 escaneos al día.
                                              </Text>
                                            </HStack>
                                          </Box>
                                        </VStack>
                                      </TabPanel>
                                      <TabPanel value="1" px={0} pt={4}>
                                        <VStack gap={3} align="stretch">
                                          {archivoMusicaLoading && (
                                            <HStack><Spinner size="sm" color="purple.500" /><Text fontSize="sm" color="gray.600">Cargando archivo…</Text></HStack>
                                          )}
                                          {!archivoMusicaLoading && (
                                            <>
                                              <Input
                                                size="sm"
                                                placeholder="Filtrar obras por título, autor o género…"
                                                value={repertorioArchivoFiltro}
                                                onChange={(e) => setRepertorioArchivoFiltro(e.target.value)}
                                                bg={inputBg}
                                              />
                                              <HStack flexWrap="wrap" gap={2}>
                                                <Button
                                                  size="xs"
                                                  colorPalette="purple"
                                                  variant="outline"
                                                  onClick={() => mergeObrasIntoRepertorioFromArchivo(obrasArchivoFiltradas)}
                                                  disabled={!obrasArchivoFiltradas.length}
                                                >
                                                  Importar todas (vista filtrada)
                                                </Button>
                                                <Text fontSize="2xs" color="gray.500">
                                                  {obrasArchivoFiltradas.length} obra(s) en la lista
                                                </Text>
                                              </HStack>
                                              <Box maxH="240px" overflowY="auto" borderWidth="1px" borderColor="gray.200" rounded="md">
                                                {obrasArchivoFiltradas.length === 0 ? (
                                                  <Text fontSize="sm" color="gray.500" p={3}>No hay obras. Crea obras en el archivo de partituras.</Text>
                                                ) : (
                                                  obrasArchivoFiltradas.map((obra) => {
                                                    const dup = profileData.repertorio.some(
                                                      (r) => r.nombre.toLowerCase() === (obra.titulo || '').trim().toLowerCase(),
                                                    );
                                                    return (
                                                      <HStack
                                                        key={obra.id}
                                                        justify="space-between"
                                                        p={2}
                                                        borderBottomWidth="1px"
                                                        borderColor="gray.100"
                                                        gap={2}
                                                      >
                                                        <VStack align="start" gap={0} flex={1} minW={0}>
                                                          <Text fontSize="xs" fontWeight="600" lineClamp={1}>{obra.titulo || 'Sin título'}</Text>
                                                          <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                                            {obra.autor || '—'} · {obra.genero?.nombre || 'Otro'}
                                                          </Text>
                                                        </VStack>
                                                        <Button
                                                          size="xs"
                                                          variant={dup ? 'ghost' : 'solid'}
                                                          colorPalette="purple"
                                                          disabled={dup}
                                                          onClick={() => mergeObrasIntoRepertorioFromArchivo([obra])}
                                                        >
                                                          {dup ? 'Ya está' : 'Añadir'}
                                                        </Button>
                                                      </HStack>
                                                    );
                                                  })
                                                )}
                                              </Box>
                                              <Separator />
                                              <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase">
                                                Carpetas del archivo
                                              </Text>
                                              <VStack gap={2} align="stretch" maxH="200px" overflowY="auto">
                                                {archivoCarpetasList.length === 0 ? (
                                                  <Text fontSize="sm" color="gray.500">No hay carpetas. Organiza obras en carpetas desde el archivo.</Text>
                                                ) : (
                                                  archivoCarpetasList.map((carpeta) => {
                                                    const obrasC = [...(carpeta.obras || [])]
                                                      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
                                                      .map((x) => x.obra)
                                                      .filter(Boolean);
                                                    return (
                                                      <HStack
                                                        key={carpeta.id}
                                                        justify="space-between"
                                                        p={2}
                                                        bg="gray.50"
                                                        rounded="md"
                                                        borderWidth="1px"
                                                        borderColor="gray.100"
                                                      >
                                                        <HStack gap={2} minW={0}>
                                                          <Icon as={FaFolder} color="orange.400" />
                                                          <VStack align="start" gap={0} minW={0}>
                                                            <Text fontSize="xs" fontWeight="600" lineClamp={1}>{carpeta.nombre}</Text>
                                                            <Text fontSize="2xs" color="gray.500">{obrasC.length} obra(s)</Text>
                                                          </VStack>
                                                        </HStack>
                                                        <Button
                                                          size="xs"
                                                          colorPalette="orange"
                                                          variant="outline"
                                                          disabled={!obrasC.length}
                                                          onClick={() => mergeObrasIntoRepertorioFromArchivo(obrasC)}
                                                        >
                                                          Importar carpeta
                                                        </Button>
                                                      </HStack>
                                                    );
                                                  })
                                                )}
                                              </VStack>
                                            </>
                                          )}
                                        </VStack>
                                      </TabPanel>
                                    </TabPanels>
                                  </Tabs.Root>
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2}>
                                      Repertorio público ({(profileData.repertorio || []).length})
                                    </Text>
                                    {(profileData.repertorio || []).length === 0 ? (
                                      <Text fontSize="sm" color="gray.500">Aún no hay obras en el listado público.</Text>
                                    ) : (
                                      <Box maxH="280px" overflowY="auto" borderWidth="1px" borderColor="gray.200" rounded="md">
                                        {(profileData.repertorio || []).map((marcha, index) => (
                                          <Flex
                                            key={marcha.id}
                                            p={2}
                                            align="center"
                                            justify="space-between"
                                            bg={index % 2 === 0 ? 'white' : 'gray.50'}
                                            borderBottom={index < (profileData.repertorio || []).length - 1 ? '1px solid' : 'none'}
                                            borderColor="gray.100"
                                          >
                                            <VStack align="start" gap={0} flex={1} minW={0}>
                                              <Text fontSize="xs" fontWeight="600" lineClamp={1}>{marcha.nombre}</Text>
                                              <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                                {marcha.autor}{marcha.tipo ? ` · ${marcha.tipo}` : ''}
                                              </Text>
                                            </VStack>
                                            <IconButton
                                              size="xs"
                                              colorPalette="red"
                                              variant="ghost"
                                              aria-label="Quitar del repertorio"
                                              onClick={() => handleRemoveMarcha(marcha.id)}><Icon as={FaTrash} /></IconButton>
                                          </Flex>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </VStack>
                              )}

                              {sec.id === 'logros' && (
                                <>
                                  <HStack justify="flex-end" mb={2}>
                                    <Button
                                      size="xs"
                                      colorPalette="purple"
                                      variant="outline"
                                      onClick={() => setMsConfig(p => ({ ...p, logros: [...p.logros, { titulo: '', ano: '', descripcion: '' }] }))}><Icon as={FaPlus} />Anadir</Button>
                                  </HStack>
                                  <VStack gap={3} align="stretch">
                                    {msConfig.logros.map((l, i) => (
                                      <HStack key={i} gap={3} p={3} bg="white" rounded="lg" align="start" borderWidth="1px" borderColor="gray.100">
                                        <VStack flex={1} gap={2}>
                                          <HStack w="full">
                                            <Input size="sm" placeholder="Titulo del logro" value={l.titulo} flex={1}
                                              onChange={(e) => { const lg = [...msConfig.logros]; lg[i] = { ...lg[i], titulo: e.target.value }; setMsConfig(p => ({ ...p, logros: lg })); }}
                                            />
                                            <Input size="sm" placeholder="Ano" value={l.ano} w="80px"
                                              onChange={(e) => { const lg = [...msConfig.logros]; lg[i] = { ...lg[i], ano: e.target.value }; setMsConfig(p => ({ ...p, logros: lg })); }}
                                            />
                                          </HStack>
                                          <Input size="sm" placeholder="Descripcion (opcional)" value={l.descripcion}
                                            onChange={(e) => { const lg = [...msConfig.logros]; lg[i] = { ...lg[i], descripcion: e.target.value }; setMsConfig(p => ({ ...p, logros: lg })); }}
                                          />
                                        </VStack>
                                        <IconButton
                                          size="sm"
                                          colorPalette="red"
                                          variant="ghost"
                                          aria-label="Eliminar"
                                          onClick={() => setMsConfig(p => ({ ...p, logros: p.logros.filter((_, j) => j !== i) }))}><Icon as={FaTrash} /></IconButton>
                                      </HStack>
                                    ))}
                                    {msConfig.logros.length === 0 && <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No has anadido logros todavia</Text>}
                                  </VStack>
                                </>
                              )}

                              {sec.id === 'resenas' && (
                                <VStack align="stretch" gap={4}>
                                  <Alert.Root status="info" variant="subtle" rounded="md" fontSize="sm">
                                    <Alert.Indicator />
                                    <Text fontSize="sm">
                                      En tu web pública solo aparecen los avales que hayas <strong>aceptado</strong> y las reseñas que tengas <strong>publicadas</strong> (pestaña Perfil).
                                    </Text>
                                  </Alert.Root>
                                  {avalesPendientes.length > 0 && (
                                    <Alert.Root status="warning" variant="subtle" rounded="md" fontSize="sm">
                                      <Alert.Indicator />
                                      <Text fontSize="sm">
                                        Tienes {avalesPendientes.length} aval(es) pendiente(s): no se mostrarán en la web hasta que los aceptes en la pestaña Perfil.
                                      </Text>
                                    </Alert.Root>
                                  )}
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2}>
                                      Avales visibles en la web ({avalesRecibidos.length})
                                    </Text>
                                    {avalesRecibidos.length === 0 ? (
                                      <Text fontSize="sm" color="gray.500">
                                        Aún no hay avales aceptados. Si una cofradía te ha avalado, acéptalo en «Perfil» para que aparezca aquí y en tu web.
                                      </Text>
                                    ) : (
                                      <VStack align="stretch" gap={2} maxH="260px" overflowY="auto">
                                        {avalesRecibidos.map((aval) => (
                                          <HStack
                                            key={aval.id}
                                            align="start"
                                            p={3}
                                            rounded="lg"
                                            borderWidth="1px"
                                            borderColor="gray.100"
                                            bg="white"
                                            gap={3}
                                          >
                                            <Avatar.Root size="sm"><Avatar.Fallback name={aval.cofradia?.nombre}>{<Icon as={FaChurch} />}</Avatar.Fallback><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
                                            <VStack align="start" gap={0} flex={1} minW={0}>
                                              <HStack gap={1}>
                                                <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                                  {aval.cofradia?.nombre || 'Cofradía'}
                                                </Text>
                                                <Icon as={FaCheckCircle} color="green.500" boxSize={3.5} />
                                              </HStack>
                                              {aval.texto ? (
                                                <Text fontSize="xs" color="gray.600" lineClamp={3} fontStyle="italic">
                                                  &ldquo;{aval.texto}&rdquo;
                                                </Text>
                                              ) : null}
                                              <Text fontSize="2xs" color="gray.400">
                                                {aval.acceptedAt
                                                  ? `Aceptado el ${new Date(aval.acceptedAt).toLocaleDateString('es-ES')}`
                                                  : ''}
                                              </Text>
                                            </VStack>
                                          </HStack>
                                        ))}
                                      </VStack>
                                    )}
                                  </Box>
                                  <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2}>
                                      Reseñas publicadas en la web (
                                      {profileData.resenas.filter((r) => r.isPublished).length})
                                    </Text>
                                    {profileData.resenas.filter((r) => r.isPublished).length === 0 ? (
                                      <Text fontSize="sm" color="gray.500">
                                        No hay reseñas publicadas. Desde la pestaña Perfil puedes publicar las que quieras mostrar.
                                      </Text>
                                    ) : (
                                      <VStack align="stretch" gap={2} maxH="220px" overflowY="auto">
                                        {profileData.resenas
                                          .filter((r) => r.isPublished)
                                          .map((r) => (
                                            <HStack
                                              key={r.id}
                                              align="start"
                                              p={2}
                                              rounded="md"
                                              borderWidth="1px"
                                              borderColor="gray.100"
                                              bg="gray.50"
                                              gap={2}
                                            >
                                              <Avatar.Root size="sm"><Avatar.Fallback name={r.cofradiaName}>{<Icon as={FaChurch} />}</Avatar.Fallback><Avatar.Image src={r.cofradiaAvatar} /></Avatar.Root>
                                              <VStack align="start" gap={0} flex={1} minW={0}>
                                                <Text fontSize="xs" fontWeight="600" lineClamp={1}>
                                                  {r.cofradiaName}
                                                </Text>
                                                {r.eventoNombre ? (
                                                  <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                                    {r.eventoNombre}
                                                  </Text>
                                                ) : null}
                                                {r.comment ? (
                                                  <Text fontSize="xs" color="gray.600" lineClamp={2}>
                                                    {r.comment}
                                                  </Text>
                                                ) : null}
                                              </VStack>
                                            </HStack>
                                          ))}
                                      </VStack>
                                    )}
                                  </Box>
                                </VStack>
                              )}

                              {sec.id === 'calendario' && (
                                <Box>
                                  <Alert.Root status="info" variant="subtle" rounded="md" fontSize="sm" mb={3}>
                                    <Text fontSize="sm">
                                      Configura qué quieres mostrar en tu web: actuaciones (Gestión) y/o ensayos (Gestión). También puedes elegir el modo de visualización.
                                    </Text>
                                  </Alert.Root>

                                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
                                    <Field.Root display="flex" alignItems="center" justifyContent="space-between" bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3}>
                                      <HStack gap={2}>
                                        <Icon as={FaCalendarAlt} color="purple.500" />
                                        <Text fontSize="sm" fontWeight="700">Mostrar actuaciones</Text>
                                      </HStack>
                                      <Switch
                                        colorPalette="purple"
                                        checked={msConfig.calendarioMostrarActuaciones !== false}
                                        onChange={(e) => setMsConfig((p) => ({ ...p, calendarioMostrarActuaciones: e.target.checked }))}
                                      />
                                    </Field.Root>

                                    <Field.Root display="flex" alignItems="center" justifyContent="space-between" bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3}>
                                      <HStack gap={2}>
                                        <Icon as={FaClock} color="purple.500" />
                                        <Text fontSize="sm" fontWeight="700">Mostrar ensayos</Text>
                                      </HStack>
                                      <Switch
                                        colorPalette="purple"
                                        checked={msConfig.calendarioMostrarEnsayos !== false}
                                        onChange={(e) => setMsConfig((p) => ({ ...p, calendarioMostrarEnsayos: e.target.checked }))}
                                      />
                                    </Field.Root>
                                  </SimpleGrid>

                                  {(msConfig.calendarioMostrarActuaciones !== false) && (
                                    <Box bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3} mb={3}>
                                      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                                        <HStack gap={2}>
                                          <Text fontSize="sm" fontWeight="800">Actuaciones a mostrar</Text>
                                          {tuWebCalendarioLoading ? <Spinner size="sm" color="purple.500" /> : null}
                                        </HStack>
                                        <Field.Root display="flex" alignItems="center" w="auto" m={0}>
                                          <Field.Label htmlFor="cal-incluir-act-pasadas" mb={0} mr={2} fontSize="xs" color="gray.600" fontWeight="600">
                                            Incluir pasadas
                                          </Field.Label>
                                          <Switch
                                            id="cal-incluir-act-pasadas"
                                            size="sm"
                                            colorPalette="purple"
                                            checked={msConfig.calendarioIncluirActuacionesPasadas === true}
                                            onChange={(e) => setMsConfig((p) => ({ ...p, calendarioIncluirActuacionesPasadas: e.target.checked }))}
                                          />
                                        </Field.Root>
                                      </HStack>
                                      {msConfig.calendarioIncluirActuacionesPasadas ? (
                                        <Text fontSize="xs" color="gray.500" mb={2}>
                                          Pasadas del año actual y del anterior únicamente.
                                        </Text>
                                      ) : null}
                                      {(tuWebCalendarioActuaciones || []).length === 0 ? (
                                        <Text fontSize="xs" color="gray.500">
                                          {msConfig.calendarioIncluirActuacionesPasadas
                                            ? 'No se han podido cargar actuaciones (o no tienes ninguna registrada).'
                                            : 'No se han podido cargar actuaciones (o no tienes próximas). Activa «Incluir pasadas» para ver actuaciones anteriores.'}
                                        </Text>
                                      ) : (
                                        <VStack align="stretch" gap={1} maxH="240px" overflowY="auto" pr={1}
                                          css={{
                                            '& &::-webkit-scrollbar': { width: '8px' },
                                            '& &::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '10px' },
                                            '& &::-webkit-scrollbar-track': { background: '#EDF2F7', borderRadius: '10px' }
                                          }}
                                        >
                                          <IsolateCheckboxesFromField>
                                          {(tuWebCalendarioActuaciones || []).map((a) => {
                                            const hidden = new Set(Array.isArray(msConfig.calendarioActuacionesOcultasIds) ? msConfig.calendarioActuacionesOcultasIds : []);
                                            const checked = !hidden.has(a.id);
                                            const fechaTxt = a.fecha ? new Date(a.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
                                            return (
                                              <HStack key={a.id} justify="space-between" p={2} rounded="md" bg={checked ? 'gray.50' : 'gray.100'}>
                                                <Checkbox.Root
                                                  colorPalette="purple"
                                                  onCheckedChange={(details) => {
                                                    setMsConfig((p) => {
                                                      const set = new Set(Array.isArray(p.calendarioActuacionesOcultasIds) ? p.calendarioActuacionesOcultasIds : []);
                                                      if (details.checked === true) set.delete(a.id);
                                                      else set.add(a.id);
                                                      return { ...p, calendarioActuacionesOcultasIds: Array.from(set) };
                                                    });
                                                  }}
                                                  checked={checked}
                                                ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                                  <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                                    {a.nombre || 'Actuación'}
                                                  </Text>
                                                </Checkbox.Label></Checkbox.Root>
                                                <HStack gap={2} flexShrink={0}>
                                                  {isCalendarioItemPasado(a) ? (
                                                    <Badge colorPalette="gray" variant="outline">Pasada</Badge>
                                                  ) : null}
                                                  {fechaTxt ? <Badge colorPalette="purple" variant="subtle">{fechaTxt}</Badge> : null}
                                                  {a.horaInicio ? <Badge colorPalette="gray" variant="subtle">{a.horaInicio}{a.horaFin ? `–${a.horaFin}` : ''}</Badge> : null}
                                                </HStack>
                                              </HStack>
                                            );
                                          })}
                                          </IsolateCheckboxesFromField>
                                        </VStack>
                                      )}
                                    </Box>
                                  )}

                                  {(msConfig.calendarioMostrarEnsayos !== false) && (
                                    <Box bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3} mb={3}>
                                      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                                        <HStack gap={2}>
                                          <Text fontSize="sm" fontWeight="800">Ensayos a mostrar</Text>
                                          {tuWebCalendarioLoading ? <Spinner size="sm" color="purple.500" /> : null}
                                        </HStack>
                                        <Field.Root display="flex" alignItems="center" w="auto" m={0}>
                                          <Field.Label htmlFor="cal-incluir-ens-pasados" mb={0} mr={2} fontSize="xs" color="gray.600" fontWeight="600">
                                            Incluir pasados
                                          </Field.Label>
                                          <Switch
                                            id="cal-incluir-ens-pasados"
                                            size="sm"
                                            colorPalette="purple"
                                            checked={msConfig.calendarioIncluirEnsayosPasados === true}
                                            onChange={(e) => setMsConfig((p) => ({ ...p, calendarioIncluirEnsayosPasados: e.target.checked }))}
                                          />
                                        </Field.Root>
                                      </HStack>
                                      {msConfig.calendarioIncluirEnsayosPasados ? (
                                        <Text fontSize="xs" color="gray.500" mb={2}>
                                          Pasados del año actual y del anterior únicamente.
                                        </Text>
                                      ) : null}
                                      {(tuWebCalendarioEnsayos || []).length === 0 ? (
                                        <Text fontSize="xs" color="gray.500">
                                          {msConfig.calendarioIncluirEnsayosPasados
                                            ? 'No se han podido cargar ensayos (o no tienes ninguno registrado).'
                                            : 'No se han podido cargar ensayos (o no tienes próximos). Activa «Incluir pasados» para ver ensayos anteriores.'}
                                        </Text>
                                      ) : (
                                        <VStack align="stretch" gap={1} maxH="240px" overflowY="auto" pr={1}
                                          css={{
                                            '& &::-webkit-scrollbar': { width: '8px' },
                                            '& &::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '10px' },
                                            '& &::-webkit-scrollbar-track': { background: '#EDF2F7', borderRadius: '10px' }
                                          }}
                                        >
                                          <IsolateCheckboxesFromField>
                                          {(tuWebCalendarioEnsayos || []).map((e) => {
                                            const hidden = new Set(Array.isArray(msConfig.calendarioEnsayosOcultosIds) ? msConfig.calendarioEnsayosOcultosIds : []);
                                            const checked = !hidden.has(e.id);
                                            const fechaTxt = e.fecha ? new Date(e.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
                                            const titulo = e.descripcion || e.grupo?.nombre || 'Ensayo';
                                            return (
                                              <HStack key={e.id} justify="space-between" p={2} rounded="md" bg={checked ? 'gray.50' : 'gray.100'}>
                                                <Checkbox.Root
                                                  colorPalette="purple"
                                                  onCheckedChange={(details) => {
                                                    setMsConfig((p) => {
                                                      const set = new Set(Array.isArray(p.calendarioEnsayosOcultosIds) ? p.calendarioEnsayosOcultosIds : []);
                                                      if (details.checked === true) set.delete(e.id);
                                                      else set.add(e.id);
                                                      return { ...p, calendarioEnsayosOcultosIds: Array.from(set) };
                                                    });
                                                  }}
                                                  checked={checked}
                                                ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                                  <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                                    {titulo}
                                                  </Text>
                                                </Checkbox.Label></Checkbox.Root>
                                                <HStack gap={2} flexShrink={0}>
                                                  {isCalendarioItemPasado(e) ? (
                                                    <Badge colorPalette="gray" variant="outline">Pasado</Badge>
                                                  ) : null}
                                                  {fechaTxt ? <Badge colorPalette="purple" variant="subtle">{fechaTxt}</Badge> : null}
                                                  {e.horaInicio ? <Badge colorPalette="gray" variant="subtle">{e.horaInicio}{e.horaFin ? `–${e.horaFin}` : ''}</Badge> : null}
                                                </HStack>
                                              </HStack>
                                            );
                                          })}
                                          </IsolateCheckboxesFromField>
                                        </VStack>
                                      )}
                                    </Box>
                                  )}

                                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                    <Field.Root bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" p={3}>
                                      <Field.Label fontSize="sm" fontWeight="700" mb={2}>Modo</Field.Label>
                                      <NativeSelect.Root>
                                        <NativeSelect.Field
                                          size="sm"
                                          borderRadius="lg"
                                          value={msConfig.calendarioModo || 'lista'}
                                          onChange={(e) => setMsConfig((p) => ({ ...p, calendarioModo: e.target.value }))}>
                                          <option value="lista">Lista</option>
                                          <option value="calendario">Calendario</option>
                                        </NativeSelect.Field>
                                        <NativeSelect.Indicator />
                                      </NativeSelect.Root>
                                      <Text fontSize="xs" color="gray.500" mt={2}>
                                        El modo calendario es ideal para mostrar actuaciones por meses.
                                      </Text>
                                    </Field.Root>

                                    <MultiCheckboxField
                                      bg="white"
                                      rounded="lg"
                                      borderWidth="1px"
                                      borderColor="gray.100"
                                      p={3}
                                      label={
                                        <Text fontSize="sm" fontWeight="700" mb={2}>
                                          Meses a mostrar (opcional)
                                        </Text>
                                      }
                                    >
                                      <Wrap gap={2}>
                                        {['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'].map((m, idx) => {
                                          const month = idx + 1;
                                          const set = new Set(Array.isArray(msConfig.calendarioMeses) ? msConfig.calendarioMeses : []);
                                          const checked = set.has(month);
                                          return (
                                            <Checkbox.Root
                                              key={month}
                                              size="sm"
                                              colorPalette="purple"
                                              onCheckedChange={(details) => {
                                                setMsConfig((p) => {
                                                  const next = new Set(Array.isArray(p.calendarioMeses) ? p.calendarioMeses : []);
                                                  if (details.checked === true) next.add(month);
                                                  else next.delete(month);
                                                  return { ...p, calendarioMeses: Array.from(next).sort((a, b) => a - b) };
                                                });
                                              }}
                                              checked={checked}
                                            ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                                {m}
                                              </Checkbox.Label></Checkbox.Root>
                                          );
                                        })}
                                      </Wrap>
                                      <Text fontSize="xs" color="gray.500" mt={2}>
                                        Si no marcas ningún mes, se mostrarán todas las actuaciones y ensayos visibles (próximos o pasados, según tu configuración).
                                      </Text>
                                    </MultiCheckboxField>
                                  </SimpleGrid>
                                </Box>
                              )}

                              {sec.id === 'patrocinadores' && (
                                <>
                                  <input
                                    ref={patrocinadorLogoInputRef}
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handlePatrocinadorLogoFileChange}
                                  />
                                  <HStack justify="flex-end" mb={2}>
                                    <Button
                                      size="xs"
                                      colorPalette="purple"
                                      variant="outline"
                                      onClick={() => setMsConfig((p) => ({ ...p, patrocinadores: [...p.patrocinadores, newPatrocinador()] }))}><Icon as={FaPlus} />Anadir</Button>
                                  </HStack>
                                  <VStack gap={3} align="stretch">
                                    {msConfig.patrocinadores.map((p, i) => (
                                      <HStack key={p.id || i} gap={3} p={3} bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100" align="flex-start">
                                        <VStack gap={2} align="center" flexShrink={0} minW="88px">
                                          {p.logo ? (
                                            <Image src={p.logo} alt="" maxH="52px" maxW="88px" objectFit="contain" rounded="md" borderWidth="1px" borderColor="gray.100" />
                                          ) : (
                                            <Box w="72px" h="44px" rounded="md" borderWidth="1px" borderStyle="dashed" borderColor="gray.200" bg="gray.50" />
                                          )}
                                          {patrocinadorLogoUploadingIndex === i ? (
                                            <Spinner size="sm" color="purple.500" />
                                          ) : (
                                            <HStack gap={1} flexWrap="wrap" justify="center">
                                              <Button
                                                size="xs"
                                                variant="outline"
                                                colorPalette="purple"
                                                onClick={() => {
                                                  let sponsorId = p.id;
                                                  if (!sponsorId) {
                                                    sponsorId =
                                                      typeof crypto !== 'undefined' && crypto.randomUUID
                                                        ? crypto.randomUUID()
                                                        : `pat-${Date.now()}`;
                                                    setMsConfig((prev) => {
                                                      const ps = [...prev.patrocinadores];
                                                      ps[i] = { ...ps[i], id: sponsorId };
                                                      return { ...prev, patrocinadores: ps };
                                                    });
                                                  }
                                                  queuePatrocinadorLogoPick(i, sponsorId, p.logo);
                                                }}><Icon as={FaUpload} />{p.logo ? 'Cambiar' : 'Subir'}</Button>
                                              {p.logo && (
                                                <Button size="xs" variant="ghost" colorPalette="red" onClick={() => removePatrocinadorLogoAt(i)}>
                                                  Quitar
                                                </Button>
                                              )}
                                            </HStack>
                                          )}
                                        </VStack>
                                        <VStack flex={1} gap={2}>
                                          <Input size="sm" placeholder="Nombre del patrocinador" value={p.nombre}
                                            onChange={(e) => { const ps = [...msConfig.patrocinadores]; ps[i] = { ...ps[i], nombre: e.target.value }; setMsConfig(prev => ({ ...prev, patrocinadores: ps })); }}
                                          />
                                          <Input size="sm" placeholder="O URL externa del logo (opcional)" value={p.logo}
                                            onChange={(e) => { const ps = [...msConfig.patrocinadores]; ps[i] = { ...ps[i], logo: e.target.value }; setMsConfig(prev => ({ ...prev, patrocinadores: ps })); }}
                                          />
                                          <Input size="sm" placeholder="Enlace web (opcional)" value={p.url}
                                            onChange={(e) => { const ps = [...msConfig.patrocinadores]; ps[i] = { ...ps[i], url: e.target.value }; setMsConfig(prev => ({ ...prev, patrocinadores: ps })); }}
                                          />
                                        </VStack>
                                        <IconButton
                                          size="sm"
                                          colorPalette="red"
                                          variant="ghost"
                                          aria-label="Eliminar"
                                          onClick={() => {
                                            const row = msConfig.patrocinadores[i];
                                            if (row?.logo) {
                                              bandasAPI.deleteMicrositePatrocinadorLogo(row.logo).catch(() => {});
                                            }
                                            setMsConfig((prev) => ({ ...prev, patrocinadores: prev.patrocinadores.filter((_, j) => j !== i) }));
                                          }}><Icon as={FaTrash} /></IconButton>
                                      </HStack>
                                    ))}
                                    {msConfig.patrocinadores.length === 0 && <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No has anadido patrocinadores todavia</Text>}
                                  </VStack>
                                </>
                              )}

                              {sec.id === 'enlaces' && (
                                <>
                                  <HStack justify="flex-end" mb={2}>
                                    <Button
                                      size="xs"
                                      colorPalette="purple"
                                      variant="outline"
                                      onClick={() => setMsConfig(p => ({ ...p, enlacesPersonalizados: [...p.enlacesPersonalizados, { titulo: '', url: '', icono: 'link' }] }))}><Icon as={FaPlus} />Anadir</Button>
                                  </HStack>
                                  <VStack gap={3} align="stretch">
                                    {msConfig.enlacesPersonalizados.map((e, i) => (
                                      <HStack key={i} gap={3} p={3} bg="white" rounded="lg" borderWidth="1px" borderColor="gray.100">
                                        <VStack flex={1} gap={2}>
                                          <HStack w="full">
                                            <NativeSelect.Root>
                                              <NativeSelect.Field
                                                size="sm"
                                                w="140px"
                                                value={e.icono}
                                                onValueChange={(ev) => { const el = [...msConfig.enlacesPersonalizados]; el[i] = { ...el[i], icono: ev.target.value }; setMsConfig(p => ({ ...p, enlacesPersonalizados: el })); }}>
                                                <option value="link">Enlace</option>
                                                <option value="spotify">Spotify</option>
                                                <option value="soundcloud">SoundCloud</option>
                                                <option value="bandcamp">Bandcamp</option>
                                                <option value="shop">Tienda</option>
                                                <option value="tiktok">TikTok</option>
                                                <option value="twitter">X</option>
                                                <option value="music">Musica</option>
                                              </NativeSelect.Field>
                                              <NativeSelect.Indicator />
                                            </NativeSelect.Root>
                                            <Input size="sm" placeholder="Titulo" value={e.titulo} flex={1}
                                              onValueChange={(ev) => { const el = [...msConfig.enlacesPersonalizados]; el[i] = { ...el[i], titulo: ev.target.value }; setMsConfig(p => ({ ...p, enlacesPersonalizados: el })); }}
                                            />
                                          </HStack>
                                          <Input size="sm" placeholder="https://..." value={e.url}
                                            onValueChange={(ev) => { const el = [...msConfig.enlacesPersonalizados]; el[i] = { ...el[i], url: ev.target.value }; setMsConfig(p => ({ ...p, enlacesPersonalizados: el })); }}
                                          />
                                        </VStack>
                                        <IconButton
                                          size="sm"
                                          colorPalette="red"
                                          variant="ghost"
                                          aria-label="Eliminar"
                                          onClick={() => setMsConfig(p => ({ ...p, enlacesPersonalizados: p.enlacesPersonalizados.filter((_, j) => j !== i) }))}><Icon as={FaTrash} /></IconButton>
                                      </HStack>
                                    ))}
                                    {msConfig.enlacesPersonalizados.length === 0 && <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No has anadido enlaces todavia</Text>}
                                  </VStack>
                                </>
                              )}

                              {sec.id === 'contacto' && (
                                <>
                                  <Alert.Root status="info" variant="subtle" rounded="md" fontSize="sm" mb={3}>
                                    <Text fontSize="sm">
                                      Los interruptores se guardan con el contenido de la web. Los datos de contacto, ubicación y redes se guardan en tu{' '}
                                      <Text as="span" fontWeight="700">perfil de banda</Text> al pulsar <Text as="span" fontWeight="700">Guardar cambios</Text> en esta pestaña (también puedes editarlos en la pestaña Perfil).
                                    </Text>
                                  </Alert.Root>
                                  <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>
                                    Qué mostrar en la web
                                  </Text>
                                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={2} mb={5}>
                                    {TU_WEB_CONTACTO_MOSTRAR_CAMPOS.map(({ key, label }) => {
                                      const cmRow = normalizeContactoMostrar(msConfig.contactoMostrar);
                                      const checked = cmRow[key];
                                      return (
                                        <Field.Root
                                          key={key}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          rounded="md"
                                          borderWidth="1px"
                                          borderColor="gray.200"
                                          bg="white"
                                          px={3}
                                          py={2}
                                        >
                                          <Field.Label htmlFor={`tuweb-contacto-mostrar-${key}`} mb={0} fontSize="sm" fontWeight="500" cursor="pointer" flex={1}>
                                            {label}
                                          </Field.Label>
                                          <Switch
                                            id={`tuweb-contacto-mostrar-${key}`}
                                            colorPalette="purple"
                                            checked={checked}
                                            onChange={(e) => {
                                              const next = e.target.checked;
                                              setMsConfig((p) => ({
                                                ...p,
                                                contactoMostrar: { ...normalizeContactoMostrar(p.contactoMostrar), [key]: next },
                                              }));
                                            }}
                                          />
                                        </Field.Root>
                                      );
                                    })}
                                  </SimpleGrid>
                                  <Separator my={4} />
                                  <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>
                                    Datos de contacto en la web
                                  </Text>
                                  <VStack gap={3} align="stretch" mb={4}>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">Teléfono</Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.contactoPublico.telefono}
                                        onChange={(e) => handleNestedInputChange('contactoPublico', 'telefono', e.target.value)}
                                        placeholder="+34 600 123 456"
                                        type="tel"
                                      />
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">Email</Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.contactoPublico.email}
                                        onChange={(e) => handleNestedInputChange('contactoPublico', 'email', e.target.value)}
                                        placeholder="contacto@ejemplo.com"
                                        type="email"
                                      />
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">Sitio web</Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.contactoPublico.website}
                                        onChange={(e) => handleNestedInputChange('contactoPublico', 'website', e.target.value)}
                                        placeholder="https://..."
                                        type="url"
                                        disabled={useMicrositeUrlAsWebsite}
                                      />
                                    </Field.Root>
                                  </VStack>
                                  <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>
                                    Ubicación (texto en la web)
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" mb={2}>
                                    El micrositio muestra ciudad y provincia. La dirección en mapa y coordenadas siguen en Perfil → Ubicación.
                                  </Text>
                                  <HStack gap={3} align="stretch" flexWrap="wrap" mb={4}>
                                    <Field.Root flex="1" minW="140px">
                                      <Field.Label fontSize="sm">Ciudad</Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.localizacion.ciudad}
                                        onChange={(e) => handleNestedInputChange('localizacion', 'ciudad', e.target.value)}
                                        placeholder="Ciudad"
                                      />
                                    </Field.Root>
                                    <Field.Root flex="1" minW="140px">
                                      <Field.Label fontSize="sm">Provincia</Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.localizacion.provincia}
                                        onChange={(e) => handleNestedInputChange('localizacion', 'provincia', e.target.value)}
                                        placeholder="Provincia"
                                      />
                                    </Field.Root>
                                  </HStack>
                                  <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>
                                    Redes sociales
                                  </Text>
                                  <VStack gap={3} align="stretch" mb={4}>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">
                                        <HStack gap={2}>
                                          <Icon as={FaInstagram} color="pink.500" />
                                          <Text>Instagram</Text>
                                        </HStack>
                                      </Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.redesSociales.instagram}
                                        onChange={(e) => handleNestedInputChange('redesSociales', 'instagram', e.target.value)}
                                        placeholder="@tu_banda"
                                      />
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">
                                        <HStack gap={2}>
                                          <Icon as={FaFacebook} color="blue.500" />
                                          <Text>Facebook</Text>
                                        </HStack>
                                      </Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.redesSociales.facebook}
                                        onChange={(e) => handleNestedInputChange('redesSociales', 'facebook', e.target.value)}
                                        placeholder="https://www.facebook.com/tu.perfil"
                                        type="url"
                                      />
                                      <Field.HelperText fontSize="xs">Pega el enlace completo de tu perfil o página de Facebook.</Field.HelperText>
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">
                                        <HStack gap={2}>
                                          <Icon as={FaYoutube} color="red.500" />
                                          <Text>YouTube</Text>
                                        </HStack>
                                      </Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.redesSociales.youtube}
                                        onChange={(e) => handleNestedInputChange('redesSociales', 'youtube', e.target.value)}
                                        placeholder="@tu_canal o https://www.youtube.com/@tu_canal"
                                      />
                                      <Field.HelperText fontSize="xs">Puedes indicar usuario/canal o pegar el enlace completo de YouTube.</Field.HelperText>
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">
                                        <HStack gap={2}>
                                          <Icon as={FaXTwitter} color="gray.800" />
                                          <Text>X</Text>
                                        </HStack>
                                      </Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.redesSociales.twitter || ''}
                                        onChange={(e) => handleNestedInputChange('redesSociales', 'twitter', e.target.value)}
                                        placeholder="@tu_banda o https://x.com/tu_banda"
                                      />
                                      <Field.HelperText fontSize="xs">Usuario de X (con o sin @) o enlace completo.</Field.HelperText>
                                    </Field.Root>
                                    <Field.Root>
                                      <Field.Label fontSize="sm">
                                        <HStack gap={2}>
                                          <Icon as={FaTiktok} />
                                          <Text>TikTok</Text>
                                        </HStack>
                                      </Field.Label>
                                      <Input
                                        size="sm"
                                        value={profileData.redesSociales.tiktok}
                                        onChange={(e) => handleNestedInputChange('redesSociales', 'tiktok', e.target.value)}
                                        placeholder="@usuario (no se muestra en el bloque contacto del micrositio)"
                                      />
                                      <Field.HelperText fontSize="xs">TikTok no aparece en la sección contacto del micrositio; sí en enlaces personalizados si lo añades allí.</Field.HelperText>
                                    </Field.Root>
                                  </VStack>
                                  <Separator my={4} />
                                  <Text fontWeight="600" fontSize="xs" color="gray.600" mb={1}>
                                    Mensaje de contratación en la web
                                  </Text>
                                  <Textarea
                                    value={msConfig.textoContratar}
                                    onChange={(e) => setMsConfig(p => ({ ...p, textoContratar: e.target.value }))}
                                    placeholder="¿Quieres contar con nosotros para tu evento? Contactanos sin compromiso..."
                                    rows={3} fontSize="sm" maxLength={1000}
                                  />
                                  <Text fontSize="xs" color="gray.400" textAlign="right" mt={1}>{(msConfig.textoContratar || '').length}/1000</Text>
                                </>
                              )}
                            </Box>
                          </React.Fragment>
                        );
                      })}
                          </VStack>
                        </GridItem>
                      </SimpleGrid>

                      {micrositeContenidoDirty && (
                        <Box
                          position="sticky"
                          bottom={{ base: 2, md: 4 }}
                          zIndex={8}
                          py={4}
                          px={4}
                          mt={2}
                          rounded="xl"
                          borderWidth="2px"
                          borderColor="orange.300"
                          bg="orange.50"
                          shadow="lg"
                        >
                          <Flex
                            direction={{ base: 'column', sm: 'row' }}
                            align={{ base: 'stretch', sm: 'center' }}
                            justify="space-between"
                            gap={4}
                          >
                            <VStack align="start" gap={1} flex={1} minW={0}>
                              <Text fontSize="sm" fontWeight="800" color="orange.900">
                                Tienes cambios sin guardar
                              </Text>
                              {tuWebContenidoUnsavedSectionLabels.length > 0 && (
                                <Text fontSize="xs" fontWeight="700" color="orange.900">
                                  Pendiente en: {tuWebContenidoUnsavedSectionLabels.join(', ')}
                                </Text>
                              )}
                              <Text fontSize="xs" color="orange.800">
                                Se aplicarán a tu web al guardar. En «Contacto» también se guardan aquí teléfono, email, redes y ubicación del perfil de banda.
                              </Text>
                            </VStack>
                            <Button
                              colorPalette="orange"
                              size="lg"
                              loading={msConfigSaving}
                              onClick={saveMicrositeContenidoManual}
                              flexShrink={0}
                              alignSelf={{ base: 'stretch', sm: 'center' }}><Icon as={FaSave} />Guardar cambios
                                                          </Button>
                          </Flex>
                        </Box>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>
                )}

                </Box>
              </Flex>
              )}
            </TabPanel>

            {/* Información privada / verificación */}
            <TabPanel value="4" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 4, md: 6, lg: 8 }} alignItems="start">
                {/* Gestión de Datos y Cuenta (GDPR) */}
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" border="1px solid" borderColor="blue.200">
                  <Card.Header pb={2}>
                    <HStack gap={3}>
                      <Icon as={FaShieldAlt} color="blue.500" w={5} h={5} />
                      <Heading size="md" color="blue.600">Gestión de Datos (GDPR)</Heading>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                      <Alert.Root status="info" rounded="md">
                        <Alert.Indicator />
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            Tus derechos sobre tus datos
                          </Text>
                          <Text fontSize="xs">
                            Conforme al RGPD, tienes derecho a descargar, modificar y eliminar tus datos personales.
                          </Text>
                        </VStack>
                      </Alert.Root>
                      <Button
                        colorPalette="blue"
                        variant="outline"
                        onClick={handleExportData}
                        loading={isExporting}><Icon as={FaFileAlt} />Descargar mis datos
                                              </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Zona de Peligro */}
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" border="1px solid" borderColor="red.200">
                  <Card.Header pb={2}>
                    <HStack gap={3}>
                      <Icon as={FaShieldAlt} color="red.500" w={5} h={5} />
                      <Heading size="md" color="red.600">Zona de Peligro</Heading>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={5} align="stretch">
                      {/* Desactivar cuenta */}
                      <Box p={4} bg="orange.50" rounded="md" border="1px solid" borderColor="orange.200">
                        <VStack align="start" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="orange.700">
                            Desactivar cuenta
                          </Text>
                          <Text fontSize="xs" color="orange.600">
                            Tu cuenta quedará inactiva pero tus datos se conservarán. Podrás reactivarla contactando con soporte.
                          </Text>
                          <Button
                            colorPalette="orange"
                            variant="outline"
                            size="sm"
                            onClick={onDeactivateModalOpen}
                          >
                            Desactivar mi cuenta
                          </Button>
                        </VStack>
                      </Box>

                      <Separator />

                      {/* Eliminar permanentemente */}
                      <Box p={4} bg="red.50" rounded="md" border="1px solid" borderColor="red.200">
                        <VStack align="start" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="red.700">
                            Eliminar cuenta permanentemente
                          </Text>
                          <Text fontSize="xs" color="red.600">
                            Se eliminarán todos tus datos personales de forma irreversible. Esta acción no se puede deshacer.
                          </Text>
                          <Button
                            colorPalette="red"
                            variant="solid"
                            size="sm"
                            onClick={onDeleteModalOpen}
                          >
                            Eliminar mi cuenta
                          </Button>
                        </VStack>
                      </Box>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </SimpleGrid>
            </TabPanel>

            {/* Suscripción */}
            <TabPanel value="5" w="full" maxW="full" minW={0} px={{ base: 0, sm: 2 }}>
              <MiSuscripcion embedded />
            </TabPanel>
          </TabPanels>
        </Tabs.Root>
        </Box>

        <ProfileTipsCarousel slides={profileTipsSlides} />

        {/* Modal de Éxito/Info */}
    <Dialog.Root open={isSaveModalOpen} placement='center' onOpenChange={e => {
      if (!e.open) {
        onSaveModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon 
                  as={modalContent.type === 'success' ? FaCheckCircle : FaShieldAlt} 
                  color={modalContent.type === 'success' ? 'green.500' : 'blue.500'} 
                  w={6} 
                  h={6} 
                />
                <Text>{modalContent.title}</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <Text lineHeight="tall" color={textColor}>
                {modalContent.message}
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="purple" onClick={onSaveModalClose}>
                Entendido
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal de Verificación */}
    <Dialog.Root open={isVerificationModalOpen} placement='center' onOpenChange={e => {
      if (!e.open) {
        onVerificationModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaShieldAlt} color="green.500" w={6} h={6} />
                <Text>{modalContent.title}</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={4} align="start">
                <Text lineHeight="tall" color={textColor}>
                  {modalContent.message}
                </Text>
                <Box bg="green.50" p={4} rounded="md" w="full">
                  <VStack gap={2} align="start">
                    <Text fontSize="sm" fontWeight="medium" color="green.800">
                      ¿Qué verificamos?
                    </Text>
                    <VStack gap={1} align="start" fontSize="sm" color="green.700">
                      <Text>• Información de contacto válida</Text>
                      <Text>• Existencia legal de la {userType === 'banda' ? 'banda' : 'cofradía'}</Text>
                      <Text>• Veracidad de los datos proporcionados</Text>
                      <Text>• Historial y reputación</Text>
                    </VStack>
                  </VStack>
                </Box>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="green" onClick={onVerificationModalClose}>
                Perfecto
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal de Error */}
    <Dialog.Root open={isErrorModalOpen} placement='center' size='lg' onOpenChange={e => {
      if (!e.open) {
        onErrorModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaIdCard} color="red.500" w={6} h={6} />
                <Text>{modalContent.title}</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={4} align="start">
                <Text lineHeight="tall" color={textColor}>
                  {modalContent.message}
                </Text>
                
                {/* Lista de campos específicos clickeables */}
                {modalContent.type === 'validation' && modalContent.missingFields && (
                  <Box bg="red.50" p={4} rounded="md" w="full">
                    <VStack gap={3} align="start">
                      <Text fontSize="sm" fontWeight="medium" color="red.800">
                        Campos faltantes (haz clic para ir al campo):
                      </Text>
                      <VStack gap={2} align="start" w="full">
                        {modalContent.missingFields.map((field, index) => (
                          <HStack
                            key={index}
                            gap={3}
                            w="full"
                            p={3}
                            bg="white"
                            rounded="md"
                            border="1px solid"
                            borderColor="red.200"
                            cursor="pointer"
                            _hover={{
                              bg: "red.50",
                              borderColor: "red.300",
                              transform: "translateY(-1px)",
                              shadow: "md"
                            }}
                            transition="all 0.2s ease"
                            onClick={() => {
                              navigateToField(field.ref, field.tab);
                              onErrorModalClose();
                            }}
                          >
                            <Icon as={FaExclamationTriangle} color="red.500" />
                            <VStack gap={0} align="start" flex={1}>
                              <Text fontSize="sm" fontWeight="medium" color="red.800">
                                {field.name}
                              </Text>
                              <Text fontSize="xs" color="red.600">
                                {field.section} • Tab {field.tab + 1}
                              </Text>
                            </VStack>
                            <Icon as={FaArrowRight} color="red.400" w={4} h={4} />
                          </HStack>
                        ))}
                      </VStack>
                      
                      <Box bg="blue.50" p={3} rounded="md" w="full" mt={2}>
                        <HStack gap={2}>
                          <Icon as={FaInfoCircle} color="blue.500" w={4} h={4} />
                          <Text fontSize="xs" color="blue.700">
                            <Text as="span" fontWeight="medium">Consejo:</Text> Al hacer clic en un campo, 
                            serás dirigido automáticamente a la pestaña y sección correspondiente.
                          </Text>
                        </HStack>
                      </Box>
                    </VStack>
                  </Box>
                )}
                
                {/* Modal de error genérico */}
                {modalContent.type === 'error' && modalContent.title === 'Información Incompleta' && (
                  <Box bg="red.50" p={4} rounded="md" w="full">
                    <VStack gap={2} align="start">
                      <Text fontSize="sm" fontWeight="medium" color="red.800">
                        Campos requeridos para validación:
                      </Text>
                      <VStack gap={1} align="start" fontSize="sm" color="red.700">
                        <Text>• Nombre de la {userType === 'banda' ? 'banda' : 'cofradía'}</Text>
                        <Text>• Información del responsable completa</Text>
                        <Text>• Dirección completa (calle, ciudad, provincia)</Text>
                        <Text>• Datos de contacto válidos</Text>
                      </VStack>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="red" variant="outline" onClick={onErrorModalClose}>
                Entendido
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal de confirmación de eliminación de reseña */}
    <Dialog.Root open={isDeleteReviewModalOpen} placement='center' onOpenChange={e => {
      if (!e.open) {
        onDeleteReviewModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaExclamationTriangle} color="orange.500" w={6} h={6} />
                <Text>Confirmar eliminación</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={4} align="start">
                <Text lineHeight="tall" color={textColor}>
                  ¿Estás seguro de que quieres eliminar esta reseña de <Text as="span" fontWeight="bold">{reviewToDelete?.cofradiaName}</Text>?
                </Text>
                <Alert.Root status="warning" rounded="md">
                  <Alert.Indicator />
                  <Text fontSize="sm">
                    Esta acción no se puede deshacer. La reseña se eliminará permanentemente.
                  </Text>
                </Alert.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={onDeleteReviewModalClose}>
                  Cancelar
                </Button>
                <Button colorPalette="red" onClick={handleDeleteReview}><Icon as={FaTrash} />Eliminar reseña
                              </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal para añadir marcha personalizada */}
    <Dialog.Root open={isAddMarchaModalOpen} placement='center' size='lg' onOpenChange={e => {
      if (!e.open) {
        handleCancelAddMarcha();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaMusic} color="purple.500" w={6} h={6} />
                <Text>Añadir obra al repertorio</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={5} align="stretch">
                {/* Nombre de la obra */}
                <Field.Root >
                  <Field.Label fontWeight="600">Nombre de la obra</Field.Label>
                  <Input
                    value={nuevaMarcha.nombre}
                    onChange={(e) => setNuevaMarcha(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Virgen de la Esperanza"
                    size="lg"
                    readOnly
                    bg={reviewCardBg}
                  />
                </Field.Root>

                {/* Autor */}
                <Field.Root >
                  <Field.Label fontWeight="600">Autor/Compositor</Field.Label>
                  <Box position="relative">
                    <Input
                      value={nuevaMarcha.autorPersonalizado}
                      onChange={(e) => handleAutorInputChange(e.target.value)}
                      placeholder="Busca o escribe el nombre del autor..."
                      size="lg"
                    />
                    
                    {/* Dropdown de sugerencias de autores */}
                    {showAutorSuggestions && autoresFiltrados.length > 0 && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        zIndex={10}
                        mt={1}
                        bg={cardBg}
                        border="1px solid"
                        borderColor={borderColorAlt}
                        rounded="md"
                        shadow="lg"
                        maxHeight="200px"
                        overflowY="auto"
                      >
                        {autoresFiltrados.map((autor, index) => (
                          <Box
                            key={index}
                            p={3}
                            cursor="pointer"
                            _hover={{ bg: purpleHoverBgAlt }}
                            onClick={() => handleSelectAutor(autor)}
                            borderBottom={index < autoresFiltrados.length - 1 ? '1px solid' : 'none'}
                            borderColor={subtleBorderColor}
                          >
                            <Text fontWeight="500" fontSize="sm">
                              {autor}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Field.HelperText fontSize="xs">
                    Escribe al menos 2 caracteres para buscar en nuestra base de datos de compositores
                  </Field.HelperText>
                </Field.Root>

                {/* Tipo de obra */}
                <Field.Root >
                  <Field.Label fontWeight="600">Tipo de obra</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={nuevaMarcha.tipo}
                      onChange={(e) => setNuevaMarcha(prev => ({ ...prev, tipo: e.target.value }))}
                      placeholder="Selecciona el tipo de obra..."
                      size="lg">
                      {tiposDeObra.map((tipo, index) => (
                        <option key={index} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Alert.Root status="info" rounded="md">
                  <Alert.Indicator />
                  <Text fontSize="sm">
                    Al añadir una obra, se incorporará al catálogo de Cadencya y estará disponible para otras bandas.
                  </Text>
                </Alert.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={handleCancelAddMarcha}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleConfirmAddMarcha}
                  disabled={!nuevaMarcha.nombre.trim() || !nuevaMarcha.tipo}><Icon as={FaPlus} />Añadir al repertorio
                              </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal importar desde Archivo Musical */}
    <Dialog.Root open={isImportArchivoModalOpen} placement='center' size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onImportArchivoModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content maxH="85vh">
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaUpload} color="purple.500" w={6} h={6} />
                <VStack align="start" gap={0}>
                  <Text>Importar desde Archivo Musical</Text>
                  <Text fontSize="sm" fontWeight="normal" color="gray.500">
                    Todo el archivo, carpetas concretas u obras sueltas
                  </Text>
                </VStack>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={4}>
              {archivoMusicaLoading ? (
                <Flex justify="center" py={10}>
                  <Spinner color="purple.500" />
                </Flex>
              ) : (
                <Tabs.Root
                  value={tabsControlledValue(importArchivoTab)}
                  onValueChange={tabsOnValueChange(setImportArchivoTab)}
                  size="sm"
                  variant='enclosed'
                  colorPalette="purple"
                >
                  <Tabs.List flexWrap="wrap">
                    <Tab value="0">Todo el archivo</Tab>
                    <Tab value="1">Carpetas</Tab>
                    <Tab value="2">Obras sueltas</Tab>
                  </Tabs.List>
                  <TabPanels>
                    <TabPanel value="0" px={0} pt={4}>
                      <VStack align="stretch" gap={3}>
                        <Text fontSize="sm" color="gray.600">
                          Importa todas las obras de tu archivo de partituras al repertorio público. Las que ya estén por nombre se omitirán.
                        </Text>
                        <HStack
                          p={4}
                          bg="purple.50"
                          rounded="lg"
                          borderWidth="1px"
                          borderColor="purple.100"
                          justify="space-between"
                          flexWrap="wrap"
                          gap={3}
                        >
                          <VStack align="start" gap={0}>
                            <Text fontWeight="700" fontSize="sm">Archivo completo</Text>
                            <Text fontSize="xs" color="gray.600">
                              {(archivoObrasList || []).length} obra(s) en el archivo
                            </Text>
                          </VStack>
                          <Button
                            colorPalette="purple"
                            size="sm"
                            onClick={handleImportArchivoTodo}
                            disabled={!archivoObrasList.length}><Icon as={FaUpload} />Importar todo
                                                  </Button>
                        </HStack>
                        {!archivoObrasList.length ? (
                          <Text fontSize="sm" color="gray.500">
                            No hay obras en el archivo. Créalas en Archivo Musical → Partituras.
                          </Text>
                        ) : null}
                      </VStack>
                    </TabPanel>
                    <TabPanel value="1" px={0} pt={4}>
                      <VStack align="stretch" gap={3}>
                        <Text fontSize="sm" color="gray.600">
                          Selecciona una o más carpetas. Se importarán todas las obras que contengan.
                        </Text>
                        {!archivoCarpetasList.length ? (
                          <Text fontSize="sm" color="gray.500">
                            No hay carpetas. Organiza obras en carpetas desde Archivo Musical → Partituras.
                          </Text>
                        ) : (
                          <VStack align="stretch" gap={2} maxH="45vh" overflowY="auto">
                            {archivoCarpetasList.map((carpeta) => {
                              const obrasC = obrasFromCarpetaPartituras(carpeta);
                              const checked = importCarpetasSeleccion.includes(carpeta.id);
                              return (
                                <Checkbox.Root
                                  key={carpeta.id}
                                  onCheckedChange={() => toggleImportCarpetaSeleccion(carpeta.id)}
                                  colorPalette="purple"
                                  p={2}
                                  rounded="md"
                                  borderWidth="1px"
                                  borderColor={checked ? 'purple.200' : 'gray.100'}
                                  bg={checked ? 'purple.50' : 'gray.50'}
                                  checked={checked}
                                ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                    <HStack gap={2}>
                                      <Icon as={FaFolder} color="orange.400" />
                                      <Text as="span" fontWeight="600" fontSize="sm">{carpeta.nombre}</Text>
                                      <Text as="span" fontSize="xs" color="gray.500">
                                        ({obrasC.length} obra{obrasC.length === 1 ? '' : 's'})
                                      </Text>
                                    </HStack>
                                  </Checkbox.Label></Checkbox.Root>
                              );
                            })}
                          </VStack>
                        )}
                      </VStack>
                    </TabPanel>
                    <TabPanel value="2" px={0} pt={4}>
                      <VStack align="stretch" gap={3}>
                        <Text fontSize="sm" color="gray.600">
                          Marca las obras que quieras importar de forma individual.
                        </Text>
                        <Input
                          size="sm"
                          placeholder="Filtrar por título, autor o género…"
                          value={importArchivoFiltro}
                          onChange={(e) => setImportArchivoFiltro(e.target.value)}
                          bg={inputBg}
                        />
                        {obrasImportArchivoFiltradas.length > 0 ? (
                          <HStack justify="space-between" p={2} bg={reviewCardBg} rounded="md">
                            <Checkbox.Root
                              colorPalette="purple"
                              onCheckedChange={handleSelectAllImportObras}
                              checked={obrasImportArchivoFiltradas.some((o) => importObrasSeleccion.includes(o.id))
                              && !obrasImportArchivoFiltradas
                                .filter((o) => !profileData.repertorio.some(
                                  (r) => r.nombre.toLowerCase() === (o.titulo || '').trim().toLowerCase(),
                                ))
                                .every((o) => importObrasSeleccion.includes(o.id)) ? 'indeterminate' : obrasImportArchivoFiltradas.length > 0
                              && obrasImportArchivoFiltradas
                                .filter((o) => !profileData.repertorio.some(
                                  (r) => r.nombre.toLowerCase() === (o.titulo || '').trim().toLowerCase(),
                                ))
                                .every((o) => importObrasSeleccion.includes(o.id))}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                              <Text fontSize="sm" fontWeight="600">Seleccionar visibles</Text>
                            </Checkbox.Label></Checkbox.Root>
                            <Badge colorPalette="purple" rounded="full">
                              {importObrasSeleccion.length} seleccionada(s)
                            </Badge>
                          </HStack>
                        ) : null}
                        <IsolateCheckboxesFromField>
                        <VStack align="stretch" gap={1} maxH="40vh" overflowY="auto">
                          {obrasImportArchivoFiltradas.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">No hay obras que coincidan.</Text>
                          ) : (
                            obrasImportArchivoFiltradas.map((obra) => {
                              const ya = profileData.repertorio.some(
                                (r) => r.nombre.toLowerCase() === (obra.titulo || '').trim().toLowerCase(),
                              );
                              const checked = importObrasSeleccion.includes(obra.id);
                              return (
                                <HStack
                                  key={obra.id}
                                  p={2}
                                  rounded="md"
                                  borderWidth="1px"
                                  borderColor={checked ? 'purple.200' : 'gray.100'}
                                  bg={checked ? 'purple.50' : 'white'}
                                  opacity={ya ? 0.65 : 1}
                                  cursor={ya ? 'not-allowed' : 'pointer'}
                                  onClick={() => !ya && toggleImportObraSeleccion(obra.id)}
                                >
                                  <Checkbox.Root
                                    colorPalette="purple"
                                    disabled={ya}
                                    onCheckedChange={() => toggleImportObraSeleccion(obra.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    checked={checked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                                  <VStack align="start" gap={0} flex={1} minW={0}>
                                    <HStack gap={2}>
                                      <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                        {obra.titulo || 'Sin título'}
                                      </Text>
                                      {ya ? <Badge colorPalette="yellow" size="sm">Ya está</Badge> : null}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                      {obra.autor || '—'} · {obra.genero?.nombre || 'Otro'}
                                    </Text>
                                  </VStack>
                                </HStack>
                              );
                            })
                          )}
                        </VStack>
                        </IsolateCheckboxesFromField>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onImportArchivoModalClose}>
                Cancelar
              </Button>
              {importArchivoTab === 1 ? (
                <Button
                  colorPalette="purple"
                  onClick={handleImportArchivoCarpetas}
                  disabled={!importCarpetasSeleccion.length}><Icon as={FaUpload} />Importar carpetas ({importCarpetasSeleccion.length})
                              </Button>
              ) : importArchivoTab === 2 ? (
                <Button
                  colorPalette="purple"
                  onClick={handleImportArchivoObras}
                  disabled={!importObrasSeleccion.length}><Icon as={FaUpload} />Importar obras ({importObrasSeleccion.length})
                              </Button>
              ) : (
                <Button
                  colorPalette="purple"
                  onClick={handleImportArchivoTodo}
                  disabled={!archivoObrasList.length}><Icon as={FaUpload} />Importar todo
                              </Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal para revisar marchas extraídas del PDF */}
    <Dialog.Root open={isPdfScanModalOpen} placement='center' size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onPdfScanModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content maxH="80vh">
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaFileAlt} color="green.500" w={6} h={6} />
                <VStack align="start" gap={0}>
                  <Text>Marchas encontradas en el PDF</Text>
                  <Text fontSize="sm" fontWeight="normal" color="gray.500">
                    Selecciona las que quieres añadir a tu repertorio
                  </Text>
                </VStack>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={4} align="stretch">
                {/* Seleccionar todos */}
                <HStack justify="space-between" p={3} bg={reviewCardBg} rounded="md">
                  <HStack>
                    <Checkbox.Root
                      onCheckedChange={handleSelectAllExtracted}
                      colorPalette="green"
                      checked={selectedExtractedMarchas.length > 0 && selectedExtractedMarchas.length < extractedMarchas.length ? 'indeterminate' : selectedExtractedMarchas.length === extractedMarchas.length}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                    <Text fontWeight="600">Seleccionar todas ({extractedMarchas.length})</Text>
                  </HStack>
                  <Badge colorPalette="green" fontSize="md" px={3} py={1} rounded="full">
                    {selectedExtractedMarchas.length} seleccionadas
                  </Badge>
                </HStack>

                {/* Lista de marchas */}
                <VStack gap={2} align="stretch" maxH="400px" overflowY="auto">
                  {extractedMarchas.map((marcha, index) => {
                    const isSelected = selectedExtractedMarchas.includes(index);
                    const alreadyExists = profileData.repertorio.some(
                      item => item.nombre.toLowerCase() === marcha.nombre.toLowerCase()
                    );
                    
                    return (
                      <HStack
                        key={index}
                        p={3}
                        bg={isSelected ? selectedBg : cardBg}
                        border="1px solid"
                        borderColor={isSelected ? 'green.300' : borderColorAlt}
                        rounded="md"
                        cursor={alreadyExists ? 'not-allowed' : 'pointer'}
                        opacity={alreadyExists ? 0.6 : 1}
                        onClick={() => !alreadyExists && handleToggleExtractedMarcha(index)}
                        _hover={!alreadyExists ? { borderColor: 'green.400' } : {}}
                      >
                        <Checkbox.Root
                          disabled={alreadyExists}
                          colorPalette="green"
                          onCheckedChange={() => handleToggleExtractedMarcha(index)}
                          onClick={(e) => e.stopPropagation()}
                          checked={isSelected}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                        <VStack align="start" gap={0} flex={1}>
                          <HStack>
                            <Text fontWeight="600" fontSize="sm">{marcha.nombre}</Text>
                            {alreadyExists && (
                              <Badge colorPalette="yellow" size="sm">Ya existe</Badge>
                            )}
                          </HStack>
                          <HStack gap={2}>
                            <Text fontSize="xs" color="gray.500">{marcha.autor}</Text>
                            <Text fontSize="xs" color="gray.400">•</Text>
                            <Badge colorPalette="purple" size="sm">{marcha.tipo}</Badge>
                          </HStack>
                        </VStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={onPdfScanModalClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="green"
                  onClick={handleAddExtractedMarchas}
                  disabled={selectedExtractedMarchas.length === 0}><Icon as={FaPlus} />Añadir {selectedExtractedMarchas.length}marchas
                              </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal de edición de ensayo */}
    <Dialog.Root open={isEnsayoModalOpen} placement='center' size='lg' onOpenChange={e => {
      if (!e.open) {
        onEnsayoModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaClock} color="purple.500" w={6} h={6} />
                <Text>{ensayoEditando?.id?.toString().startsWith('temp-') ? 'Añadir' : 'Editar'} horario de ensayo</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              {ensayoEditando && (
                <VStack gap={4}>
                  <Field.Root >
                    <Field.Label>Día de la semana</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={ensayoEditando.diaSemana}
                        onChange={(e) => setEnsayoEditando({ ...ensayoEditando, diaSemana: e.target.value })}
                        placeholder="Selecciona un día...">
                        <option value="Lunes">Lunes</option>
                        <option value="Martes">Martes</option>
                        <option value="Miércoles">Miércoles</option>
                        <option value="Jueves">Jueves</option>
                        <option value="Viernes">Viernes</option>
                        <option value="Sábado">Sábado</option>
                        <option value="Domingo">Domingo</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                    <Field.Root >
                      <Field.Label>Hora de inicio</Field.Label>
                      <Input
                        type="time"
                        value={ensayoEditando.horaInicio}
                        onChange={(e) => setEnsayoEditando({ ...ensayoEditando, horaInicio: e.target.value })}
                      />
                    </Field.Root>

                    <Field.Root >
                      <Field.Label>Hora de fin</Field.Label>
                      <Input
                        type="time"
                        value={ensayoEditando.horaFin}
                        onChange={(e) => setEnsayoEditando({ ...ensayoEditando, horaFin: e.target.value })}
                      />
                    </Field.Root>
                  </SimpleGrid>

                  <Field.Root>
                    <Field.Label>Ubicación</Field.Label>
                    <Input
                      value={ensayoEditando.ubicacion}
                      onChange={(e) => setEnsayoEditando({ ...ensayoEditando, ubicacion: e.target.value })}
                      placeholder="Ej: Local de ensayo, Plaza Mayor..."
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Notas adicionales</Field.Label>
                    <Textarea
                      value={ensayoEditando.notas}
                      onChange={(e) => setEnsayoEditando({ ...ensayoEditando, notas: e.target.value })}
                      placeholder="Ej: Abierto a oyentes, confirmar asistencia por email..."
                      rows={3}
                    />
                  </Field.Root>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={onEnsayoModalClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleSaveEnsayo}
                  disabled={!ensayoEditando?.diaSemana || !ensayoEditando?.horaInicio || !ensayoEditando?.horaFin}><Icon as={FaCheck} />{ensayoEditando?.id?.toString().startsWith('temp-') ? 'Añadir' : 'Guardar'}</Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    {/* Modal de confirmación de solicitud de reseña */}
    <Dialog.Root open={isConfirmReviewRequestOpen} placement='center' onOpenChange={e => {
      if (!e.open) {
        onConfirmReviewRequestClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3}>
                <Icon as={FaPaperPlane} color="purple.500" w={6} h={6} />
                <Text>Confirmar solicitud de reseña</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
              <VStack gap={4} align="start">
                <Text lineHeight="tall" color={textColor}>
                  ¿Estás seguro de que quieres enviar una solicitud de reseña a{' '}
                  <Text as="span" fontWeight="bold">{reviewRequestEmail}</Text>?
                </Text>
                <Alert.Root status="info" rounded="md">
                  <Alert.Indicator />
                  <Text fontSize="sm">
                    La cofradía recibirá un correo electrónico con un enlace para dejar su reseña sobre tu banda.
                  </Text>
                </Alert.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="ghost" onClick={onConfirmReviewRequestClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={handleConfirmRequestReview}><Icon as={FaPaperPlane} />Enviar solicitud
                              </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
        </VStack>
        </Container>
      </Box>

      {/* Modal de Previsualización del perfil público */}
      <PublicProfilePreview
        isOpen={showPublicPreview}
        onClose={() => setShowPublicPreview(false)}
        profileData={{ ...profileData, avalesRecibidos, eventosPublicos: eventosPublicosPreview }}
        shareUrl={publicProfileShareUrl}
      />

      {/* Barra fija de cambios sin guardar con botón integrado */}
      {hasUnsavedChanges && (
        <Box
          position="fixed"
          top={{ base: '70px', md: '80px' }}
          left="50%"
          transform="translateX(-50%)"
          zIndex={1000}
          bg="orange.500"
          color="white"
          px={{ base: 3, md: 5 }}
          py={2}
          rounded="full"
          shadow="lg"
          display="flex"
          alignItems="center"
          gap={{ base: 2, md: 3 }}
          maxW="95vw"
        >
          <Icon as={FaExclamationTriangle} flexShrink={0} />
          <Text fontSize="sm" fontWeight="bold" display={{ base: 'none', sm: 'block' }}>
            Cambios sin guardar
          </Text>
          <Tooltip
            content={
              verificationStatus === 'pending'
                ? 'Perfil bloqueado mientras la validación está en proceso'
                : missingMandatoryForValidateLabels.length > 0
                  ? `Completa los obligatorios para validar: ${missingMandatoryForValidateLabels.join(', ')}`
                  : 'Guardar cambios del perfil'
            }
            showArrow><span>
              <span>
                <Button
                  size="sm"
                  bg="white"
                  color="orange.600"
                  fontWeight="700"
                  rounded="full"
                  onClick={handleSave}
                  loading={isLoading}
                  disabled={
                    verificationStatus === 'pending' || missingMandatoryForValidateLabels.length > 0
                  }
                  loadingText="Guardando..."
                  _hover={{ bg: 'orange.50', transform: 'scale(1.05)' }}
                  _active={{ transform: 'scale(0.95)' }}
                  transition="all 0.2s ease"><Icon as={FaSave} boxSize={3} />{verificationStatus === 'pending' ? 'Bloqueado' : 'Guardar'}</Button>
              </span>
            </span></Tooltip>
        </Box>
      )}

      {/* Modal de Desactivar cuenta */}
      <Dialog.Root open={isDeactivateModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeactivateModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaShieldAlt} color="red.500" w={6} h={6} />
                  <Text color="red.600">Desactivar cuenta</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="error" rounded="md">
                    <Alert.Indicator />
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        ¿Estás seguro de que quieres desactivar tu cuenta?
                      </Text>
                      <Text fontSize="xs">
                        • No podrás acceder a la plataforma{'\n'}
                        • Tu perfil dejará de ser visible{'\n'}
                        • Para reactivarla, contacta con soporte
                      </Text>
                    </VStack>
                  </Alert.Root>
                  <Field.Root >
                    <Field.Label>Confirma tu contraseña</Field.Label>
                    <Input
                      type="password"
                      placeholder="Introduce tu contraseña"
                      value={deactivatePassword}
                      onChange={(e) => setDeactivatePassword(e.target.value)}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={onDeactivateModalClose}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeactivateAccount}
                    loading={isDeactivating}
                  >
                    Desactivar cuenta
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Eliminar cuenta permanentemente */}
      <Dialog.Root open={isDeleteModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeleteModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaTrash} color="red.500" w={6} h={6} />
                  <Text color="red.600">Eliminar cuenta permanentemente</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="error" rounded="md">
                    <Alert.Indicator />
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" fontWeight="bold">
                        ⚠️ Esta acción es IRREVERSIBLE
                      </Text>
                      <Text fontSize="xs">
                        • Todos tus datos personales serán eliminados{'\n'}
                        • Tu perfil será anonimizado{'\n'}
                        • No podrás recuperar tu cuenta{'\n'}
                        • Los acuerdos existentes se mantendrán anonimizados
                      </Text>
                    </VStack>
                  </Alert.Root>
                  
                  <Field.Root >
                    <Field.Label>Confirma tu contraseña</Field.Label>
                    <Input
                      type="password"
                      placeholder="Introduce tu contraseña"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                    />
                  </Field.Root>

                  <Field.Root >
                    <Field.Label>Escribe ELIMINAR para confirmar</Field.Label>
                    <Input
                      placeholder="ELIMINAR"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={onDeleteModalClose}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeleteAccount}
                    loading={isDeleting}
                    disabled={deleteConfirmText !== 'ELIMINAR'}
                  >
                    Eliminar permanentemente
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Editor de imágenes (Cropper) */}
      <ImageCropper
        isOpen={isCropperOpen}
        onClose={() => {
          setIsCropperOpen(false);
          setCropperImage(null);
          setCropperType(null);
          setPendingFile(null);
        }}
        imageSrc={cropperImage}
        onCropComplete={handleCropComplete}
        aspectRatio={cropperType === 'avatar' ? 1 : 16 / 9}
        cropShape={cropperType === 'avatar' ? 'round' : 'rect'}
        title={
          cropperType === 'avatar'
            ? 'Editar foto de perfil'
            : cropperType === 'imagenCabeceraWeb'
              ? 'Recortar imagen de cabecera de tu web'
              : 'Editar imagen de fondo'
        }
      />

      {/* Modal de Cambios Sin Guardar */}
      <Dialog.Root open={isUnsavedChangesModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          cancelNavigation();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaExclamationTriangle} color="orange.500" w={6} h={6} />
                  <Text color="orange.600">Cambios sin guardar</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning" rounded="md">
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      Tienes cambios sin guardar en tu perfil. Si sales ahora, perderás todos los cambios realizados.
                    </Text>
                  </Alert.Root>
                  <Text color="gray.600">
                    ¿Qué deseas hacer?
                  </Text>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={confirmNavigation} colorPalette="red">
                    Salir sin guardar
                  </Button>
                  <Button colorPalette="purple" onClick={cancelNavigation}>
                    Continuar editando
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal para ver imagen en tamaño completo */}
      <Dialog.Root open={isImageModalOpen} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          handleCloseImageModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger
                color="white"
                size="lg"
                bg="blackAlpha.600"
                _hover={{ bg: 'blackAlpha.700' }}
                rounded="full"
                m={4}
              />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4}>
                {selectedImage && (
                  <Image
                    src={selectedImage}
                    alt="Imagen en tamaño completo"
                    maxH="90vh"
                    maxW="90vw"
                    objectFit="contain"
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de confirmación para eliminar todas las fotos */}
      <Dialog.Root open={isDeleteAllPhotosModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeleteAllPhotosModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Eliminar todas las fotos</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text>¿Estás seguro de que quieres eliminar todas las fotos? Esta acción no se puede deshacer.</Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onDeleteAllPhotosModalClose} disabled={isDeletingPhotos}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDeleteAllPhotos}
                  loading={isDeletingPhotos}
                  loadingText="Eliminando..."
                >
                  Eliminar todas
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
      {/* Modal de cambio de username */}
      <Dialog.Root open={isUsernameModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onUsernameModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Cambiar nombre de usuario</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Introduce tu nuevo nombre de usuario. Puedes cambiarlo un máximo de 2 veces al año.
                  </Text>
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="nuevo_username"
                  />
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onUsernameModalClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleChangeUsername}
                  loading={isChangingUsername}
                  disabled={!newUsername.trim()}
                >
                  Guardar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
};

export default Profile;
